import { Viewport } from "./canvas/Viewport";
import { PointerInput } from "./canvas/PointerInput";
import { Scene } from "./canvas/Scene";
import { History } from "./model/History";
import { ServerImageGc } from "./model/serverImageGc";
import { createEmptyProject } from "./model/Project";
import { placedStitchHoles } from "./model/placedStitchHoles";
import { deriveProject } from "./model/deriveProject";
import { hasPieceLayoutOffset } from "./model/pieceTransform";
import { StampLibrary } from "./stamps/StampLibrary";
import type { StampMeta } from "./stamps/StampMeta";
import { defaultSettings, type EditorContext } from "./tools/EditorContext";
import { cssVar } from "./colors";
import type { Tool } from "./tools/ToolBase";
import { SelectTool } from "./tools/SelectTool";
import {
  arrangePieceSelection,
  autoLayoutPieces,
  movablePieceIds,
  transformPieceSelection,
  transformablePieceIds,
} from "./model/pieceArrange";
import { ShapeTool } from "./tools/ShapeTool";
import { offsetSourceOutlines } from "./tools/outlineOffsetController";
import { TraceTool } from "./tools/TraceTool";
import { RegionTool } from "./tools/RegionTool";
import type { ShapeMode } from "./tools/constructionShapeMode";
import { HoleTool } from "./tools/HoleTool";
import { CutoutTool } from "./tools/CutoutTool";
import { StitchTool } from "./tools/StitchTool";
import { StampTool } from "./tools/StampTool";
import { LineStampTool } from "./tools/LineStampTool";
import { FillTool } from "./tools/FillTool";
import { FreePathTool } from "./tools/FreePathTool";
import { HardwareTool } from "./tools/HardwareTool";
import {
  OutlineEditTool,
  type OutlineEditAction,
} from "./tools/OutlineEditTool";
import { MeasureTool } from "./tools/MeasureTool";
import { NoteTool } from "./tools/NoteTool";
import { LeatherMirrorTool } from "./tools/LeatherMirrorTool";
import { LeatherRoundTool } from "./tools/LeatherRoundTool";
import { LeatherDeleteTool } from "./tools/LeatherDeleteTool";
import { LeatherSliceTool } from "./tools/LeatherSliceTool";
import {
  ConstructionTool,
  type ConstructionEditAction,
  type ConstructionSubMode,
} from "./tools/ConstructionTool";
import {
  constructionSubtoolDisabled,
  partRequiringSubtoolDisabled,
  topbarToolDisabled,
} from "./toolGating";
import autoLayoutSvg from "./icons/auto-layout.svg?raw";
import { stripIconSvg } from "./icons/stripIconSvg";

import { stitchHolePlacements } from "./model/stitching";
import { seamPathData } from "./model/seamGeometry";
import { CHECK_SVG, X_SVG } from "./ui/flyoutIcons";
import { Toolbar } from "./ui/Toolbar";
import { PropertiesPanel, type ToolId } from "./ui/PropertiesPanel";
import { StampPanel } from "./ui/StampPanel";
import {
  SHAPE_BOOLEAN_OPS,
  type ShapeBooleanOp,
} from "./model/shapeBooleanCore";
import {
  canShapeBooleanSelection,
  shapeBooleanSelection,
} from "./model/constructionBoolean";
import {
  canLeatherBooleanSelection,
  leatherBooleanSelection,
} from "./model/leatherBoolean";
import {
  BOOLEAN_UNITE_ICON,
  BOOLEAN_SUBTRACT_ICON,
  BOOLEAN_INTERSECT_ICON,
  BOOLEAN_EXCLUDE_ICON,
  BOOLEAN_DIVIDE_ICON,
} from "./icons/booleanIcons";

/** Glyph per op, so the toolbar builder stays a loop over the shared table. */
const BOOLEAN_ICONS: Record<ShapeBooleanOp, string> = {
  unite: BOOLEAN_UNITE_ICON,
  subtract: BOOLEAN_SUBTRACT_ICON,
  intersect: BOOLEAN_INTERSECT_ICON,
  exclude: BOOLEAN_EXCLUDE_ICON,
  divide: BOOLEAN_DIVIDE_ICON,
};
import { LayersPanel } from "./ui/LayersPanel";
import { ExportDialog } from "./ui/ExportDialog";
import { AdvancedPrintDialog } from "./ui/AdvancedPrintDialog";
import { printPdf } from "./exporters/pdfExporter";
import { CalibrationDialog } from "./ui/CalibrationDialog";
import { setupConstructionSubbar } from "./ui/setup/constructionSubbar";
import { SettingsDialog } from "./ui/SettingsDialog";
import { isFeatureEnabled, onFeatureFlagsChange } from "./featureFlags";
import { getPxPerMm, onCalibrationChange } from "./calibration";
import {
  markPaidFeature,
  withPreviewNote,
  withoutPreviewNote,
} from "./ui/paidPreview";
import { onAngleHintChange } from "./snapAngleIndicator";
import { showCanvasToast } from "./canvasToast";
import { isTextTarget } from "./isTextTarget";
import { ModifierTapDetector } from "./modifierDoubleTap";
import { ThemeManager } from "./ui/ThemeManager";
import { Autosave } from "./storage/autosave";
import { SUBTOOL_ORDER } from "./ui/constructionSubtools";
import {
  LOADING_TEXT_MS,
  PROGRESS_BAR_MS,
  type LoadPhases,
  type RestorePhase,
  clearLoadHint,
  isDelayedLoad,
  showsProgressBar,
  stuckAfterMs,
  predictPhases,
  readLoadHint,
  restoreMs,
  writeLoadHint,
} from "./storage/loadHint";
import { LoadProgressBar } from "./ui/loadProgressBar";
import { LOAD_BAR_PX, MAT_BORDER_PX } from "./canvas/matRenderer";
import { deserializeProject } from "./storage/projectFile";
import {
  getUnits,
  onShowSecondaryUnitsChange,
  onUnitsChange,
  setUnits,
  constructionEditActionPref,
  constructionFreehandSmoothnessPref,
  constructionKindPref,
  constructionSubModePref,
  cutoutKindPref,
  lastToolPref,
  leatherFreehandSmoothnessPref,
  leatherShapeKindPref,
  matVisiblePref,
  partLayoutPref,
  leftHandedPref,
  propertiesDetachedPref,
  toolPropertiesSplitPref,
  stitchKindPref,
  showSnapCandidatesPref,
  toolSettingsPref,
} from "./preferences";
import { isPointSnapActive, isLineSnapActive } from "./tools/dragSnap";
import {
  isSnapApplicable,
  isRoundModeApplicable,
  roundModeInapplicableReason,
  snapInapplicableReason,
  type SnapToolContext,
} from "./tools/snapApplicability";
import {
  downloadProjectFile,
  pickProjectFile,
  readProjectFile,
  isProjectFileName,
  ensureUserStampRegistered,
} from "./storage/projectFile";
import { deleteSelection } from "./model/deleteSelection";
import { nudgeSelection, hasNudgeableSelection } from "./model/nudgeSelection";
import {
  isVisible,
  pieceForStitchLine,
  pieceStitchLines,
  type StitchLine,
  projectConstructionLines,
  projectTracingImages,
  type Project,
} from "./model/Project";
import { buildConstructionArrangement } from "./model/constructionArrangement";
import { findFaces } from "./model/planarFaces";
import {
  arrangeConstructionSelection,
  arrangeableConstructionObjects,
  transformConstructionSelection,
  duplicateConstructionSelection,
  groupConstructionSelection,
  ungroupConstructionSelection,
  type ConstructionTransformOp,
} from "./model/constructionGroupActions";
import {
  duplicateChildFeatureSelection,
  duplicatePieceSelection,
} from "./model/pieceDuplicate";
import { stepConstructionItems, stepPiecesInList } from "./model/projectOps";
import {
  copySelection,
  pasteClipboard,
  type ClipboardPayload,
} from "./model/clipboard";
import type { ArrangeOp } from "./model/arrange";
import {
  FLIP_HORIZONTAL_ICON,
  FLIP_VERTICAL_ICON,
  ROTATE_LEFT_ICON,
  ROTATE_RIGHT_ICON,
} from "./icons/transformIcons";
import {
  ARRANGE_ALIGN_LEFT_ICON,
  ARRANGE_ALIGN_RIGHT_ICON,
  ARRANGE_ALIGN_TOP_ICON,
  ARRANGE_ALIGN_BOTTOM_ICON,
  ARRANGE_CENTER_HORIZONTAL_ICON,
  ARRANGE_CENTER_VERTICAL_ICON,
  ARRANGE_DISTRIBUTE_HORIZONTAL_ICON,
  ARRANGE_DISTRIBUTE_VERTICAL_ICON,
} from "./icons/arrangeIcons";

// Tool the app falls back to when no valid tool is persisted (first-ever
// load or a stale/invalid stored id) and the tool the project Reset button
// returns to (#806). Construction is the natural starting point — guide
// lines are the foundation everything else is built against, and unlike
// most other tools it works on an empty project so the user can begin
// drawing immediately.
const DEFAULT_TOOL_ID: ToolId = "construction";

export async function startApp() {
  const canvas = document.getElementById("canvas") as HTMLCanvasElement;
  if (!canvas) throw new Error("#canvas missing");

  const themeModeBtn = document.getElementById(
    "theme-mode-toggle",
  ) as HTMLButtonElement | null;
  // Forward-declared so the ThemeManager — built before the Scene exists —
  // can ask for a repaint once there is one. Light/dark now moves a canvas
  // color (the note card, #157), and paper items hold their colors resolved.
  let repaintCanvas: () => void = () => {};
  const themeManager = themeModeBtn
    ? new ThemeManager(themeModeBtn, () => repaintCanvas())
    : null;

  const viewport = new Viewport(canvas);
  const library = new StampLibrary();
  library.all();
  const scene = new Scene(viewport.scope, library);

  const project = createEmptyProject();
  const history = new History(project);
  // Loading the saved project (#1230). The editor is up before IndexedDB
  // answers, so until the restore lands the tools take no input (anything
  // drawn would be overwritten) and the mat is laid out where the saved one
  // will be. The load-time hint predicts how long that takes: from
  // LOADING_TEXT_MS the delayed state ("Loading…", "Loading parts…", dimmed
  // tools) shows from the first paint, set already by the inline script in
  // index.html; from PROGRESS_BAR_MS, separately, the progress bar. `data-project-load` on <html> is
  // "fast" | "delayed" while loading and "done" after, which the e2e suite
  // waits for.
  const loadHint = readLoadHint();
  const predictedPhases = predictPhases(loadHint);
  let projectLoad: "fast" | "delayed" | "done" = isDelayedLoad(
    restoreMs(predictedPhases),
  )
    ? "delayed"
    : "fast";
  document.documentElement.dataset.projectLoad = projectLoad;
  if (loadHint?.mat) scene.setLoadingMatLayout(loadHint.mat);
  // Both halves now exist — let the appearance toggle repaint (see the
  // forward declaration above).
  repaintCanvas = () => scene.render(history.state);

  // Screen-constant affordances — the construction resize handles (#388 /
  // #863), the rotation knob, and the corner-radius handles — are sized
  // `k / zoom` at render time so they hold a fixed on-screen size. A bare zoom
  // change (paper applies it to the view WITHOUT re-rendering) would otherwise
  // leave them scaled with the view until the next commit. Re-render on every
  // zoom change so they rebuild at the right size; rAF-throttled so a rapid
  // wheel / pinch gesture triggers at most one render per frame.
  let zoomRenderQueued = false;
  viewport.onZoomChange(() => {
    if (zoomRenderQueued) return;
    zoomRenderQueued = true;
    const flush = () => {
      zoomRenderQueued = false;
      scene.render(history.state);
    };
    if (typeof requestAnimationFrame === "function")
      requestAnimationFrame(flush);
    else flush();
  });

  // "Last saved" fingerprint — the `modified` timestamp of whichever
  // project state is currently on disk (or freshly opened from disk
  // / autosave / reset). `History.commit` stamps a new `modified` on
  // every change, so `state.modified !== lastSavedModified` is a
  // cheap test for "dirty since last save". Updated whenever a fresh
  // project enters the editor (open / reset / autosave restore /
  // initial empty) and whenever the user explicitly Saves.
  let lastSavedModified = history.state.modified;
  const hasUnsavedChanges = () => history.state.modified !== lastSavedModified;

  const settings = defaultSettings();
  settings.activeStampId = library.all()[0]?.id ?? null;
  // Restore tool settings persisted from a previous session, then validate
  // any id-based fields against the current library so a removed stamp falls
  // back to a default. The preference itself already survives unreadable
  // storage and unparseable JSON (#998); the guard is for the migration +
  // library lookups below, where a hand-edited blob could hold anything.
  try {
    const saved = toolSettingsPref.get() as
      | (Partial<typeof settings> & { snapToAxis?: boolean })
      | null;
    if (saved) {
      // Migrate from the old single `snapToAxis` toggle (issue #98).
      // `snapToAxis: true` enabled both the 45° angle snap AND the
      // square / circle aspect-ratio constraint, so we mirror it to
      // both children when the new flags weren't explicitly saved.
      const legacySnap = saved.snapToAxis;
      if (legacySnap !== undefined) {
        if (saved.snapAngle === undefined) saved.snapAngle = legacySnap;
        if (saved.snapSizes === undefined) saved.snapSizes = legacySnap;
        delete saved.snapToAxis;
      }
      Object.assign(settings, saved);
      if (settings.activeStampId && !library.get(settings.activeStampId)) {
        settings.activeStampId = library.all()[0]?.id ?? null;
      }
    }
  } catch {
    /* corrupt prefs — ignore */
  }

  const settingsListeners = new Set<() => void>();
  const ctx: EditorContext = {
    history,
    viewport,
    scene,
    library,
    settings,
    setSelection: (sel, opts) => {
      scene.setSelection(sel, opts);
      // Edit points targets whatever is selected (#1033), so picking a seam
      // has to bring the canvas into stitch display and picking a part has to
      // bring it back — otherwise the tool edits an object the view isn't
      // drawing.
      syncOutlineEditDisplayMode();
      ui.props.refresh();
      ui.layers.refresh();
      // The leather Round method follows the selection (#974): its corner
      // handles belong to whatever is selected, and a radius already set in
      // the panel applies to the newly-picked object straight away — so
      // picking part after part rounds each one, from the canvas or the Parts
      // list, without leaving the tool.
      if (activeTool?.id === "leatherRound") {
        (tools.leatherRound as LeatherRoundTool).onSelectionChanged();
      }
      // Selection drives the Arrange cluster's enabled state (#115) —
      // re-sync so the buttons light up the moment a second object is
      // added (and dim again when the selection drops below two).
      syncConstructionSubbar();
    },
    rerender: () => {
      scene.render(history.state);
    },
    commit: (label, mutate) => {
      // Every commit re-derives the project's derived data — the same chain
      // the load paths run (#1141).
      history.commit(
        label,
        deriveProject(mutate(history.state), scene.getPaperScope()),
      );
    },
    onSettingsChanged: (fn) => {
      settingsListeners.add(fn);
      return () => settingsListeners.delete(fn);
    },
    notifySettingsChanged: () => {
      for (const fn of settingsListeners) fn();
    },
    activateTool: (id) => activateTool(id as ToolId),
  };

  // Test hook — exposes `commit` and `getState` so Playwright
  // tests can seed project state (e.g., inject constraints to
  // exercise the hover behaviour) without going through any
  // not-yet-built UI. Intentionally untyped on the window side so
  // the production code doesn't depend on it.
  (window as unknown as Record<string, unknown>).__editor = {
    commit: ctx.commit,
    getState: () => ctx.history.state,
    // Computed hole centres for a stitch line (by id) — used by e2e to assert
    // hole placement / construction-grid alignment (#199 Phase 3).
    getStitchHoles: (stitchId: string) => {
      const piece = pieceForStitchLine(ctx.history.state, stitchId);
      const line = piece
        ? pieceStitchLines(piece).find((l) => l.id === stitchId)
        : null;
      // Route through the shared on-piece retention (#910) so e2e asserts the
      // REAL kept set — the work-area window AND the leather outline — rather
      // than the raw along-seam row, which included holes that overflow the
      // piece edge.
      return line && piece
        ? placedStitchHoles(line, piece, scene.getPaperScope()).map((h) => ({
            x: h.position.x,
            y: h.position.y,
          }))
        : null;
    },
    // Selection accessors — used by e2e specs that need to script a
    // multi-select before exercising group / ungroup shortcuts
    // without re-implementing the click-modifier ladder. Routes
    // through `ctx.setSelection` (not the bare `scene.setSelection`)
    // so the properties / layers panels also pick up the change —
    // tests that assert button-enabled state right after a
    // scripted selection depend on the layers panel being in sync.
    getSelection: () => scene.getSelection(),
    setSelection: (sel: Parameters<typeof scene.setSelection>[0]) =>
      ctx.setSelection(sel),
    // The active paper.PaperScope — exposed so e2e tests can parse
    // pathData (e.g., after a vertex drag the serializer may emit
    // C tokens for previously L-only segments, and round-tripping
    // through paper is the easiest way to read anchor positions
    // out reliably).
    getPaperScope: () => scene.getPaperScope(),
    // #982 — where a seam's chisel marks actually land, and the seam's real
    // (curve-carrying) outline. Lets e2e assert that holes follow a CURVED seam
    // without re-deriving the placement rules in the test.
    getStitchHolePlacements: (pieceId: string, stitchId: string) => {
      const piece = history.state.pieces.find((p) => p.id === pieceId);
      const line = piece
        ? pieceStitchLines(piece).find((l) => l.id === stitchId)
        : undefined;
      return line ? stitchHolePlacements(line) : [];
    },
    getSeamPathData: (line: StitchLine) => seamPathData(line),
    // Live rendered item for a construction line — needed by e2e
    // to read anchor positions *during* a drag, before the commit
    // updates the model state.
    getConstructionLineItem: (id: string) => scene.getConstructionLineItem(id),
    // Full scene reference — e2e tests use this to call live-update
    // hooks like `rederiveMirrorsAffectedBy` directly without
    // synthesizing the full drag pipeline (issue #180 live preview
    // coverage).
    getScene: () => scene,
    // Live rendered dark-halo item beneath one construction line's
    // yellow stroke. Used by the issue #85 regression spec to read
    // halo bounds mid-drag and confirm the halo translates in
    // lock-step with the yellow path.
    getConstructionLineHaloItem: (id: string) =>
      scene.getConstructionLineHaloItem(id),
    // Live rendered overlay (bbox rectangle + centre dot wrapper) for
    // one selected construction group. Used by the #80 group-bbox
    // spec to assert the overlay appears, sits around all members,
    // and translates with the group during a drag.
    getConstructionGroupOverlayItem: (id: string) =>
      scene.getConstructionGroupOverlayItem(id),
    // Construction sub-mode (issue #29 e2e). Mirrors the property on
    // the live tool so a spec can confirm it activated the right
    // sub-tool before exercising a drag.
    getConstructionSubMode: () =>
      (tools.construction as ConstructionTool).subMode,
    // Current Mirror sub-tool hover axis. Used by `e2e/construction-
    // mirror-skip-curve-axis.spec.ts` (issue #340) to assert that
    // curved bezier segments don't surface as candidate axes.
    getMirrorHoverAxis: () =>
      (tools.construction as ConstructionTool).getMirrorHoverAxis(),
    // #973 — the leather Mirror sub-tool's hovered axis, so e2e can assert
    // what the cursor resolves to without parsing overlay items.
    getLeatherMirrorAxis: () =>
      (tools.leatherMirror as LeatherMirrorTool).getHoverAxis(),
    getLeatherMirrorPreviewBounds: () =>
      (tools.leatherMirror as LeatherMirrorTool).getPreviewBounds(),
    // #974 — corner-handle positions, so e2e can aim at a handle where it
    // actually sits (the dot parks a rest offset inside the corner).
    getLeatherRoundHandles: () =>
      (tools.leatherRound as LeatherRoundTool).getHandlePositions(),
    // #1000 — what the leather Delete method would remove on a click.
    getLeatherDeleteTarget: () =>
      (tools.leatherDelete as LeatherDeleteTool).getHoverTarget(),
    // Origin + step of the active cutting-mat grid in project mm.
    // Used by `e2e/snap-to-grid.spec.ts` to compute the expected
    // post-drag lattice point.
    getMatGridInfo: () => scene.getMatGridInfo(),
    // Marching-ants hover setters (issue #194). Used by
    // `e2e/raf-loop.spec.ts` to verify that Paper.js's `view.onFrame`
    // RAF loop is installed lazily when an overlay appears and torn
    // down when every overlay clears. Routed straight to Scene
    // (bypassing the panel hover handlers) so the spec is
    // deterministic across browsers — no real cursor events needed.
    setConstructionSegmentHover: (
      spec: Parameters<typeof scene.setConstructionSegmentHover>[0],
    ) => scene.setConstructionSegmentHover(spec),
    setConstructionConstraintHover: (
      spec: Parameters<typeof scene.setConstructionConstraintHover>[0],
    ) => scene.setConstructionConstraintHover(spec),
    // Selection-biased hit-test (issue #262). Specs build a scene
    // where two construction lines overlap a handle, set a
    // selection, and assert that hitTest at the contested point
    // returns the selected line's handle rather than the topmost
    // one. `pt` is in project mm — same coordinate space `Scene`
    // already uses.
    hitTest: (pt: { x: number; y: number }) => {
      const scope = scene.getPaperScope();
      return scene.hitTest(new scope.Point(pt.x, pt.y));
    },
    // Free-floating Construction draw-mode snap target (grid hover
    // preview). Specs use this to verify the yellow snap ring
    // appears at the right project-mm point when the cursor moves
    // over the canvas without scraping Paper item children.
    getConstructionDraftSnapPoint: () => scene.getConstructionDraftSnapPoint(),
  };

  // Feature-flags test hook (issue #358). Development and the e2e build only.
  if (import.meta.env.DEV || __FF_E2E__) {
    (window as unknown as Record<string, unknown>).__features = {
      isEnabled: isFeatureEnabled,
    };
  }

  // Persist settings on every change so tool parameters survive page reloads.
  ctx.onSettingsChanged(() => {
    toolSettingsPref.set(settings);
  });

  const tools: Record<ToolId, Tool> = {
    select: new SelectTool(ctx),
    shape: new ShapeTool(ctx),
    trace: new TraceTool(ctx),
    region: new RegionTool(ctx),
    hole: new HoleTool(ctx),
    cutout: new CutoutTool(ctx),
    stitch: new StitchTool(ctx),
    stamp: new StampTool(ctx),
    lineStamp: new LineStampTool(ctx),
    fill: new FillTool(ctx),
    freePath: new FreePathTool(ctx),
    hardware: new HardwareTool(ctx),
    construction: new ConstructionTool(ctx),
    outlineEdit: new OutlineEditTool(ctx),
    measure: new MeasureTool(ctx),
    notes: new NoteTool(ctx),
    leatherMirror: new LeatherMirrorTool(ctx),
    leatherRound: new LeatherRoundTool(ctx),
    leatherSlice: new LeatherSliceTool(ctx),
    leatherDelete: new LeatherDeleteTool(ctx),
  };
  // `freePath` (the "Line" tool) is registered above so its keyboard shortcut
  // and saved tool prefs still work, but it's intentionally omitted from the
  // toolbar for now — bring it back to this list to surface the button again.
  // Top-level toolbar groups (issue #150). The user-facing toolbar
  // has four buttons — Construction, Leather, Hardware, Stamping —
  // where Leather and Stamping are *parent groups* whose subtools
  // live in their own sub-toolbars. Every individual tool class is
  // still registered above so its keyboard shortcut, panel
  // settings, and pointer logic keep working unchanged; the parent
  // groups are purely a UI grouping.
  //
  // `Toolbar.ts` finds buttons by `data-tool-id`, so the descriptor
  // list below just needs id / label / shortcut. Each parent
  // button activates its `defaultSubtool` on click; activating any
  // child tool (via shortcut, sub-tool click, etc.) highlights
  // the matching parent button via `parentToolbarIdFor`.
  const TOPBAR_TOOLS: Array<{
    id: string;
    label: string;
    shortcut?: string;
    /** Set on parent groups — the child tool id activated by a
     *  click on this parent button. Absent on stand-alone tools
     *  (Construction, Hardware) whose top-level button IS the
     *  tool itself. */
    defaultSubtool?: ToolId;
  }> = [
    { id: "construction", label: "Construction", shortcut: "c" },
    {
      id: "leather",
      label: "Leather",
      shortcut: "l",
      defaultSubtool: "shape",
    },
    { id: "hardware", label: "Hardware", shortcut: "r" },
    {
      id: "stamping",
      label: "Stamping",
      shortcut: "s",
      defaultSubtool: "stamp",
    },
    // The ruler measures whatever is on the canvas, so it belongs to neither
    // Construction nor Leather — it sits on its own at the foot of the bar
    // (#155).
    // Notes annotate whatever is on the canvas rather than belonging to any
    // one tool family, so they sit with Measure below the groups. Above the
    // ruler, not below it: #155 deliberately parked the ruler at the foot of
    // the bar and e2e pins it there.
    { id: "notes", label: "Notes", shortcut: "n" },
    { id: "measure", label: "Measure", shortcut: "m" },
  ];
  /** Child tool id → parent group id (for toolbar highlight + the
   *  sub-toolbar-show-hide pass). Tools that aren't in this map
   *  are top-level (their toolbar id IS their tool id). */
  const TOOL_PARENTS: Record<string, string> = {
    shape: "leather",
    trace: "leather",
    region: "leather",
    select: "leather",
    cutout: "leather",
    hole: "leather",
    stitch: "leather",
    outlineEdit: "leather",
    leatherMirror: "leather",
    leatherRound: "leather",
    leatherSlice: "leather",
    leatherDelete: "leather",
    stamp: "stamping",
    lineStamp: "stamping",
    fill: "stamping",
  };
  /** Translate a tool id into the id of the toolbar button that
   *  should appear active when it's the active tool. Top-level
   *  tools map to themselves; parented tools map to their parent. */
  const parentToolbarIdFor = (toolId: string): string =>
    TOOL_PARENTS[toolId] ?? toolId;

  const pointer = new PointerInput(viewport, canvas);

  // Double-click on the canvas zooms to the part under the cursor — except
  // when the Stitch tool has a polyline draft in progress, in which case the
  // double-click finishes the draft instead.
  canvas.addEventListener("dblclick", (ev) => {
    if (activeTool === tools.stitch) {
      (tools.stitch as StitchTool).handleDoubleClick();
      return;
    }
    // Leather Shape / Cutout polygon: double-click finishes the draft (#779).
    // Only acts when a polygon is in progress, so a double-click elsewhere
    // still falls through to the zoom-to-part behaviour below.
    if (
      activeTool === tools.shape &&
      (tools.shape as ShapeTool).vertexCount > 0
    ) {
      (tools.shape as ShapeTool).handleDoubleClick();
      return;
    }
    if (
      activeTool === tools.cutout &&
      (tools.cutout as CutoutTool).vertexCount > 0
    ) {
      (tools.cutout as CutoutTool).handleDoubleClick();
      return;
    }
    const rect = canvas.getBoundingClientRect();
    const projectPt = viewport.viewToProject({
      x: ev.clientX - rect.left,
      y: ev.clientY - rect.top,
    });
    if (activeTool === tools.construction) {
      // Construction needs the project point so its Edit-mode
      // double-click can hit-test the anchor under the cursor and
      // toggle its smooth-lock. Polyline / polygon finish in Draw
      // mode ignores the point.
      (tools.construction as ConstructionTool).handleDoubleClick(projectPt);
      return;
    }
    const hit = scene.hitTest(projectPt);
    if (!hit.pieceId) return;
    const bounds = scene.getPieceBounds(hit.pieceId);
    if (bounds) viewport.fitToBounds(bounds);
  });

  // Wire UI
  const ui = {
    toolbar: new Toolbar(document.getElementById("toolbar")!, {
      tools: TOPBAR_TOOLS,
      onSelect: (id) => {
        // Parent group click → activate the default child subtool.
        // Stand-alone tools (Construction, Hardware) map straight to
        // themselves. Either way we route through `activateTool`
        // which will set the toolbar's `.active` highlight to the
        // resolved parent id via `parentToolbarIdFor`.
        const entry = TOPBAR_TOOLS.find((t) => t.id === id);
        const target = entry?.defaultSubtool ?? (id as ToolId);
        activateTool(target);
      },
      initial: "construction",
    }),
    props: new PropertiesPanel(
      document.getElementById("properties-panel")!,
      ctx,
      // Where the tool scope goes when split out into its own sidebar (#339).
      document.getElementById("tool-properties-panel") ?? undefined,
    ),
    stamps: new StampPanel(document.getElementById("stamp-panel")!, ctx, () => {
      ui.props.refresh();
    }),
    // The Layers panel gets a ctx whose `setSelection` also switches to
    // the matching selection tool (issue #381) — selecting an object in
    // the parts list leaves the user ready to manipulate it. Only the
    // panel's selections trigger this; canvas tools and the test hook
    // use the bare `ctx.setSelection` so they keep their own tool state.
    layers: new LayersPanel(document.getElementById("layers-panel")!, {
      ...ctx,
      setSelection: (sel, opts) => {
        ctx.setSelection(sel, opts);
        switchToSelectToolForSelection(sel);
      },
    }),
    exportDialog: new ExportDialog(document.getElementById("modal-host")!, ctx),
    advancedPrintDialog: new AdvancedPrintDialog(
      document.getElementById("modal-host")!,
      ctx,
    ),
  };

  ui.props.setShapeKindHandler((kind) => {
    (tools.shape as ShapeTool).setKind(kind);
    leatherShapeKindPref.set(kind);
    // Size lock / draw-from-center apply to shapes with a bounding box, so the
    // chips have to re-evaluate on a KIND change too, not just a tool change (#364).
    syncSnapApplicability();
  });
  // Offset shape kind (#972): typed Distance preset + the live readout back
  // into that input, mirroring the Stitch tool's Offset seam kind wiring.
  ui.props.setShapePresetOffsetDistanceHandler((mm) => {
    (tools.shape as ShapeTool).setPresetOffsetDistance(mm);
  });
  (tools.shape as ShapeTool).setLiveOffsetDistanceCallback((mm) => {
    ui.props.setShapeLiveOffsetDistance(Math.abs(mm));
  });
  // Stitch matching (#199): the panel's "Match with…" button activates the
  // Stitch tool and arms it to match the source seam to the next clicked one.
  ui.props.setStitchMatchArmHandler((source) => {
    if (activeTool !== tools.stitch) activateTool("stitch");
    (tools.stitch as StitchTool).armMatchPick(source);
  });
  ui.props.setShapeSlotWidthHandler((width) => {
    (tools.shape as ShapeTool).setSlotWidth(width);
  });
  // Stitch seam kind (#766): polyline (click-to-add) or a draggable
  // rectangle / ellipse / slot outline.
  ui.props.setStitchKindHandler((kind) => {
    (tools.stitch as StitchTool).setKind(kind);
    stitchKindPref.set(kind);
    syncSnapApplicability();
  });
  ui.props.setStitchSlotWidthHandler((width) => {
    (tools.stitch as StitchTool).setSlotWidth(width);
  });
  // Offset seam kind (#768): typed Distance preset + "single edge" toggle,
  // mirroring the construction offset controls.
  ui.props.setStitchPresetOffsetDistanceHandler((mm) => {
    (tools.stitch as StitchTool).setPresetOffsetDistance(mm);
  });
  ui.props.setStitchSingleSegmentOffsetHandler((on) => {
    (tools.stitch as StitchTool).setSingleSegmentOffset(on);
  });
  (tools.stitch as StitchTool).setLiveOffsetDistanceCallback((mm) => {
    ui.props.setStitchLiveOffsetDistance(Math.abs(mm));
  });
  ui.props.setShapePresetWidthHandler((mm) => {
    (tools.shape as ShapeTool).setPresetWidth(mm);
  });
  ui.props.setShapePresetHeightHandler((mm) => {
    (tools.shape as ShapeTool).setPresetHeight(mm);
  });
  // Live dim feedback: while the rect/ellipse drag is in progress the tool
  // pushes its current bounding box into the panel so the width/height
  // inputs mirror the drag (only the unlocked axis actually moves —
  // dimensionField's `setLive` is a no-op when a preset is set).
  (tools.shape as ShapeTool).setLiveDimsCallback((w, h) => {
    ui.props.updateLiveDimensions(w, h);
  });

  // Properties-panel detach button: pops the panel out of the rightbar and
  // into its own sidebar to the left of the parts catalog. State persists in
  // localStorage so the layout survives reloads. The shell element moves
  // wholesale (button + panel) between the two containers so PropertiesPanel
  // doesn't need to know about it.
  const propertiesShell = document.getElementById("properties-shell");
  const propertiesSidebar = document.getElementById("properties-sidebar");
  const propertiesDetachBtn = document.getElementById(
    "properties-detach-btn",
  ) as HTMLButtonElement | null;
  const rightbar = document.querySelector(".rightbar") as HTMLElement | null;
  const stampPanel = document.getElementById("stamp-panel");
  const appEl = document.getElementById("app");
  if (
    propertiesShell &&
    propertiesSidebar &&
    propertiesDetachBtn &&
    rightbar &&
    stampPanel &&
    appEl
  ) {
    const applyDetached = (detached: boolean) => {
      appEl.classList.toggle("properties-detached", detached);
      if (detached) {
        // Secondary sidebar order: tool-context (stamps) on top, then the
        // properties shell below. The stamp panel hides itself when the
        // active tool isn't a stamping one, so this only takes vertical
        // space when relevant. The parts list (layers) stays in the main
        // rightbar and stretches to fill 100% of it via CSS.
        propertiesSidebar.appendChild(stampPanel);
        propertiesSidebar.appendChild(propertiesShell);
      } else {
        // Re-append in the original render order: layers stays first, then
        // properties shell, then the stamp catalog at the bottom.
        rightbar.appendChild(propertiesShell);
        rightbar.appendChild(stampPanel);
      }
      // Detach button icon is owned by CSS (issue #181) — both
      // icons ship in the static HTML and the `properties-detached`
      // class on `#app` (toggled above) drives the swap. JS only
      // updates the tooltip / aria label for accessibility.
      propertiesDetachBtn.title = detached
        ? "Dock properties + stamps back into the parts list sidebar"
        : "Split properties + stamps into their own sidebar";
      propertiesDetachBtn.setAttribute(
        "aria-label",
        detached
          ? "Dock properties and stamps back into the parts list sidebar"
          : "Split properties and stamps into their own sidebar",
      );
    };
    const readPref = () => propertiesDetachedPref.get();
    // Portrait orientation docks the side panels to the bottom of the screen.
    // There the properties + stamps always sit in their own strip below the
    // parts list (split mode), regardless of the saved preference, and the
    // split toggle is hidden by CSS — so JS forces the detached layout while
    // the device is portrait and restores the saved choice on return to
    // landscape.
    const portraitMq = window.matchMedia("(orientation: portrait)");
    const syncDetached = () => {
      applyDetached(portraitMq.matches || readPref());
    };
    syncDetached();
    // Lift the static `disabled` attribute now that the click
    // handler is about to be wired (issue #181).
    propertiesDetachBtn.disabled = false;
    propertiesDetachBtn.addEventListener("click", () => {
      // The toggle is hidden in portrait (split mode is forced there); ignore
      // any stray click so it can't flip the forced layout.
      if (portraitMq.matches) return;
      const next = !appEl.classList.contains("properties-detached");
      applyDetached(next);
      propertiesDetachedPref.set(next);
    });
    // Re-apply when the orientation flips so rotating the device moves the
    // panels into / out of the forced-split layout.
    portraitMq.addEventListener("change", syncDetached);
  }

  // Left-handed layout (#332). One class on `#app`; the mirroring itself is
  // entirely CSS, since it is a question of which column each area lands in
  // and nothing about it needs to be computed. Applied before first paint
  // rather than after a settings change, so a left-handed maker never watches
  // the workspace assemble itself right-handed and then flip.
  //
  // Behind `left-handed-layout`. With the flag off a stored preference is
  // IGNORED rather than honoured-but-unreachable: the Settings row is the only
  // way to set it, so a flag that hid the row while still mirroring would
  // strand anyone who had turned it on in a workspace they could no longer
  // straighten.
  if (appEl) {
    const applyHandedness = (left: boolean) =>
      appEl.classList.toggle(
        "left-handed",
        left && isFeatureEnabled("left-handed-layout"),
      );
    applyHandedness(leftHandedPref.get());
    leftHandedPref.subscribe(applyHandedness);
    // The gate can change on its own: a developer flag, or the user opting
    // out of preview features (#1204).
    onFeatureFlagsChange(() => applyHandedness(leftHandedPref.get()));
  }

  // Touch device support (#40): one finger draws, two fingers pan and pinch.
  // Behind `touch-device-support`, and the flag is read here rather than inside
  // PointerInput so the input layer stays a pure gesture decoder — with the
  // flag off it never hears about touch drawing at all.
  const applyTouchDraw = () => {
    pointer.setTouchDrawEnabled(isFeatureEnabled("touch-device-support"));
  };
  applyTouchDraw();

  // Tool-settings sidebar toggle (#339): the same split gesture one level in.
  // The properties panel's two scopes — the tool's settings and the selected
  // object's — are stacked in one sidebar by default; this moves the tool half
  // out into a sidebar of its own, the same 280px width as every other, so
  // each scope gets a full column rather than half of one. Its own button on
  // the properties shell, wearing the same two-icon glyph pair as the sidebar
  // detach button above, because it is recognisably the same idea.
  const toolSplitBtn = document.getElementById(
    "tool-properties-split-btn",
  ) as HTMLButtonElement | null;
  if (toolSplitBtn && appEl) {
    // Another sidebar costs 280px, so there has to be a window to spend it
    // from. Kept low deliberately: 1280x800 and 1366x768 laptops are still
    // common, and a gate that hid the toggle on those would read as the
    // feature simply not being there. Below this even the two-sidebar case
    // leaves the canvas too narrow to draw in.
    // The threshold lives HERE and nowhere else: per #480 portrait is the only
    // responsive breakpoint in styles.css, and the layout keys off the class
    // this sets rather than a second media query that could disagree with it.
    const wideMq = window.matchMedia(
      "(min-width: 1200px) and (orientation: landscape)",
    );
    // What the user asked for, vs. whether the last render actually had two
    // scopes to separate — a tool with no selection has one, and splitting
    // that would open a sidebar with nothing in it.
    let wanted = false;
    let hasTwoScopes = false;
    const reflect = () => {
      // The class drives the grid, so it tracks whether the sidebar is really
      // showing — not merely what was asked for. Left on with an empty sidebar
      // it would hold a 280px column open around nothing.
      appEl.classList.toggle("tool-properties-split", wanted && hasTwoScopes);
      toolSplitBtn.hidden = !wideMq.matches || !hasTwoScopes;
      toolSplitBtn.title = wanted
        ? "Dock tool settings back above the object's properties"
        : "Move tool settings into their own sidebar";
      toolSplitBtn.setAttribute("aria-label", toolSplitBtn.title);
      toolSplitBtn.setAttribute("aria-pressed", wanted ? "true" : "false");
    };
    const apply = (on: boolean) => {
      wanted = on;
      ui.props.setToolSplit(on);
      reflect();
    };
    ui.props.setToolSplitAvailableHandler((available) => {
      // `setToolSplit` re-renders, which calls back into here — so only read
      // state here and reflect it; calling `apply` would recurse.
      hasTwoScopes = available;
      reflect();
    });
    apply(wideMq.matches && toolPropertiesSplitPref.get());
    // The panel's first render happened in its constructor, before the handler
    // above existed, so nothing has reported availability yet. One render now
    // settles it — otherwise the toggle stays hidden until the first selection
    // change, even with a selection already restored from the session.
    ui.props.refresh();
    toolSplitBtn.disabled = false;
    toolSplitBtn.addEventListener("click", () => {
      apply(!wanted);
      toolPropertiesSplitPref.set(wanted);
    });
    // A window too narrow for the third sidebar forces it off; widening again
    // restores the saved choice.
    wideMq.addEventListener("change", () => {
      apply(wideMq.matches && toolPropertiesSplitPref.get());
    });
  }
  ui.props.setCutoutKindHandler((kind) => {
    (tools.cutout as CutoutTool).setKind(kind);
    cutoutKindPref.set(kind);
    syncSnapApplicability();
  });
  ui.props.setCutoutPresetOffsetDistanceHandler((mm) => {
    (tools.cutout as CutoutTool).setPresetOffsetDistance(mm);
  });
  (tools.cutout as CutoutTool).setLiveOffsetDistanceCallback((mm) => {
    ui.props.setCutoutLiveOffsetDistance(Math.abs(mm));
  });
  ui.props.setCutoutSlotWidthHandler((width) => {
    (tools.cutout as CutoutTool).setSlotWidth(width);
  });
  ui.props.setCutoutPresetWidthHandler((mm) => {
    (tools.cutout as CutoutTool).setPresetWidth(mm);
  });
  ui.props.setCutoutPresetHeightHandler((mm) => {
    (tools.cutout as CutoutTool).setPresetHeight(mm);
  });
  (tools.cutout as CutoutTool).setLiveDimsCallback((w, h) => {
    ui.props.updateLiveDimensions(w, h);
  });

  // Hardware tool — type / colour / size / side handlers all flow into the
  // same HardwareTool instance so the live ghost preview tracks the panel
  // selections immediately.
  ui.props.setHardwareTypeHandler((type) => {
    (tools.hardware as HardwareTool).setType(type);
  });
  ui.props.setHardwareColorHandler((color) => {
    (tools.hardware as HardwareTool).setColor(color);
  });
  ui.props.setHardwareSizeHandler((mm) => {
    (tools.hardware as HardwareTool).setSizeMm(mm);
  });
  ui.props.setHardwareSideHandler((side) => {
    (tools.hardware as HardwareTool).setSide(side);
  });

  // Construction tool — kind / slot width handlers flow into the tool's
  // current state. Preset width / height are accepted by the panel but
  // not yet stored on the tool (rect / ellipse currently free-drag); we
  // wire the panel's setters to no-ops so the inputs still validate.
  ui.props.setConstructionKindHandler((kind) => {
    (tools.construction as ConstructionTool).setKind(kind);
    constructionKindPref.set(kind);
    syncSnapApplicability();
  });
  ui.props.setConstructionSlotWidthHandler((mm) => {
    (tools.construction as ConstructionTool).setSlotWidth(mm);
  });
  ui.props.setConstructionPresetWidthHandler((mm) => {
    (tools.construction as ConstructionTool).setPresetWidth(mm);
  });
  ui.props.setConstructionPresetHeightHandler((mm) => {
    (tools.construction as ConstructionTool).setPresetHeight(mm);
  });
  // The corner radius a rectangle is DRAWN with (#1069) — each tool its own.
  ui.props.setDrawCornerRadiusHandler((scope, mm) => {
    if (scope === "shape") (tools.shape as ShapeTool).setDrawCornerRadius(mm);
    else if (scope === "cutout")
      (tools.cutout as CutoutTool).setDrawCornerRadius(mm);
    else (tools.construction as ConstructionTool).setDrawCornerRadius(mm);
  });
  ui.props.setConstructionPresetLineLengthHandler((mm) => {
    (tools.construction as ConstructionTool).setPresetLineLength(mm);
  });
  ui.props.setConstructionPresetLineAngleHandler((deg) => {
    (tools.construction as ConstructionTool).setPresetLineAngleDeg(deg);
  });
  ui.props.setLeatherPresetFreehandSmoothnessHandler((v) => {
    // One preset across the three leather freehand tools.
    (tools.shape as ShapeTool).setFreehandSmoothness(v);
    (tools.cutout as CutoutTool).setFreehandSmoothness(v);
    (tools.stitch as StitchTool).setFreehandSmoothness(v);
    leatherFreehandSmoothnessPref.set(v);
  });
  ui.props.setConstructionPresetFreehandSmoothnessHandler((v) => {
    (tools.construction as ConstructionTool).setPresetFreehandSmoothness(v);
    constructionFreehandSmoothnessPref.set(v);
  });
  ui.props.setConstructionPresetOffsetDistanceHandler((mm) => {
    (tools.construction as ConstructionTool).setPresetOffsetDistance(mm);
  });
  ui.props.setConstructionSingleSegmentOffsetHandler((on) => {
    (tools.construction as ConstructionTool).setSingleSegmentOffset(on);
  });
  ui.props.setConstructionOffsetRoundedCornersHandler((on) => {
    (tools.construction as ConstructionTool).setOffsetRoundedCorners(on);
  });
  ui.props.setLeatherOffsetRoundedCornersHandler((on) => {
    // One preference across the three leather offset kinds — outline offsets
    // on Shape / Cut-out, and the Stitch edge offset (#423).
    (tools.shape as ShapeTool).setOffsetRoundedCorners(on);
    (tools.cutout as CutoutTool).setOffsetRoundedCorners(on);
    (tools.stitch as StitchTool).setOffsetRoundedCorners(on);
  });
  ui.props.setConstructionPresetCornerRadiusHandler((mm) => {
    (tools.construction as ConstructionTool).setPresetCornerRadius(mm);
    // Mirror into the Scene so corner handles re-render at the new
    // arc-centre distance — picking a 10 mm chip should visibly
    // push handles outward on un-rounded corners (#100).
    scene.setRoundPresetRadius(mm);
  });
  // Live dim feedback during rect/ellipse construction draws — mirrors
  // how the Shape and Cutout tools push their in-progress bounding box
  // into the panel's width/height inputs.
  (tools.construction as ConstructionTool).setLiveDimsCallback((w, h) => {
    ui.props.updateLiveDimensions(w, h);
  });
  // Same live-feedback bridge for the Line draw — pushes the active
  // length / angle into the matching panel inputs so the user can
  // watch the value change as they drag.
  (tools.construction as ConstructionTool).setLiveLineCallback(
    (lengthMm, angleDeg) => {
      ui.props.updateLiveLine(lengthMm, angleDeg);
    },
  );
  // Per-line panel live mirror — fires every frame while the user
  // drags a vertex of a selected Line primitive, so the panel's
  // Length / Angle inputs track the live geometry without waiting
  // for the drag-commit.
  (tools.construction as ConstructionTool).setLiveLinePrimitiveCallback(
    (lengthMm, angleDeg) => {
      ui.props.updateLineLivePrimitive(lengthMm, angleDeg);
    },
  );
  // Per-line panel live mirror for the corner-resize drag (#388): the tool
  // pushes the in-progress (uncommitted) line each frame so the panel's
  // dimension readouts track the resize; null on drag end restores the
  // committed geometry.
  (tools.construction as ConstructionTool).setLiveResizeCallback((line) => {
    ui.props.setLiveConstructionLine(line);
  });
  // Live offset-distance feedback during an offset drag (#29
  // follow-up). The tool fires this every frame while the drag is
  // re-deriving distance; the panel mirrors the magnitude into its
  // Distance input so the user reads the perpendicular as they
  // sweep. Pushed inert (no callback fire) when the user has a
  // typed preset distance — see ConstructionTool.refreshOffsetPreview.
  (tools.construction as ConstructionTool).setLiveOffsetDistanceCallback(
    (mm) => {
      // The input renders magnitude only — direction is communicated
      // by which side the user is dragging on, not by a sign in the
      // text field.
      ui.props.setLiveOffsetDistance(Math.abs(mm));
    },
  );
  // Same live-feedback bridge for the round-corner drag (issue #100):
  // mirrors the current drag radius into the panel's Radius input so
  // the user sees the value as they sweep. Stays silent while a typed
  // preset is locked, by the same contract as the offset live mirror.
  (tools.construction as ConstructionTool).setLiveCornerRadiusCallback((mm) => {
    ui.props.setLiveCornerRadius(mm);
  });
  // Same palette + live bridge for the leather Round method (#974): the panel
  // owns the preset, the tool applies it, and the drag mirrors back.
  ui.props.setLeatherPresetCornerRadiusHandler((mm) => {
    (tools.leatherRound as LeatherRoundTool).setPresetRadius(mm);
  });
  (tools.leatherRound as LeatherRoundTool).setLiveRadiusCallback((mm) => {
    ui.props.setLiveCornerRadius(mm);
  });

  // Bridge the panel's "add constraint" palette to the Construction
  // tool's pick session. Starting a pick auto-activates the
  // Construction tool (in Edit sub-mode, with handles visible) so
  // anchor / curve / line taps are captured by the picker. The
  // panel re-renders on every state change so the status block
  // tracks ref count and the active chip stays highlighted.
  ui.props.setConstraintPickerProvider({
    start: (kind, originLineId) => {
      if (activeTool !== tools.construction) {
        activateTool("construction");
      }
      (tools.construction as ConstructionTool).startConstraintPicker(
        kind,
        originLineId,
      );
      // Mirror the sub-mode change the picker forces internally so
      // the toolbar / panel pickers reflect Edit being active.
      ui.props.setConstructionSubMode("edit");
      syncConstructionSubbar();
    },
    cancel: () => {
      (tools.construction as ConstructionTool).cancelConstraintPicker();
    },
    state: () => {
      const picker = (
        tools.construction as ConstructionTool
      ).getConstraintPicker();
      return picker;
    },
    subscribe: (fn) =>
      (tools.construction as ConstructionTool).addConstraintPickerListener(fn),
  });

  // Tools that operate on existing parts — disabled in the toolbar while the
  // project is empty so the user is funneled to Shape (the only thing they
  // can do is draw the first part). Switching to one of these via keyboard
  // shortcut is a no-op when there are no parts to act on.
  const TOOLS_REQUIRING_PART: ReadonlySet<ToolId> = new Set([
    "select",
    "hole",
    "cutout",
    "stitch",
    "stamp",
    "lineStamp",
    "fill",
    "freePath",
    "hardware",
  ]);

  let activeTool: Tool | null = null;
  // Forward-declared so `activateTool` can call it during bootstrap
  // before the snap toolbar wires the real implementation. The
  // no-op default keeps the early call safe; the real version is
  // assigned after the snap-group setup further down.
  let updateRoundModeToggle: () => void = () => {};
  // Snap-chip applicability (#364) — forward-declared like the round toggle
  // and the rays toggle, so `activateTool` can re-evaluate which chips apply
  // without depending on the snap toolbar's declaration order.
  let syncSnapApplicability: () => void = () => {};
  // Rays show/hide toggle (#745) — forward-declared like the round toggle so
  // the bootstrap `activateTool` call is safe; real impl assigned further down.
  let syncRaysToggle: () => void = () => {};
  // Global Shift modifier state, mirrored from window keydown /
  // keyup. Hoisted up here (rather than next to the round-mode
  // setup) so `syncSnapButtons` — defined earlier in the bootstrap
  // — can read it without hitting a TDZ on first call.
  let roundModifierHeld = false;
  // Global Alt / Option modifier state, mirrored from window
  // keydown / keyup the same way as Shift above. Drives the
  // "draw from centre" visual flip on the centre snap chip and
  // re-fires the active draw tool's preview mid-drag (issue #389).
  let centerModifierHeld = false;
  // Global Ctrl / Cmd modifier state — the dedicated angle-snap key (#927),
  // so angle can be forced without Shift's size-link side effect. Lights the
  // angle snap chip and re-fires the active draw tool's preview mid-drag.
  let angleKeyHeld = false;
  // App clipboard for Cmd+C / Cmd+V / Cmd+F (#763): the captured payload
  // plus how many times it's been pasted with the nudge, so repeated Cmd+V
  // pastes cascade one step further each time instead of stacking. Session-
  // scoped — copying replaces it, nothing persists.
  let appClipboard: { payload: ClipboardPayload; pasteCount: number } | null =
    null;
  // Same hoist for the round-corner toggle button reference, so
  // `syncSnapButtons` can hide/show it alongside the snap children
  // when the master toggle flips without depending on declaration
  // order with the round-mode setup block.
  const roundModeBtn = document.getElementById(
    "round-mode-toggle",
  ) as HTMLButtonElement | null;
  // Trace needs at least one construction line to trace; Fill needs an
  // enclosed region — a bounded face of the construction arrangement — to fill
  // (#752). The arrangement is O(n²) to build, so memoize it against the
  // construction lines' geometry: most commits (part moves, undo landing on the
  // same lines) reuse the cached answer.
  let fillableCache: { key: string; value: boolean } | null = null;
  function hasFillableRegion(): boolean {
    const lines = projectConstructionLines(history.state);
    if (lines.length === 0) return false;
    const key = lines
      .map((l) => `${l.id}${l.visible === false ? 0 : 1}${l.pathData}`)
      .join("");
    if (fillableCache?.key === key) return fillableCache.value;
    const arr = buildConstructionArrangement(lines, scene.getPaperScope());
    const value = findFaces(arr.nodes, arr.edges).length > 0;
    fillableCache = { key, value };
    return value;
  }
  /** The canvas display mode the Edit-points tool needs for whatever is
   *  selected. A seam is only drawn in stitch display, so editing one means
   *  being in it; a part or cut-out outline wants the structural view. Since
   *  #1033 the tool follows the selection rather than a mode of its own, so
   *  this is re-applied on every selection change as well as on activation. */
  function outlineEditDisplayMode(): "structural" | "stitch" {
    return scene.selectionTransformKind() === "stitch"
      ? "stitch"
      : "structural";
  }
  function syncOutlineEditDisplayMode() {
    if (activeTool?.id !== "outlineEdit") return;
    scene.setEditMode(outlineEditDisplayMode());
  }
  function activateTool(id: ToolId) {
    const t = tools[id];
    if (!t) return;
    // While the saved project loads (#1230) the project is still empty, so
    // the checks below would turn away the tool the user last had, and the
    // toolbar would jump once it arrives. Take it as is; the load's end
    // re-runs `syncToolAvailability` against the real project.
    const loaded = projectLoad === "done";
    if (
      loaded &&
      TOOLS_REQUIRING_PART.has(id) &&
      history.state.pieces.length === 0
    ) {
      // Tool needs at least one part to be useful; silently ignore so a
      // keyboard shortcut on an empty canvas doesn't strand the user on a
      // disabled tool.
      return;
    }
    // Trace / Fill have nothing to act on without construction geometry (#752):
    // Trace needs a line, Fill needs an enclosed region. Silently ignore so a
    // keyboard shortcut doesn't strand the user on an unusable tool.
    if (
      loaded &&
      id === "trace" &&
      projectConstructionLines(history.state).length === 0
    ) {
      return;
    }
    if (loaded && id === "region" && !hasFillableRegion()) {
      return;
    }
    activeTool?.onDeactivate?.();
    activeTool = t;
    // No tool input until the saved project has loaded (#1230); panning and
    // zooming still work.
    pointer.setTool(projectLoad === "done" ? t : null);
    canvas.style.cursor = t.cursor ?? "crosshair";
    // Toolbar shows the *parent* group as active when a child tool is
    // picked (issue #150): the user clicks Hole, the Leather button
    // lights up + the Leather subbar shows Hole highlighted. Stand-
    // alone tools (Construction / Hardware) map to themselves.
    ui.toolbar.setActive(parentToolbarIdFor(id));
    syncGroupSubbars();
    ui.props.setTool(id);
    ui.stamps.setTool(id);
    // Push the current effective round-mode into the Scene so its
    // corner-handle highlight state matches the active tool when
    // entering Round (issue #100 follow-up). `updateRoundModeToggle`
    // is a forward-declared `let` binding initialised to a no-op so
    // the first `activateTool` call during bootstrap (before the
    // snap toolbar wires the real implementation) is a safe no-op.
    updateRoundModeToggle();
    syncRaysToggle();
    // Which snap chips mean anything depends on the tool (#364) — forward
    // declared like the two above, so the bootstrap's first `activateTool`
    // (before the snap toolbar is wired) is a safe no-op.
    syncSnapApplicability();
    scene.setEditMode(
      id === "hole" || id === "cutout"
        ? "structural"
        : id === "outlineEdit"
          ? // Edit points renders structurally for piece / cutout targets and
            // in stitch display for seams (#970) — read off the SELECTION
            // since #1033, because that is what the tool now edits.
            outlineEditDisplayMode()
          : id === "stitch"
            ? "stitch"
            : "none",
    );
    t.onActivate?.();
    // Sync the touch fly-outs — switching tools always hides them (any
    // draft got cancelled by onDeactivate); switching to a draft-driven
    // tool shows the matching flyout only if the draft is non-empty
    // (it's empty right after activation).
    syncStitchFlyout();
    syncConstructionFlyout();
    syncTraceFlyout();
    syncRegionFlyout();
    syncConstructionSubbar();
    lastToolPref.set(id);
  }

  // Issue #381 — picking an object from the Layers panel (or via
  // Cmd/Ctrl+A) should leave the user on the matching *selection* tool,
  // ready to move / transform what they just selected, rather than
  // stranded on whatever draw / draft sub-tool happened to be active.
  // Construction objects route to the Construction tool's Select
  // sub-mode; parts (and their sub-items) route to the leather Select
  // tool. An empty selection — or a bare stitching-scope pick — switches
  // nothing.
  function switchToSelectToolForSelection(
    sel: Parameters<typeof scene.setSelection>[0],
  ) {
    const hasConstruction =
      (sel.constructionIds?.length ?? 0) > 0 ||
      (sel.constructionGroupIds?.length ?? 0) > 0 ||
      !!sel.constructionId;
    if (hasConstruction) {
      if (activeTool !== tools.construction) activateTool("construction");
      setConstructionSubMode("select");
      return;
    }
    const hasPart =
      !!sel.pieceId ||
      (sel.pieceIds?.length ?? 0) > 0 ||
      !!sel.decorationId ||
      !!sel.holeId ||
      !!sel.cutoutId ||
      !!sel.stitchId;
    // The leather Mirror (#973) and Round (#974) methods ACT ON the selection —
    // mirroring it, rounding its corners — so picking an object from the Layers
    // panel is choosing what to work on, not a signal that the user is stranded
    // and wants to move it. Switching them to Select is the opposite of #381's
    // intent here: it drops the tool the user is actively working in, one row
    // click at a time.
    const selectionDrivenTool =
      activeTool === tools.leatherMirror || activeTool === tools.leatherRound;
    if (hasPart && activeTool !== tools.select && !selectionDrivenTool) {
      activateTool("select");
    }
  }

  // Touch fly-outs next to the draw / draft tool buttons — give users without
  // a keyboard a way to commit (✓) or discard (✗) an in-progress draft. All
  // six draft tools (stitch / construction / shape / cutout / trace / region)
  // share ONE builder (`makeDraftFlyout`), so their scaffold, anchoring,
  // keyboard-hint tooltip, resize handling, and visibility/enable sync live in
  // a single place (#804 S2 / #811). Each fly-out is anchored via fixed
  // positioning to its toolbar button so it isn't clipped by the toolbar's
  // auto-overflow.
  const stitchBtn = ui.toolbar.getButton("stitch");

  // Show the keyboard-shortcut hint in the hover tooltip only on devices
  // where a hover-capable, fine pointer suggests a mouse/trackpad+keyboard
  // setup. Touch-only devices (iPad without keyboard, phones) get the plain
  // action description, since "(Enter)" / "(Esc)" would be misleading. Reacts
  // to media-query changes so attaching a keyboard mid-session updates the
  // tooltip on the next hover.
  const keyboardLikely = window.matchMedia(
    "(hover: hover) and (pointer: fine)",
  );

  // One parameterized fly-out builder for all six draft tools. Returns the
  // `sync` fn (visibility + confirm-disabled + reposition) so the caller can
  // also drive it from `activateTool` (switching tools hides stale fly-outs).
  //   - `enableGate`: a `minVertices` number (polygon / stitch / construction)
  //     keeps confirm disabled until `count >= minVertices`; a predicate
  //     (Trace `canCommit()`, Region always-true) disables until it returns
  //     true.
  //   - `onDraft`: optional per-draft-change hook (Trace mirrors its draft's
  //     traced lines into the properties panel). It fires only on real draft
  //     changes, NOT on the `activateTool`-driven sync, matching the previous
  //     per-tool wiring.
  function makeDraftFlyout(opts: {
    cssClass: string;
    finishNoun: string;
    discardNoun?: string;
    isActive: () => boolean;
    anchor: () => HTMLElement | null;
    count: () => number;
    enableGate: number | (() => boolean);
    onConfirm: () => void;
    onCancel: () => void;
    registerDraftListener: (sync: () => void) => void;
    onDraft?: () => void;
  }): () => void {
    const discardNoun = opts.discardNoun ?? opts.finishNoun;
    const btnClass = `${opts.cssClass}-btn`;
    const flyout = document.createElement("div");
    flyout.className = opts.cssClass;
    // Stable per-fly-out handle. Four of the six share `construction-flyout`
    // styling, so the class alone can't address one of them — in markup or in
    // a test.
    flyout.dataset.flyout = opts.finishNoun.replace(/\s+/g, "-");
    const confirm = document.createElement("button");
    confirm.className = `${btnClass} confirm`;
    confirm.setAttribute("aria-label", `Finish ${opts.finishNoun}`);
    confirm.innerHTML = CHECK_SVG;
    confirm.addEventListener("click", () => opts.onConfirm());
    const cancel = document.createElement("button");
    cancel.className = `${btnClass} cancel`;
    cancel.setAttribute("aria-label", `Discard ${discardNoun}`);
    cancel.innerHTML = X_SVG;
    cancel.addEventListener("click", () => opts.onCancel());
    flyout.appendChild(confirm);
    flyout.appendChild(cancel);
    document.body.appendChild(flyout);

    const syncTips = () => {
      const kb = keyboardLikely.matches;
      confirm.title = kb
        ? `Finish ${opts.finishNoun} (Enter)`
        : `Finish ${opts.finishNoun}`;
      cancel.title = kb
        ? `Discard ${discardNoun} (Esc)`
        : `Discard ${discardNoun}`;
    };
    syncTips();
    keyboardLikely.addEventListener("change", syncTips);

    const position = () => {
      const anchor = opts.anchor();
      if (!anchor) return;
      const rect = anchor.getBoundingClientRect();
      flyout.style.top = `${rect.top}px`;
      flyout.style.left = `${rect.right + 8}px`;
    };

    const sync = () => {
      const count = opts.count();
      const visible = opts.isActive() && count >= 1;
      flyout.classList.toggle("open", visible);
      // Keep confirm disabled until the draft can actually commit, so the
      // green check never reads as a no-op cancel: a vertex-count threshold
      // for the polygon / stitch / construction drafts, or a predicate for
      // Trace (`canCommit`) / Region (always allowed).
      confirm.disabled =
        typeof opts.enableGate === "number"
          ? count < opts.enableGate
          : !opts.enableGate();
      if (visible) position();
    };

    opts.registerDraftListener(() => {
      sync();
      opts.onDraft?.();
    });
    window.addEventListener("resize", () => {
      if (flyout.classList.contains("open")) position();
    });
    return sync;
  }

  const leatherModeAnchor = (mode: string) => () =>
    document.querySelector<HTMLElement>(
      `.subtool-btn[data-leather-mode="${mode}"]`,
    ) ?? document.getElementById("leather-subtoolbar");

  // Stitch — anchored to the Stitch mode sub-sub-tool (#741). `stitchBtn` was
  // a top-level toolbar lookup that never resolved for stitch, so before this
  // the fly-out was never positioned at all (#754); fall back to the whole
  // leather sub-toolbar so it floats to the RIGHT of the column regardless.
  // Confirm needs ≥ 2 vertices to commit a real seam.
  const syncStitchFlyout = makeDraftFlyout({
    cssClass: "stitch-flyout",
    finishNoun: "stitch line",
    isActive: () => activeTool === tools.stitch,
    anchor: () =>
      document.querySelector<HTMLElement>(
        '.subtool-btn[data-leather-mode="stitch"]',
      ) ??
      document.getElementById("leather-subtoolbar") ??
      stitchBtn,
    count: () => (tools.stitch as StitchTool).vertexCount,
    enableGate: 2,
    onConfirm: () => (tools.stitch as StitchTool).handleFinish(),
    onCancel: () => (tools.stitch as StitchTool).handleDiscard(),
    registerDraftListener: (sync) =>
      (tools.stitch as StitchTool).setDraftChangeListener(sync),
  });

  // Construction polyline / polygon — `vertexCount` only ever goes above zero
  // for those two kinds (rect / ellipse / slot / freehand don't populate
  // `polyPoints`), so the fly-out automatically stays hidden while drawing the
  // drag-style kinds. Anchored on the Draw sub-tool button (looked up lazily
  // because the sub-toolbar is constructed further down in this setup block),
  // falling back to the main Construction toolbar button. Confirm needs ≥ 2
  // vertices to commit a real line.
  const syncConstructionFlyout = makeDraftFlyout({
    cssClass: "construction-flyout",
    finishNoun: "construction line",
    isActive: () => activeTool === tools.construction,
    anchor: () =>
      subtoolButtons.get("draw") ?? ui.toolbar.getButton("construction"),
    count: () => (tools.construction as ConstructionTool).vertexCount,
    enableGate: 2,
    onConfirm: () => (tools.construction as ConstructionTool).handleFinish(),
    onCancel: () => (tools.construction as ConstructionTool).handleDiscard(),
    registerDraftListener: (sync) =>
      (tools.construction as ConstructionTool).setDraftChangeListener(sync),
  });
  // Auto-polygon live panel mirror (#931): the stage-2 hover pushes the
  // derived Sides / Radius into the polygon fields; commit / cancel reverts.
  // Wired identically for every tool that runs the shared regular-polygon
  // gesture (#934) — construction plus the leather Shape / Cutout / Stitch.
  for (const t of [
    tools.construction as ConstructionTool,
    tools.shape as ShapeTool,
    tools.cutout as CutoutTool,
    tools.stitch as StitchTool,
  ]) {
    t.setPolygonLiveCallbacks(
      (sides, radiusMm) => ui.props.updateLivePolygon(sides, radiusMm),
      (mm) => ui.props.updateLivePolygonSideLength(mm),
      (mm) => ui.props.updateLivePolygonRadius(mm),
      () => ui.props.clearLivePolygon(),
    );
  }
  // Mid-gesture panel edits (#934): editing Sides / Radius / Side length /
  // Equilateral while a polygon stage is in flight re-renders the staged
  // preview + rays immediately (no-op when nothing is staged).
  ctx.onSettingsChanged(() => {
    if (activeTool === tools.construction)
      (tools.construction as ConstructionTool).refreshPolygonGesture();
    else if (activeTool === tools.shape)
      (tools.shape as ShapeTool).refreshPolygonGesture();
    else if (activeTool === tools.cutout)
      (tools.cutout as CutoutTool).refreshPolygonGesture();
    else if (activeTool === tools.stitch)
      (tools.stitch as StitchTool).refreshPolygonGesture();
  });

  // Leather Shape / Cutout polygon fly-outs (#779) — reuse the shared
  // `construction-flyout` styling. A polygon needs ≥ 3 vertices to enclose an
  // area, so confirm stays disabled until then.
  makeDraftFlyout({
    cssClass: "construction-flyout",
    finishNoun: "part",
    isActive: () => activeTool === tools.shape,
    anchor: leatherModeAnchor("leather"),
    count: () => (tools.shape as ShapeTool).vertexCount,
    enableGate: 3,
    onConfirm: () => (tools.shape as ShapeTool).handleFinish(),
    onCancel: () => (tools.shape as ShapeTool).handleDiscard(),
    registerDraftListener: (sync) =>
      (tools.shape as ShapeTool).setDraftChangeListener(sync),
  });
  // Leather Mirror (#973). Its "draft" is the armed reflection under the cursor
  // rather than a click-built point list, but it exposes the same contract, so
  // it uses the shared builder unchanged. `count` is 0/1, hence an enable gate
  // of 1 — armed means committable. Anchored on the Mirror method button.
  makeDraftFlyout({
    cssClass: "construction-flyout",
    finishNoun: "mirror",
    isActive: () => activeTool === tools.leatherMirror,
    anchor: () =>
      document.querySelector<HTMLElement>(
        '.subtool-btn[data-leather-method="mirror"]',
      ) ?? document.getElementById("leather-subtoolbar"),
    count: () => (tools.leatherMirror as LeatherMirrorTool).vertexCount,
    enableGate: 1,
    onConfirm: () => (tools.leatherMirror as LeatherMirrorTool).handleFinish(),
    onCancel: () => (tools.leatherMirror as LeatherMirrorTool).handleDiscard(),
    registerDraftListener: (sync) =>
      (tools.leatherMirror as LeatherMirrorTool).setDraftChangeListener(sync),
  });
  makeDraftFlyout({
    cssClass: "construction-flyout",
    finishNoun: "cutout",
    isActive: () => activeTool === tools.cutout,
    anchor: leatherModeAnchor("cutout"),
    count: () => (tools.cutout as CutoutTool).vertexCount,
    enableGate: 3,
    onConfirm: () => (tools.cutout as CutoutTool).handleFinish(),
    onCancel: () => (tools.cutout as CutoutTool).handleDiscard(),
    registerDraftListener: (sync) =>
      (tools.cutout as CutoutTool).setDraftChangeListener(sync),
  });

  // Trace — accept (close the outline into a piece) or discard. Anchored on
  // the Trace method sub-tool (renamed in #741), falling back to the whole
  // leather sub-toolbar then the Leather toolbar button. Confirm enables when
  // the draft can commit: a closed loop for leather / cutout, or an open seam
  // (≥ 1 leg) in stitch mode (#675). Uses `handleCancel` (not `handleDiscard`)
  // and mirrors the draft's traced lines into the properties panel — but only
  // re-renders when the set actually changes (the listener also fires on
  // hover, where the line set is unchanged), so the panel doesn't thrash.
  let lastTraceDraftKey = "";
  const syncTraceFlyout = makeDraftFlyout({
    cssClass: "trace-flyout",
    finishNoun: "traced piece",
    isActive: () => activeTool === tools.trace,
    anchor: () =>
      document.querySelector<HTMLElement>(
        '.subtool-btn[data-leather-method="trace"]',
      ) ??
      document.getElementById("leather-subtoolbar") ??
      ui.toolbar.getButton("leather"),
    count: () => (tools.trace as TraceTool).vertexCount,
    enableGate: () => (tools.trace as TraceTool).canCommit(),
    onConfirm: () => (tools.trace as TraceTool).handleFinish(),
    onCancel: () => (tools.trace as TraceTool).handleCancel(),
    registerDraftListener: (sync) =>
      (tools.trace as TraceTool).setDraftChangeListener(sync),
    onDraft: () => {
      const ids = (tools.trace as TraceTool).draftLineIds();
      const key = ids.join("|");
      if (key !== lastTraceDraftKey) {
        lastTraceDraftKey = key;
        ui.props.setTraceDraft(ids);
      }
    },
  });

  // Region — done / cancel for the region selection so it works on touch
  // without Enter / Escape. Anchored on the Fill method sub-tool (was
  // `region`, renamed in #741). Confirm is always enabled (no vertex
  // threshold); the gate reads `selectedCount` for visibility.
  const syncRegionFlyout = makeDraftFlyout({
    cssClass: "trace-flyout",
    finishNoun: "region piece",
    discardNoun: "region selection",
    isActive: () => activeTool === tools.region,
    anchor: () =>
      document.querySelector<HTMLElement>(
        '.subtool-btn[data-leather-method="fill"]',
      ) ??
      document.getElementById("leather-subtoolbar") ??
      ui.toolbar.getButton("leather"),
    count: () => (tools.region as RegionTool).selectedCount,
    enableGate: () => true,
    onConfirm: () => (tools.region as RegionTool).handleFinish(),
    onCancel: () => (tools.region as RegionTool).handleCancel(),
    registerDraftListener: (sync) =>
      (tools.region as RegionTool).setDraftChangeListener(sync),
  });

  // Leather + Stamping sub-toolbars (issue #150). Their buttons live
  // in `index.html` with `data-subtool-id` matching the underlying
  // Tool's id — clicking one just calls `activateTool(id)` and lets
  // the toolbar / subbar sync pass take care of the highlights.
  const leatherSubbar = document.getElementById(
    "leather-subtoolbar",
  ) as HTMLElement | null;
  const stampingSubbar = document.getElementById(
    "stamping-subtoolbar",
  ) as HTMLElement | null;
  const groupSubbarButtons = new Map<string, HTMLButtonElement>();
  for (const subbar of [leatherSubbar, stampingSubbar]) {
    if (!subbar) continue;
    for (const btn of subbar.querySelectorAll<HTMLButtonElement>(
      ".subtool-btn[data-subtool-id]",
    )) {
      const id = btn.dataset.subtoolId;
      if (!id) continue;
      groupSubbarButtons.set(id, btn);
      btn.addEventListener("click", () => activateTool(id as ToolId));
      btn.disabled = false;
    }
  }

  // Leather "method × mode" sub-tools (issue #741). The Draw / Trace / Fill
  // buttons pick the drawing *method*; the Leather / Cutout / Stitch
  // sub-sub-tools pick what it *creates*, and appear (like the construction
  // Edit tool's sub-sub-tools) only while a method is active. `leatherMode` is
  // the persisted last-used mode so it survives switching between methods.
  type LeatherMethod =
    | "draw"
    | "trace"
    | "fill"
    | "edit"
    | "mirror"
    | "round"
    | "slice"
    | "delete";
  let leatherMode: ShapeMode = "leather";
  const leatherMethodButtons = new Map<LeatherMethod, HTMLButtonElement>();
  const leatherModeButtons = new Map<ShapeMode, HTMLButtonElement>();
  const leatherEditActionButtons = new Map<
    OutlineEditAction,
    HTMLButtonElement
  >();
  const leatherEditActionDivider =
    leatherSubbar?.querySelector<HTMLDivElement>(
      "#leather-edit-action-divider",
    ) ?? null;
  const leatherModeDivider =
    leatherSubbar?.querySelector<HTMLDivElement>("#leather-mode-divider") ??
    null;
  /** The leather method a tool id belongs to, or null for non-drawing tools
   *  (select / hole) that have no mode sub-sub-tools. Draw is three separate
   *  tools; Trace / Fill are one tool each with a `setMode`. */
  function leatherMethodOf(id: string): LeatherMethod | null {
    if (id === "shape" || id === "cutout" || id === "stitch") return "draw";
    if (id === "trace") return "trace";
    if (id === "region") return "fill";
    if (id === "outlineEdit") return "edit";
    if (id === "leatherMirror") return "mirror";
    if (id === "leatherRound") return "round";
    if (id === "leatherSlice") return "slice";
    if (id === "leatherDelete") return "delete";
    return null;
  }
  /** Activate the tool for a (method, mode) pair. Draw routes to the three
   *  drawing tools; Trace / Fill activate their one tool and set its mode.
   *  Trace and Region are always kept in the same mode so it carries across a
   *  method switch. */
  function applyLeatherSelection(method: LeatherMethod, mode: ShapeMode) {
    leatherMode = mode;
    (tools.trace as TraceTool).setMode(mode);
    (tools.region as RegionTool).setMode(mode);
    if (method === "draw") {
      activateTool(
        mode === "cutout" ? "cutout" : mode === "stitch" ? "stitch" : "shape",
      );
    } else if (method === "edit") {
      // Mode-independent since #1033: Edit points works on whichever part,
      // cut-out or seam is selected (or clicked), so there is no Leather /
      // Cut-out / Stitch choice to carry in.
      activateTool("outlineEdit");
    } else if (method === "delete") {
      // Mode-independent, like Mirror and Round: you delete an object, not a
      // mode (#1000).
      activateTool("leatherDelete");
    } else if (method === "slice") {
      // Mode-independent for the same reason, and narrower than the others:
      // Slice only cuts PARTS (#1001), so a mode row would be offering two
      // choices it can't honour.
      activateTool("leatherSlice");
    } else if (method === "round") {
      // Like Mirror, Round has no mode of its own — corners belong to outlines,
      // not to the Leather / Cutout / Stitch mode last drawn in (#974).
      activateTool("leatherRound");
    } else if (method === "mirror") {
      // Mirror reflects whatever is SELECTED, so it has no mode of its own —
      // `leatherMode` is left as it was and restored when the user goes back to
      // a mode-bearing method (#973).
      activateTool("leatherMirror");
    } else {
      activateTool(method === "trace" ? "trace" : "region");
    }
  }
  if (leatherSubbar) {
    for (const btn of leatherSubbar.querySelectorAll<HTMLButtonElement>(
      ".subtool-btn[data-leather-method]",
    )) {
      const method = btn.dataset.leatherMethod as LeatherMethod;
      leatherMethodButtons.set(method, btn);
      // Every one of these buttons carries its glyph in the static HTML now
      // (#216) — a `<!-- svg:… -->` marker the build-time inliner expands
      // through the same `stripIconSvg` the runtime icon constants use. They
      // are one-per-button and never change, so there was nothing for JS to
      // decide; injecting them only meant the page rendered five empty
      // buttons until the bundle ran.
      btn.disabled = false;
      btn.addEventListener("click", () =>
        applyLeatherSelection(method, leatherMode),
      );
    }
    for (const btn of leatherSubbar.querySelectorAll<HTMLButtonElement>(
      ".subtool-btn[data-leather-mode]",
    )) {
      const mode = btn.dataset.leatherMode as ShapeMode;
      leatherModeButtons.set(mode, btn);
      btn.disabled = false;
      btn.addEventListener("click", () => {
        const method = leatherMethodOf(activeTool?.id ?? "") ?? "draw";
        applyLeatherSelection(method, mode);
      });
    }
    // Edit-points action row (#969) — drag / smooth / add / delete,
    // shown only while the Edit method is active. The glyphs are inlined from
    // `src/icons/*.svg` by the marker in index.html (#216), so this only wires
    // behaviour; the construction Edit row draws the SAME files through the
    // `?raw` constants, because its buttons are built in JavaScript and have no
    // markup to carry a marker.
    for (const btn of leatherSubbar.querySelectorAll<HTMLButtonElement>(
      ".subtool-btn[data-leather-edit-action]",
    )) {
      const action = btn.dataset.leatherEditAction as OutlineEditAction;
      leatherEditActionButtons.set(action, btn);
      btn.disabled = false;
      btn.addEventListener("click", () =>
        (tools.outlineEdit as OutlineEditTool).setEditAction(action),
      );
    }
    (tools.outlineEdit as OutlineEditTool).onEditActionChanged = () =>
      syncLeatherEditActions();
  }
  /** Reflect the outline-edit tool's active action in the leather
   *  edit-action row highlights. */
  function syncLeatherEditActions() {
    const tool = tools.outlineEdit as OutlineEditTool;
    const active = tool.getEditAction();
    const editActive = activeTool?.id === "outlineEdit";
    for (const [action, btn] of leatherEditActionButtons) {
      btn.classList.toggle("active", editActive && action === active);
      // Seams carry curves since #982, so the smooth toggle applies to them
      // too — it was disabled here while stitch points were plain corners
      // (#970).
    }
  }
  /** Reflect the active tool in the leather method / mode sub-tools: keep
   *  `leatherMode` in step, reveal the mode sub-sub-tools only while a method
   *  is active, and toggle the `.active` highlights. */
  function syncLeatherSubtools(activeId: string) {
    const method = leatherMethodOf(activeId);
    // Keep the persisted mode aligned with whatever is active — a Draw tool
    // encodes the mode in its id; Trace / Fill mirror the persisted mode.
    if (activeId === "shape") leatherMode = "leather";
    else if (activeId === "cutout") leatherMode = "cutout";
    else if (activeId === "stitch") leatherMode = "stitch";
    else if (activeId === "trace")
      (tools.trace as TraceTool).setMode(leatherMode);
    else if (activeId === "region")
      (tools.region as RegionTool).setMode(leatherMode);
    for (const [m, btn] of leatherMethodButtons) {
      btn.classList.toggle("active", m === method);
    }
    // Mirror has no mode of its own — it reflects the selection, whatever kind
    // of object that is — so the Leather / Cutout / Stitch row stays hidden
    // while it's active rather than showing a choice that does nothing (#973).
    const show =
      method !== null &&
      method !== "mirror" &&
      method !== "round" &&
      method !== "slice" &&
      method !== "delete" &&
      // Edit points joined them in #1033: it edits whatever object you click,
      // so a Leather / Cut-out / Stitch choice would only be a way to make the
      // tool ignore two thirds of what's on the canvas.
      method !== "edit";
    const editActive = method === "edit";
    if (leatherModeDivider) leatherModeDivider.hidden = !show;
    // Cutout / Stitch stay disabled while the project has no parts
    // (the #741 availability rule this sync must not clobber).
    const hasParts = history.state.pieces.length > 0;
    for (const [mode, btn] of leatherModeButtons) {
      btn.hidden = !show;
      btn.disabled = (mode === "cutout" || mode === "stitch") && !hasParts;
      btn.classList.toggle("active", show && mode === leatherMode);
    }
    if (leatherEditActionDivider) leatherEditActionDivider.hidden = !editActive;
    for (const btn of leatherEditActionButtons.values()) {
      btn.hidden = !editActive;
    }
    syncLeatherEditActions();
    updateLeatherModeBadges(method);
  }
  /** Badge the Leather mode sub-sub-tools (Leather / Cutout / Stitch) with the
   *  active method's icon when it's Trace or Fill, so they read distinctly from
   *  their Draw versions (#741 follow-up). Draw / no method → no badge. */
  function updateLeatherModeBadges(method: LeatherMethod | null) {
    const src =
      method && method !== "draw"
        ? (leatherMethodButtons.get(method)?.querySelector("svg") ?? null)
        : null;
    for (const btn of leatherModeButtons.values()) {
      let badge = btn.querySelector<HTMLElement>(
        ":scope > .subtool-method-badge",
      );
      if (!src) {
        badge?.remove();
        continue;
      }
      if (!badge) {
        badge = document.createElement("span");
        badge.className = "subtool-method-badge";
        badge.setAttribute("aria-hidden", "true");
        btn.appendChild(badge);
      }
      badge.innerHTML = src.outerHTML;
    }
  }
  /** Show whichever of (leather-subtoolbar, stamping-subtoolbar) is
   *  relevant for the active tool's parent group, hide the others,
   *  and toggle the matching subtool's `.active` highlight. Called
   *  from `activateTool` on every tool change. */
  function syncGroupSubbars() {
    const activeId = activeTool?.id ?? "";
    const parent = parentToolbarIdFor(activeId);
    if (leatherSubbar) leatherSubbar.hidden = parent !== "leather";
    if (stampingSubbar) stampingSubbar.hidden = parent !== "stamping";
    for (const [id, btn] of groupSubbarButtons) {
      btn.classList.toggle("active", id === activeId);
    }
    syncLeatherSubtools(activeId);
  }

  // Construction sub-toolbar — secondary left column with sub-tools
  // for the Construction tool ("Select" + "Edit points"). Visible
  // only while the Construction tool is the active tool. The Draw
  // sub-mode is the implicit default when neither button is engaged,
  // so it has no button of its own; clicking the active sub-tool
  // again toggles back to Draw.
  const constructionSubbar = document.getElementById(
    "construction-subtoolbar",
  ) as HTMLElement | null;

  const subtoolButtons = new Map<ConstructionSubMode, HTMLButtonElement>();
  // Sub-sub-tool divider + buttons. Lives under Edit only and is
  // hidden in `syncConstructionSubbar` for the other sub-modes. The
  // two sub-sub-tool buttons (drag-point + smooth-toggle) act as a
  // mutually-exclusive radio pair backed by `editAction` — picking
  // one always implies un-picking the other.
  let editActionDivider: HTMLDivElement | null = null;
  let dragPointBtn: HTMLButtonElement | null = null;
  let smoothToggleBtn: HTMLButtonElement | null = null;
  let addPointBtn: HTMLButtonElement | null = null;
  let deletePointBtn: HTMLButtonElement | null = null;
  let sliceBtn: HTMLButtonElement | null = null;
  // Arrange action cluster (issue #115) — lives under the Arrange
  // sub-tool, shown only while the construction Select sub-mode is
  // active. Each button applies one align / centre / distribute op to
  // the current multi-selection. Order follows the common align-panel
  // layout: align L / centre-H / align R, then align T / centre-V /
  // align B, then distribute H / V. The three rows are split into
  // visual groups in the bar: horizontal alignment, vertical alignment,
  // and spacing.
  type ArrangeAction = { op: ArrangeOp; title: string; icon: string };
  const ARRANGE_GROUPS: Array<{ label: string; actions: ArrangeAction[] }> = [
    {
      label: "Horizontal alignment",
      actions: [
        {
          op: "align-left",
          title: "Align left edges",
          icon: ARRANGE_ALIGN_LEFT_ICON,
        },
        {
          op: "center-horizontal",
          title: "Center horizontally",
          icon: ARRANGE_CENTER_HORIZONTAL_ICON,
        },
        {
          op: "align-right",
          title: "Align right edges",
          icon: ARRANGE_ALIGN_RIGHT_ICON,
        },
      ],
    },
    {
      label: "Vertical alignment",
      actions: [
        {
          op: "align-top",
          title: "Align top edges",
          icon: ARRANGE_ALIGN_TOP_ICON,
        },
        {
          op: "center-vertical",
          title: "Center vertically",
          icon: ARRANGE_CENTER_VERTICAL_ICON,
        },
        {
          op: "align-bottom",
          title: "Align bottom edges",
          icon: ARRANGE_ALIGN_BOTTOM_ICON,
        },
      ],
    },
    {
      label: "Spacing",
      actions: [
        {
          op: "distribute-horizontal",
          title: "Distribute horizontally (even gaps)",
          icon: ARRANGE_DISTRIBUTE_HORIZONTAL_ICON,
        },
        {
          op: "distribute-vertical",
          title: "Distribute vertically (even gaps)",
          icon: ARRANGE_DISTRIBUTE_VERTICAL_ICON,
        },
      ],
    },
  ];
  // Arrange bar (issue #115) — a horizontal toolbar above the canvas,
  // populated here and shown only while the construction Select
  // sub-mode is active. Sits in `.canvas-host` (see index.html). The
  // three op groups each get their own `.arrange-group` box, separated
  // by a divider, so alignment vs. spacing read at a glance.
  const arrangeBar = document.getElementById("arrange-bar");
  const arrangeButtons: HTMLButtonElement[] = [];
  if (arrangeBar) {
    ARRANGE_GROUPS.forEach((group, i) => {
      if (i > 0) {
        const divider = document.createElement("div");
        divider.className = "arrange-divider";
        arrangeBar.appendChild(divider);
      }
      const groupEl = document.createElement("div");
      groupEl.className = "arrange-group";
      groupEl.setAttribute("role", "group");
      groupEl.setAttribute("aria-label", group.label);
      for (const action of group.actions) {
        const btn = document.createElement("button");
        btn.type = "button";
        btn.className = "arrange-btn";
        btn.dataset.arrangeOp = action.op;
        btn.title = action.title;
        btn.setAttribute("aria-label", action.title);
        btn.innerHTML = action.icon;
        btn.addEventListener("click", () => {
          // Select tool → arrange PARTS; Construction Select → arrange lines.
          if (activeTool?.id === "select") {
            arrangePieceSelection(ctx, action.op);
          } else {
            arrangeConstructionSelection(ctx, action.op);
          }
          // Selection is preserved by the action, so re-sync keeps the
          // buttons enabled for a follow-up op.
          syncConstructionSubbar();
        });
        groupEl.appendChild(btn);
        arrangeButtons.push(btn);
      }
      arrangeBar.appendChild(groupEl);
    });
  }
  // Auto-layout button (#739) — parts only, shown while the Layout tool is
  // active. A trailing group after the align/distribute ops.
  let autoLayoutGroup: HTMLElement | null = null;
  let autoLayoutBtn: HTMLButtonElement | null = null;
  if (arrangeBar) {
    const divider = document.createElement("div");
    divider.className = "arrange-divider";
    divider.id = "auto-layout-divider";
    arrangeBar.appendChild(divider);
    autoLayoutGroup = document.createElement("div");
    autoLayoutGroup.className = "arrange-group";
    autoLayoutGroup.setAttribute("role", "group");
    autoLayoutGroup.setAttribute("aria-label", "Auto-layout");
    autoLayoutBtn = document.createElement("button");
    autoLayoutBtn.type = "button";
    autoLayoutBtn.className = "arrange-btn";
    autoLayoutBtn.id = "btn-auto-layout";
    autoLayoutBtn.title = "Auto-layout — spread overlapping parts apart";
    autoLayoutBtn.setAttribute("aria-label", "Auto-layout");
    autoLayoutBtn.innerHTML = stripIconSvg(autoLayoutSvg);
    markPaidFeature(autoLayoutBtn, "layout");
    autoLayoutBtn.addEventListener("click", () => {
      autoLayoutPieces(ctx);
      syncConstructionSubbar();
    });
    autoLayoutGroup.appendChild(autoLayoutBtn);
    arrangeBar.appendChild(autoLayoutGroup);
  }
  // Shape operations (#764) — its own floating bar, the way flip / rotate is
  // separate from align / distribute. Combining shapes is a different job from
  // placing them, and sharing the arrange bar made one long undifferentiated
  // row.
  //
  // The same five buttons drive construction lines and leather (parts, stitch
  // loops): which one runs is decided by what is selected, not by a mode, so a
  // maker doesn't have to know there are two implementations.
  const booleanBar = document.getElementById("boolean-bar");
  const booleanButtons: HTMLButtonElement[] = [];
  if (booleanBar) {
    for (const spec of SHAPE_BOOLEAN_OPS) {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "arrange-btn";
      btn.dataset.booleanOp = spec.op;
      btn.title = `${spec.label} — ${spec.title}`;
      btn.setAttribute("aria-label", spec.label);
      btn.innerHTML = BOOLEAN_ICONS[spec.op];
      btn.addEventListener("click", () => {
        const ran =
          shapeBooleanSelection(ctx, spec.op) ||
          leatherBooleanSelection(ctx, spec.op);
        if (!ran) {
          showCanvasToast(
            `${spec.label} left nothing — the selected shapes don't overlap that way.`,
          );
        }
        syncConstructionSubbar();
      });
      booleanBar.appendChild(btn);
      booleanButtons.push(btn);
    }
  }

  // Transform bar (issue #346) — a separate floating bar below the
  // arrange bar with flip (H / V) and rotate-90 (left / right), split
  // into two groups by a divider. Shown in the construction Select
  // sub-mode; enabled whenever at least one construction object is
  // selected (a single object flips / rotates about its own centre).
  type TransformAction = {
    op: ConstructionTransformOp;
    title: string;
    icon: string;
  };
  const TRANSFORM_GROUPS: Array<{ label: string; actions: TransformAction[] }> =
    [
      {
        label: "Flip",
        actions: [
          {
            op: "flip-horizontal",
            title: "Flip horizontal",
            icon: FLIP_HORIZONTAL_ICON,
          },
          {
            op: "flip-vertical",
            title: "Flip vertical",
            icon: FLIP_VERTICAL_ICON,
          },
        ],
      },
      {
        label: "Rotate",
        actions: [
          {
            op: "rotate-left",
            title: "Rotate 90° left",
            icon: ROTATE_LEFT_ICON,
          },
          {
            op: "rotate-right",
            title: "Rotate 90° right",
            icon: ROTATE_RIGHT_ICON,
          },
        ],
      },
    ];
  const transformBar = document.getElementById("transform-bar");
  const transformButtons: HTMLButtonElement[] = [];
  if (transformBar) {
    TRANSFORM_GROUPS.forEach((group, i) => {
      if (i > 0) {
        const divider = document.createElement("div");
        divider.className = "transform-divider";
        transformBar.appendChild(divider);
      }
      const groupEl = document.createElement("div");
      groupEl.className = "transform-group";
      groupEl.setAttribute("role", "group");
      groupEl.setAttribute("aria-label", group.label);
      for (const action of group.actions) {
        const btn = document.createElement("button");
        btn.type = "button";
        btn.className = "arrange-btn";
        btn.dataset.transformOp = action.op;
        btn.title = action.title;
        btn.setAttribute("aria-label", action.title);
        btn.innerHTML = action.icon;
        btn.addEventListener("click", () => {
          // Leather Select tool → transform parts; construction Select → lines
          // (mirrors how the arrange bar branches on the active tool, #795).
          if (activeTool?.id === "select") {
            transformPieceSelection(ctx, action.op);
          } else {
            transformConstructionSelection(ctx, action.op);
          }
          syncConstructionSubbar();
        });
        groupEl.appendChild(btn);
        transformButtons.push(btn);
      }
      transformBar.appendChild(groupEl);
    });
  }
  // Construction sub-toolbar wiring extracted to `src/ui/setup/constructionSubbar.ts`
  // (issue #880 / epic #828). Runs at the SAME point in the bootstrap sequence;
  // the four created elements it assigns to outer `let`s are returned and
  // re-bound here so the sibling sync closures below keep reading them. The
  // live `activeTool` binding is threaded as a getter because the block reads
  // it inside a deferred (image-placed) callback.
  const constructionSubbarEls = setupConstructionSubbar({
    constructionSubbar,
    SUBTOOL_ORDER,
    subtoolButtons,
    setConstructionSubMode,
    setEditActionAndSync,
    ctx,
    tools,
    activateTool,
    getActiveTool: () => activeTool,
  });
  editActionDivider = constructionSubbarEls.editActionDivider;
  dragPointBtn = constructionSubbarEls.dragPointBtn;
  smoothToggleBtn = constructionSubbarEls.smoothToggleBtn;
  addPointBtn = constructionSubbarEls.addPointBtn;
  deletePointBtn = constructionSubbarEls.deletePointBtn;
  sliceBtn = constructionSubbarEls.sliceBtn;
  /** Count of independently-arrangeable objects in the current
   *  construction selection. Shares `arrangeableConstructionObjects`
   *  with the action so the enable/disable gate and the actual move
   *  agree on what counts as an object (top-level groups + loose
   *  lines). Arrange needs at least two. */
  function arrangeableObjectCount(): number {
    return arrangeableConstructionObjects(history.state, scene.getSelection())
      .length;
  }
  function setConstructionSubMode(id: ConstructionSubMode) {
    // No toggle-off — one of the three sub-tools is always active
    // while the Construction tool is selected. Click another to
    // switch modes; click the same one again is a no-op.
    // Block keyboard shortcuts (v/a/o/r/i) from picking sub-tools
    // that need an existing line when the project hasn't drawn
    // one yet — matches the disabled-button gate from
    // `syncToolAvailability` (issue #150 follow-up). Select stays
    // reachable on a tracing-images-only project (issue #509). Not while
    // the saved project loads (#1230): its lines aren't in yet.
    if (
      projectLoad === "done" &&
      constructionSubtoolDisabled(
        id,
        projectConstructionLines(history.state).length > 0,
        projectTracingImages(history.state).length > 0,
      )
    ) {
      return;
    }
    (tools.construction as ConstructionTool).setSubMode(id);
    // Mirror the sub-mode into the properties panel so its draw-only
    // controls (kind picker, dimension inputs, slot width) hide when
    // the user is in Select / Edit.
    ui.props.setConstructionSubMode(id);
    syncConstructionSubbar();
    // Re-sync the Scene's round-mode-all flag so the corner
    // handles' highlight state matches the toggle when entering /
    // leaving Round.
    updateRoundModeToggle();
    syncRaysToggle();
    // Sub-mode decides the knife case (#364).
    syncSnapApplicability();
    constructionSubModePref.set(id);
  }
  function setEditActionAndSync(action: ConstructionEditAction) {
    const construction = tools.construction as ConstructionTool;
    // Edit-action buttons are only meaningful inside Edit — guard so
    // a misfire (keyboard shortcut, automation) can't engage them
    // from another sub-mode. Radio-style: clicking an already-active
    // button is a no-op (use the other button to switch).
    if (construction.subMode !== "edit") return;
    if (construction.editAction === action) return;
    construction.setEditAction(action);
    // Mirror into the panel so the segment-list dot click can
    // dispatch correctly (delete vs cycle).
    ui.props.setConstructionEditAction(action);
    syncConstructionSubbar();
    constructionEditActionPref.set(action);
  }
  function syncConstructionSubbar() {
    const isConstruction = activeTool === tools.construction;
    // The subbar's grid column is reserved at a fixed width regardless
    // of active tool (issue #181) so the canvas controls don't shift
    // sideways when the user switches tools. Toggling `hidden` here
    // still suppresses the sub-tool buttons inside via the
    // `.subtoolbar[hidden] > *` rule — the panel skin stays
    // painted as an empty column.
    if (constructionSubbar) constructionSubbar.hidden = !isConstruction;
    const construction = tools.construction as ConstructionTool;
    const mode = construction.subMode;
    for (const [id, btn] of subtoolButtons) {
      btn.classList.toggle("active", isConstruction && mode === id);
    }
    // Sub-sub-toolbar (drag-point + smooth-toggle + their divider)
    // only renders while Edit is active. Hide via the `hidden`
    // attribute so the CSS rule strips them from layout entirely.
    // Drag-point and smooth-toggle act as a mutually-exclusive
    // radio pair — exactly one is .active when shown.
    const showSubSub = isConstruction && mode === "edit";
    if (editActionDivider) editActionDivider.hidden = !showSubSub;
    if (dragPointBtn) {
      dragPointBtn.hidden = !showSubSub;
      dragPointBtn.classList.toggle(
        "active",
        showSubSub && construction.editAction === "drag",
      );
    }
    if (smoothToggleBtn) {
      smoothToggleBtn.hidden = !showSubSub;
      smoothToggleBtn.classList.toggle(
        "active",
        showSubSub && construction.editAction === "smooth-toggle",
      );
    }
    if (addPointBtn) {
      addPointBtn.hidden = !showSubSub;
      addPointBtn.classList.toggle(
        "active",
        showSubSub && construction.editAction === "add-point",
      );
    }
    if (deletePointBtn) {
      deletePointBtn.hidden = !showSubSub;
      deletePointBtn.classList.toggle(
        "active",
        showSubSub && construction.editAction === "delete",
      );
    }
    if (sliceBtn) {
      sliceBtn.hidden = !showSubSub;
      sliceBtn.classList.toggle(
        "active",
        showSubSub && construction.editAction === "slice",
      );
    }
    // Arrange bar (issue #115) — the horizontal bar above the canvas
    // shows while the construction Select sub-mode is active (that's
    // where the user builds the multi-selection). The eight buttons
    // stay disabled until the selection holds at least two arrangeable
    // objects, since align / distribute is meaningless on one object.
    // Part arranging (#739): the same bar drives align / distribute for PARTS
    // when the Select tool holds a multi-part selection. Construction Select
    // mode keeps its own line-arrange path; the two are mutually exclusive
    // (Select tool ≠ Construction tool). Align / distribute are NOT paid — only
    // the Auto-layout button below is behind the paid `layout` flag (#784).
    const partsArrange = activeTool?.id === "select";
    const selectedPartCount = partsArrange
      ? movablePieceIds(ctx, scene.getSelection().pieceIds ?? []).length
      : 0;
    // #784: show the arrange bar whenever the leather Select tool is active —
    // the same way construction Select mode always shows it — instead of only
    // once 2+ parts are selected. The individual align / distribute buttons
    // disable themselves below when the selection can't be arranged (< 2), so
    // the bar is a persistent, discoverable control rather than one that only
    // appears mid-selection.
    const partsArrangeReady = partsArrange && selectedPartCount >= 2;
    const showArrange = (isConstruction && mode === "select") || partsArrange;
    const arrangeEnabled =
      showArrange &&
      (partsArrange ? selectedPartCount >= 2 : arrangeableObjectCount() >= 2);
    if (arrangeBar) arrangeBar.hidden = !showArrange;
    for (const btn of arrangeButtons) {
      btn.disabled = !arrangeEnabled;
    }
    // Shape operations (#764): its own bar, shown wherever a multi-selection is
    // built. Enabled per-op, since Divide has a lower operand ceiling than the
    // rest, and against construction OR leather — whichever the selection
    // resolves to.
    if (booleanBar) {
      const flagOn = isFeatureEnabled("shape-booleans");
      booleanBar.hidden = !flagOn || !showArrange;
      for (const btn of booleanButtons) {
        const op = btn.dataset.booleanOp as ShapeBooleanOp;
        btn.disabled =
          !flagOn ||
          !showArrange ||
          !(
            canShapeBooleanSelection(ctx, op) ||
            canLeatherBooleanSelection(ctx, op)
          );
      }
    }
    // Auto-layout: the advanced, paid part — spreads the selected parts apart.
    // Gated behind the `layout` flag (align / distribute above are free). When
    // the flag is on it's shown for the WHOLE time the parts arrange bar is up
    // (Select tool), and just DISABLED until there are 2+ parts to lay out —
    // showing/hiding it per selection would shift the bar's layout. When the
    // flag is off it's removed entirely. Toggle via `display` (not `[hidden]`) —
    // the `.arrange-group` display rule would otherwise override `[hidden]`.
    const layoutFlagOn = partsArrange && isFeatureEnabled("layout");
    const autoLayoutDisplay = layoutFlagOn ? "" : "none";
    if (autoLayoutGroup) autoLayoutGroup.style.display = autoLayoutDisplay;
    const autoLayoutDivider = document.getElementById("auto-layout-divider");
    if (autoLayoutDivider instanceof HTMLElement) {
      autoLayoutDivider.style.display = autoLayoutDisplay;
    }
    if (autoLayoutBtn) autoLayoutBtn.disabled = !partsArrangeReady;
    // Transform bar (issue #346) — same Select-mode visibility as the
    // arrange bar, but enabled with even a single object selected since
    // flip / rotate are meaningful on one.
    const sel = scene.getSelection();
    const hasConstructionSelection =
      (sel.constructionIds?.length ?? 0) > 0 ||
      (sel.constructionGroupIds?.length ?? 0) > 0 ||
      (sel.tracingImageIds?.length ?? 0) > 0;
    // Parts: enable once at least one *transformable* (unlinked) part is
    // selected — flip / rotate are meaningful on a single part (#795).
    const transformablePartCount = partsArrange
      ? transformablePieceIds(ctx, sel.pieceIds ?? []).length
      : 0;
    const transformEnabled =
      showArrange &&
      (partsArrange ? transformablePartCount >= 1 : hasConstructionSelection);
    if (transformBar) transformBar.hidden = !showArrange;
    for (const btn of transformButtons) {
      btn.disabled = !transformEnabled;
    }
    // Keep the panel's sub-mode mirror in sync — fires on tool
    // activation as well as on sub-tool clicks, so switching to the
    // Construction tool while a non-Draw sub-mode is remembered hides
    // the draw-only controls right away.
    ui.props.setConstructionSubMode(mode);
  }

  // Enable/disable the Assemble / Spread toggle (#722). The two views only
  // differ when a part carries a cut-layout offset (has been moved off its
  // construction position), so with nothing offset there's nothing to move —
  // EXCEPT that Assembled view LOCKS linked parts, so we must always allow
  // toggling back to Spread. To avoid that trap entirely, `syncToolAvailability`
  // snaps the view back to Spread whenever nothing is offset (see below), so we
  // never sit in Assembled with no offset. The toggle is then simply disabled
  // until the user moves a part off its construction position (#722): with no
  // offset the two views are identical and Spread already lets parts move, so
  // there's nothing for the toggle to do.
  function syncPartLayoutToggle() {
    const btn = document.getElementById(
      "btn-part-layout",
    ) as HTMLButtonElement | null;
    if (!btn) return;
    btn.disabled = !history.state.pieces.some(hasPieceLayoutOffset);
  }

  // Apply a part-layout view to the scene + toggle-button UI (#652). Hoisted so
  // the button's click handler, the initial-view resolve, and the auto-switch
  // in `syncToolAvailability` all drive the exact same update. Does NOT persist
  // — only a deliberate click writes the preference, so an auto-switch to
  // Spread doesn't clobber the user's "Assembled" choice for next time.
  function applyPartLayoutView(view: "spread" | "assembled") {
    scene.setPartLayoutView(view);
    const btn = document.getElementById(
      "btn-part-layout",
    ) as HTMLButtonElement | null;
    if (btn) {
      // Label shows what a click does next: spread now → "Assemble";
      // assembled now → "Spread". On phones an icon for that action stands in
      // for the word (#1223); `data-view` picks which one CSS shows.
      const label = btn.querySelector(".part-layout-label");
      if (label)
        label.textContent = view === "assembled" ? "Spread" : "Assemble";
      btn.dataset.view = view;
      btn.classList.toggle("active", view === "assembled");
    }
    syncPartLayoutToggle();
  }

  // The view the app should open in: the persisted preference, but forced to
  // Spread unless something is offset (nothing to spread otherwise) (#722).
  function initialPartLayoutView(): "spread" | "assembled" {
    // Nothing stored means the default view, spread.
    const pref = partLayoutPref.get() ?? "spread";
    return history.state.pieces.some(hasPieceLayoutOffset) ? pref : "spread";
  }

  // Sync the disabled state of part-dependent tool buttons with the current
  // project. Called at startup and on every history change (commit / undo /
  // redo / replace) so opening a saved file with parts enables the toolbar
  // and resetting / deleting the last part disables it again.
  /** Offset can only start from an outline that already exists, so with none in
   *  the project its tile is disabled (#972). A stored kind preference could
   *  still be "offset" — from a session that HAD parts — which would otherwise
   *  leave a disabled tile selected. Fall back to the tool's own default when
   *  that happens.
   *
   *  Deliberately does NOT rewrite the stored preference: the user did choose
   *  Offset, and this is a temporary condition (draw a part and it's usable
   *  again), so forgetting their pick would be the wrong trade. This runs on
   *  every history change rather than once at startup because the kind restore
   *  happens long before the autosave project is loaded — at that point the
   *  project is always empty, so an availability check there would fall back
   *  for everyone.
   *
   *  Stitch is untouched: its Draw mode already requires a part to exist, and a
   *  part is itself an offset source, so Offset is never unavailable there. */
  function reconcileOffsetKind() {
    // The saved project's outlines aren't in until the load ends (#1230).
    if (projectLoad !== "done") return;
    if (offsetSourceOutlines(history.state).length > 0) return;
    if ((tools.shape as ShapeTool).kind === "offset") {
      (tools.shape as ShapeTool).setKind("rect");
      ui.props.setShapeKind("rect");
    }
    if ((tools.cutout as CutoutTool).kind === "offset") {
      (tools.cutout as CutoutTool).setKind("rect");
      ui.props.setCutoutKind("rect");
    }
  }

  function syncToolAvailability() {
    reconcileOffsetKind();
    // Corner handles are drawn from the model, so they go stale the moment it
    // changes — a commit, an undo, a selection swap (#974).
    if (activeTool?.id === "leatherRound") {
      (tools.leatherRound as LeatherRoundTool).refreshHandles();
    }
    const hasParts = history.state.pieces.length > 0;
    const constructionLines = projectConstructionLines(history.state);
    const hasConstruction = constructionLines.length > 0;
    // Hidden construction lines can't be traced — you can't see or click them —
    // so Trace gates on a *visible* line, matching how Fill's fillable-region
    // check already skips hidden lines (their cache key carries `visible`).
    const hasVisibleConstruction = constructionLines.some((l) => isVisible(l));
    const hasTracingImages = projectTracingImages(history.state).length > 0;
    // Top-level toolbar buttons (issue #150). A parent group is
    // enabled if its default subtool can run without a part; stand-
    // alone tools (Construction, Hardware) follow their own
    // TOOLS_REQUIRING_PART entry. The static HTML starts every
    // button disabled (issue #181) so this pass is what lifts them
    // on first hydration and again on every history change.
    for (const entry of TOPBAR_TOOLS) {
      ui.toolbar.setDisabled(
        entry.id,
        topbarToolDisabled(entry, TOOLS_REQUIRING_PART, hasParts),
      );
    }
    // Sub-toolbar buttons (Leather + Stamping). Each subtool button
    // mirrors its underlying tool's part requirement — disable when
    // the project has no parts.
    for (const [id, btn] of groupSubbarButtons) {
      btn.disabled = partRequiringSubtoolDisabled(
        id,
        TOOLS_REQUIRING_PART,
        hasParts,
      );
    }
    // Leather mode sub-sub-tools (#741): Cutout / Stitch need a part to land
    // on, so disable them when there are none — and fall back to Leather if
    // that was the persisted mode (mirrors the old properties-panel picker).
    for (const [mode, btn] of leatherModeButtons) {
      btn.disabled = (mode === "cutout" || mode === "stitch") && !hasParts;
    }
    if (!hasParts && (leatherMode === "cutout" || leatherMode === "stitch")) {
      leatherMode = "leather";
      syncLeatherSubtools(activeTool?.id ?? "");
    }
    // Leather methods with nothing to act on (#752): Trace needs a construction
    // line, Fill needs an enclosed region. Disable each until its prerequisite
    // exists (Draw is always usable).
    const hasFillable = hasFillableRegion();
    const traceBtn = leatherMethodButtons.get("trace");
    if (traceBtn) traceBtn.disabled = !hasVisibleConstruction;
    const fillBtn = leatherMethodButtons.get("fill");
    if (fillBtn) fillBtn.disabled = !hasFillable;
    // Edit points needs an existing part to act on (#969 follow-up) —
    // same prerequisite gating as Trace / Fill above.
    const editBtn = leatherMethodButtons.get("edit");
    if (editBtn) editBtn.disabled = !hasParts;
    // Mirror reflects a leather object, so it needs a part to exist (#973). It
    // gates on the part, not on the SELECTION: with a part present but nothing
    // selected the tool is still usable — the user picks the source by clicking
    // it, and the gesture simply offers no axes until they do.
    const mirrorBtn = leatherMethodButtons.get("mirror");
    if (mirrorBtn) mirrorBtn.disabled = !hasParts;
    // Round needs an outline to have corners (#974).
    const roundBtn = leatherMethodButtons.get("round");
    if (roundBtn) roundBtn.disabled = !hasParts;
    // Slice needs a part to cut (#1001).
    const sliceBtn = leatherMethodButtons.get("slice");
    if (sliceBtn) sliceBtn.disabled = !hasParts;
    // Delete needs something to delete (#1000).
    const deleteBtn = leatherMethodButtons.get("delete");
    if (deleteBtn) deleteBtn.disabled = !hasParts;
    // If the active method just lost its prerequisite, fall back to Draw so the
    // user isn't stranded on an unusable tool. Not while the saved project
    // loads (#1230): judged against the still-empty project, every fallback
    // here would fire and replace the tool the user last had.
    const loaded = projectLoad === "done";
    if (
      loaded &&
      ((activeTool?.id === "trace" && !hasVisibleConstruction) ||
        (activeTool?.id === "region" && !hasFillable) ||
        (activeTool?.id === "outlineEdit" && !hasParts) ||
        (activeTool?.id === "leatherMirror" && !hasParts) ||
        (activeTool?.id === "leatherRound" && !hasParts) ||
        (activeTool?.id === "leatherSlice" && !hasParts) ||
        (activeTool?.id === "leatherDelete" && !hasParts))
    ) {
      applyLeatherSelection("draw", leatherMode);
    }
    // Construction sub-toolbar — Draw is always usable; the other
    // sub-tools (Select / Edit / Offset / Round) only do anything
    // when there's at least one construction line to act on, so
    // they stay disabled until the user draws one.
    for (const [id, btn] of subtoolButtons) {
      btn.disabled = constructionSubtoolDisabled(
        id,
        hasConstruction,
        hasTracingImages,
      );
    }
    // Drop the user out of a sub-mode that just became disabled
    // (e.g. they deleted the last construction line while Edit was
    // active). Falling back to Draw keeps the Construction tool
    // usable on an empty canvas. The Select sub-mode survives on a
    // images-only project (issue #509) since images stay movable.
    if (
      loaded &&
      activeTool === tools.construction &&
      (tools.construction as ConstructionTool).subMode !== "draw" &&
      constructionSubtoolDisabled(
        (tools.construction as ConstructionTool).subMode,
        hasConstruction,
        hasTracingImages,
      )
    ) {
      setConstructionSubMode("draw");
    }
    // Topbar buttons that operate on the project content — Reset wipes,
    // Save downloads, Export emits the pattern; all are no-ops on an
    // empty canvas, so disable them until there's something to act on.
    // Construction lines and tracing images (issue #511) also count as
    // content worth saving / resetting.
    const hasContent = hasParts || hasConstruction || hasTracingImages;
    for (const id of ["btn-reset", "btn-save", "btn-export", "btn-print"]) {
      const btn = document.getElementById(id) as HTMLButtonElement | null;
      if (btn) btn.disabled = !hasContent;
    }
    // Assemble / Spread toggle (#722). The Assembled view is only meaningful
    // while a part is offset, and it LOCKS linked parts — so whenever nothing
    // is offset, snap back to Spread (the view where parts can be moved) so the
    // user is never stranded. The toggle then stays disabled until they move a
    // part (creating an offset). Re-run on every history change (add / offset /
    // clear / remove all affect it).
    if (
      scene.getPartLayoutView() === "assembled" &&
      !history.state.pieces.some(hasPieceLayoutOffset)
    ) {
      applyPartLayoutView("spread");
    }
    syncPartLayoutToggle();
    if (
      loaded &&
      !hasParts &&
      activeTool &&
      TOOLS_REQUIRING_PART.has(activeTool.id as ToolId)
    ) {
      // Active tool just lost its prerequisite. Route to Construction
      // (the universal default — works on an empty project, no part
      // required) so the toolbar's `.active` chip never stays on a
      // disabled button.
      activateTool("construction");
    }
  }
  syncToolAvailability();

  // Restore the last-used tool (#806), falling back to the default
  // tool when no preference is stored or the saved id no longer
  // matches a registered tool. Reads inside a try in case a stale /
  // corrupt value is stored, so a bad pref can never throw during
  // bootstrap — it just falls back to the default.
  const savedToolId = ((): ToolId => {
    const v = lastToolPref.get();
    return v && Object.prototype.hasOwnProperty.call(tools, v)
      ? (v as ToolId)
      : DEFAULT_TOOL_ID;
  })();
  activateTool(savedToolId);
  // `activateTool` short-circuits when the saved tool needs a part
  // the empty project doesn't have — fall through to the default
  // tool (always available, no part required) so the user lands on
  // something usable.
  if (!activeTool) activateTool(DEFAULT_TOOL_ID);

  // Restore Construction-tool sub-mode + edit-action from prefs.
  // Reads even when the active tool isn't Construction so the
  // tool's internal state persists across reloads — switching back
  // to Construction lands on the saved (sub-mode, sub-sub-action).
  // setSubMode / setEditAction validate their inputs and no-op
  // when unchanged, so unknown values from older preference shapes
  // are harmless.
  const savedSubMode = constructionSubModePref.get();
  if (savedSubMode) {
    setConstructionSubMode(savedSubMode);
  }
  // "delete" is no longer an Edit sub-sub-tool (it's a top-level sub-mode now,
  // #670 follow-up); the preference's value list omits it, so a stale pref
  // decodes to null.
  const savedEditAction = constructionEditActionPref.get();
  if (savedEditAction && savedSubMode === "edit") {
    setEditActionAndSync(savedEditAction);
  }
  // Restore the last picked Draw kind so the kind picker lands on
  // what the user was drawing instead of resetting to "rect" every
  // reload. Same pattern as `savedSubMode`: validate the string
  // against the union so an older / corrupt pref doesn't crash.
  const savedKind = constructionKindPref.get();
  if (savedKind) {
    (tools.construction as ConstructionTool).setKind(savedKind);
    // Mirror the saved kind into the panel so the kind picker
    // highlights it on first paint, instead of reading the tool's
    // default "rect" until the user clicks something.
    ui.props.setConstructionKind(savedKind);
  }

  // Same restore for the three leather Draw kinds (#972). Each is validated
  // against its own union so a stale or hand-edited pref can't put a tool into
  // a kind it doesn't have — and each falls back to that tool's own default,
  // which is why an absent pref leaves Stitch on Offset and the other two on
  // Rectangle rather than forcing a single shared default.
  const savedLeatherShapeKind = leatherShapeKindPref.get();
  if (savedLeatherShapeKind) {
    (tools.shape as ShapeTool).setKind(savedLeatherShapeKind);
    ui.props.setShapeKind(savedLeatherShapeKind);
  }
  const savedCutoutKind = cutoutKindPref.get();
  if (savedCutoutKind) {
    (tools.cutout as CutoutTool).setKind(savedCutoutKind);
    ui.props.setCutoutKind(savedCutoutKind);
  }
  const savedStitchKind = stitchKindPref.get();
  if (savedStitchKind) {
    (tools.stitch as StitchTool).setKind(savedStitchKind);
    ui.props.setStitchKind(savedStitchKind);
  }
  // The hydration `syncToolAvailability()` above ran BEFORE these restores, so
  // it couldn't have seen a restored "offset" kind. Re-check now that they're
  // applied, otherwise a stored Offset preference would sit selected on an
  // empty project until the first history change (#972).
  reconcileOffsetKind();
  // Restore the saved freehand smoothness default (issue #416), guarding
  // against a corrupt / out-of-range stored value, using the shared ceiling
  // (#1016).
  const savedSmoothness = constructionFreehandSmoothnessPref.get();
  if (savedSmoothness !== null) {
    (tools.construction as ConstructionTool).setPresetFreehandSmoothness(
      savedSmoothness,
    );
    ui.props.setConstructionPresetFreehandSmoothness(savedSmoothness);
  }
  // Same for the leather freehand preset (#1016), on its own mm-scaled range.
  const savedLeatherSmoothness = leatherFreehandSmoothnessPref.get();
  if (savedLeatherSmoothness !== null) {
    (tools.shape as ShapeTool).setFreehandSmoothness(savedLeatherSmoothness);
    (tools.cutout as CutoutTool).setFreehandSmoothness(savedLeatherSmoothness);
    (tools.stitch as StitchTool).setFreehandSmoothness(savedLeatherSmoothness);
    ui.props.setLeatherPresetFreehandSmoothness(savedLeatherSmoothness);
  }

  // Render-on-change
  const autosave = new Autosave(library);
  // Test hook: resolves once every change so far is stored, so a spec can
  // reload knowing the save has landed rather than racing it (#1143).
  (
    (window as unknown as Record<string, unknown>).__editor as Record<
      string,
      unknown
    >
  ).flushAutosave = () => autosave.flush();
  // Persist the mat placement the scene just resolved alongside the project
  // (#785 follow-up) so a reload restores the same mat — parts keep their spot
  // on it instead of the mat re-centring on their bounding box. Must run AFTER
  // the scene.render(p) that computes the layout.
  const scheduleAutosave = (p: Project) => {
    const matLayout = scene.getMatLayout();
    autosave.schedule(matLayout ? { ...p, matLayout } : p);
  };
  let lastPieceCount = history.state.pieces.length;
  // Deferred server-image GC (#952): tracks R2 keys that dropped out of the
  // project so real page teardown can delete them — undo-safe because a key
  // restored by undo is un-pended before any delete ever fires.
  const serverImageGc = new ServerImageGc(history.state);
  history.subscribe((p) => {
    serverImageGc.onStateChange(p);
    scene.render(p);
    ui.layers.refresh();
    scheduleAutosave(p);
    syncToolAvailability();
    if (projectNameSpan) projectNameSpan.textContent = p.name;
    // Last part deleted — recenter on the default mat at its full size so
    // the canvas opens on the empty starter mat instead of staying zoomed
    // / panned wherever the user was working.
    if (lastPieceCount > 0 && p.pieces.length === 0) {
      viewport.fitToContent();
    }
    lastPieceCount = p.pieces.length;
  });
  scene.render(history.state);

  // Autosave is debounced (200ms), so a reload / tab-close within that window
  // could drop the most recent commits — and, right after opening a file, its
  // name (#700). Flush the pending snapshot when the page is hidden or torn
  // down. `visibilitychange → hidden` is the reliable signal (fires on mobile /
  // tab close where `beforeunload` doesn't); `pagehide` covers a desktop reload
  // / navigation. The write is IndexedDB-backed (#805) so this flush is now
  // best-effort — but anything older than the 200ms debounce is already saved,
  // so at most the last fraction of a second is at risk.
  const flushAutosave = () => {
    void autosave.flush();
  };
  window.addEventListener("pagehide", flushAutosave);
  // `beforeunload` is the extra belt for a desktop reload / navigation where
  // `pagehide` is occasionally skipped — writing the pending snapshot here means
  // a Cmd-R right after a draw can't drop it (#735).
  window.addEventListener("beforeunload", flushAutosave);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flushAutosave();
  });
  // Server-image GC flush (#952) — real teardown ONLY (pagehide /
  // beforeunload), deliberately NOT on visibilitychange: a backgrounded tab
  // isn't gone, and the user may come back and undo a deletion. In-memory
  // history dies with the page and autosave persists only the current
  // state, so at teardown "still needed" is exactly "referenced by the
  // current state" — which the GC's pending set already excludes.
  const flushServerImageGc = () => serverImageGc.flush();
  window.addEventListener("pagehide", flushServerImageGc);
  window.addEventListener("beforeunload", flushServerImageGc);

  // Topbar buttons
  const projectNameSpan = document.getElementById("project-name");
  if (projectNameSpan) {
    projectNameSpan.textContent = history.state.name;
    projectNameSpan.addEventListener("click", () => {
      const next =
        prompt("Project name", history.state.name) || history.state.name;
      ctx.commit("Rename", (p) => ({ ...p, name: next }));
    });
  }
  // Undo / redo icons live in `index.html` (issue #181) — the static
  // HTML already has the correct SVG markup so we never overwrite
  // the button contents here. JS only wires the click handlers and
  // keeps the static `disabled` in lockstep with the history stacks
  // (issue #240) so the buttons never invite a click that would
  // be a no-op.
  const undoBtn = document.getElementById(
    "btn-undo",
  ) as HTMLButtonElement | null;
  const redoBtn = document.getElementById(
    "btn-redo",
  ) as HTMLButtonElement | null;
  const syncUndoRedoButtons = () => {
    if (undoBtn) undoBtn.disabled = !history.canUndo();
    if (redoBtn) redoBtn.disabled = !history.canRedo();
  };
  if (undoBtn) {
    undoBtn.addEventListener("click", () => history.undo());
  }
  if (redoBtn) {
    redoBtn.addEventListener("click", () => history.redo());
  }
  // History notifies on every commit / undo / redo / replace — same
  // subscription already drives Scene re-render, so the buttons
  // stay in sync without a separate event.
  history.subscribe(() => syncUndoRedoButtons());
  syncUndoRedoButtons();
  document
    .getElementById("btn-export")
    ?.addEventListener("click", () => ui.exportDialog.open());
  // Print (issue #289): build the same print-ready PDF the export produces
  // and hand it to the browser's print dialog. Shared by the toolbar button
  // and the Ctrl/Cmd+P shortcut below. Disabled (like Export) on an empty
  // canvas, so a stray press there is a no-op. With the `advanced-printing`
  // flag on, Print first opens the settings dialog (orientation, paper, marks)
  // — read fresh each time so a flag flip takes effect without a reload.
  const printBtn = document.getElementById(
    "btn-print",
  ) as HTMLButtonElement | null;
  const triggerPrint = async () => {
    if (printBtn?.disabled) return;
    if (isFeatureEnabled("advanced-printing")) {
      ui.advancedPrintDialog.open();
      return;
    }
    try {
      await printPdf(history.state, ctx.library);
    } catch (e) {
      alert("Print failed: " + (e as Error).message);
    }
  };
  printBtn?.addEventListener("click", () => void triggerPrint());
  document.getElementById("btn-reset")?.addEventListener("click", () => {
    if (!confirm("Discard the current project and start a new one?")) return;
    const fresh = deriveProject(createEmptyProject(), scene.getPaperScope());
    history.replace(fresh);
    // Fresh project — its own `modified` becomes the new "saved"
    // baseline, so the editor opens clean and Open won't warn.
    lastSavedModified = fresh.modified;
    scene.setSelection({ pieceId: null, decorationId: null });
    scene.render(fresh);
    ui.layers.refresh();
    ui.stamps.refresh();
    // Drop any locked shape / cutout dimension presets so the fresh project
    // doesn't carry over input field values from the prior one.
    ui.props.clearAllPresets();
    // Reset the tool selection to the default (#806): a brand-new project
    // starts at the top of the tool hierarchy rather than wherever the
    // user happened to leave off. `activateTool` also rewrites the
    // persisted PREF_TOOL_KEY, so a subsequent reload lands on the
    // default too instead of restoring the pre-reset tool.
    activateTool(DEFAULT_TOOL_ID);
    scheduleAutosave(fresh);
    // Match the last-part-deleted behavior: re-fit the viewport so the
    // canvas opens centered on the freshly-picked default mat (the
    // largest standard size that fits at real-size zoom).
    viewport.fitToContent();
  });
  const saveProject = async () => {
    // Snapshot the modified stamp BEFORE the await so a change made
    // mid-write doesn't get marked clean.
    const stamp = history.state.modified;
    // Carry the resolved mat placement into the saved file too (#785 follow-up)
    // so reopening it restores the same mat, matching the autosave/reload path.
    const matLayout = scene.getMatLayout();
    const toSave = matLayout ? { ...history.state, matLayout } : history.state;
    await downloadProjectFile(toSave, library);
    lastSavedModified = stamp;
  };
  const saveBtn = document.getElementById(
    "btn-save",
  ) as HTMLButtonElement | null;
  saveBtn?.addEventListener("click", saveProject);
  const openBtn = document.getElementById(
    "btn-open",
  ) as HTMLButtonElement | null;
  if (openBtn) openBtn.disabled = false;
  /** Install an opened project file's result as the current project —
   *  shared by the Open button and the screen-wide drop target (#49). */
  const loadOpenedProject = (result: {
    project: Project;
    userStampMetas: StampMeta[];
  }) => {
    const reg = ensureUserStampRegistered(
      library,
      result.userStampMetas,
      result.project,
    );
    // Refresh the file's derived data (#1141): a file saved before a derive
    // fix would otherwise show the stale version until its first edit. Keeps
    // `modified`, so the file still opens clean, and adds no undo entry.
    const project = deriveProject(
      {
        ...result.project,
        userStamps: [...result.project.userStamps, ...reg.addedStamps],
      },
      scene.getPaperScope(),
    );
    history.replace(project);
    // The just-loaded file's `modified` is the on-disk version —
    // any subsequent commit will bump `state.modified` past it
    // and re-arm the "unsaved changes" warning.
    lastSavedModified = project.modified;
    scene.render(project);
    ui.layers.refresh();
    ui.stamps.refresh();
    scheduleAutosave(project);
    viewport.fitToContent();
  };
  const openProject = async () => {
    if (
      hasUnsavedChanges() &&
      !confirm(
        "You have unsaved changes that will be lost. Open another project anyway?",
      )
    ) {
      return;
    }
    try {
      loadOpenedProject(await pickProjectFile());
    } catch (e) {
      alert("Open failed: " + (e as Error).message);
    }
  };
  openBtn?.addEventListener("click", openProject);

  // The whole screen is a `.leather` drop target (#49): dropping a
  // project file anywhere opens it, same flow as the Open button
  // (incl. the unsaved-changes confirm). Other file types are ignored
  // for now. Drops on an open <dialog> (e.g. the image-upload dialog's
  // own drop zone) pass through untouched.
  const dropOnDialog = (ev: DragEvent) =>
    !!(ev.target as HTMLElement | null)?.closest?.("dialog");
  window.addEventListener("dragover", (ev) => {
    if (dropOnDialog(ev)) return;
    if (!ev.dataTransfer?.types.includes("Files")) return;
    // preventDefault is what makes the window a legal drop target.
    ev.preventDefault();
    ev.dataTransfer.dropEffect = "copy";
  });
  window.addEventListener("drop", (ev) => {
    if (dropOnDialog(ev)) return;
    const file = ev.dataTransfer?.files?.[0];
    if (!file) return;
    // Always claim file drops outside dialogs — otherwise the browser
    // navigates away to the dropped file even for ignored types.
    ev.preventDefault();
    if (!isProjectFileName(file.name)) return;
    if (
      hasUnsavedChanges() &&
      !confirm(
        "You have unsaved changes that will be lost. Open another project anyway?",
      )
    ) {
      return;
    }
    void readProjectFile(file)
      .then(loadOpenedProject)
      .catch((e) => alert("Open failed: " + (e as Error).message));
  });

  // Units toggle (mm / in). Drives the cutting-mat grid step + the order
  // of paired length inputs in the properties panel. Persisted across
  // reloads by the preferences module.
  const unitsBtn = document.getElementById(
    "btn-units",
  ) as HTMLButtonElement | null;
  const syncUnitsBtn = () => {
    if (!unitsBtn) return;
    unitsBtn.textContent = getUnits();
  };
  syncUnitsBtn();
  scene.setMatUnits(getUnits());
  if (unitsBtn) unitsBtn.disabled = false;
  unitsBtn?.addEventListener("click", () => {
    setUnits(getUnits() === "mm" ? "in" : "mm");
  });
  onUnitsChange((u) => {
    syncUnitsBtn();
    scene.setMatUnits(u);
    // Re-render the properties panel so its mm/in input pairs reorder to
    // put the new preferred unit first.
    ui.props.refresh();
  });

  // Settings dialog (gear button next to the units toggle). Currently
  // exposes a single "show secondary units" toggle; flipping it re-renders
  // the properties panel so paired mm/in inputs collapse to single-unit
  // or expand to pairs without needing a manual refresh.
  const settingsDialog = new SettingsDialog(
    document.getElementById("modal-host")!,
    {
      // The chrome restyles itself the moment `data-theme` lands, but the
      // canvas is drawn into Paper.js items that hold resolved colors, so it
      // needs a repaint to pick the new palette up (#190).
      onThemeProfileChange: (id) => {
        themeManager?.setProfile(id);
        scene.render(history.state);
      },
    },
  );
  const settingsBtn = document.getElementById(
    "btn-settings",
  ) as HTMLButtonElement | null;
  if (settingsBtn) {
    settingsBtn.disabled = false;
    settingsBtn.addEventListener("click", () => settingsDialog.open());
  }
  onShowSecondaryUnitsChange(() => {
    ui.props.refresh();
  });

  // Hamburger menu (issue #470). The topbar's actions collapse into a
  // dropdown on every view (#1223; portrait only before), which keeps the
  // bar's width free. Undo / redo, Assemble and the units toggle stay inline,
  // since they're used while drawing. CSS handles the show/hide; this just
  // toggles the `.open` class and keeps the menu from lingering after a
  // choice. Inside the menu the Settings gear shows its text label instead.
  const menuToggle = document.getElementById(
    "topbar-menu-toggle",
  ) as HTMLButtonElement | null;
  const topbarActions = document.getElementById("topbar-actions");
  if (menuToggle && topbarActions) {
    const closeMenu = () => {
      topbarActions.classList.remove("open");
      menuToggle.setAttribute("aria-expanded", "false");
    };
    const openMenu = () => {
      topbarActions.classList.add("open");
      menuToggle.setAttribute("aria-expanded", "true");
    };
    menuToggle.addEventListener("click", (e) => {
      e.stopPropagation();
      if (topbarActions.classList.contains("open")) closeMenu();
      else openMenu();
    });
    // Picking an action collapses the menu so it doesn't cover the canvas.
    topbarActions.addEventListener("click", (e) => {
      if ((e.target as HTMLElement).closest(".btn")) closeMenu();
    });
    // Tapping anywhere outside the open menu dismisses it.
    document.addEventListener("click", (e) => {
      if (!topbarActions.classList.contains("open")) return;
      const target = e.target as Node;
      if (!topbarActions.contains(target) && !menuToggle.contains(target)) {
        closeMenu();
      }
    });
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape") closeMenu();
    });
  }

  // Cutting-mat background toggle. State persists in localStorage so the
  // mat stays on / off across reloads. The mat lives in the Scene and is
  // re-rendered (auto-sized to envelop the parts' bounding box) on every
  // history commit / replace; just toggling visibility re-renders too.
  const matBtn = document.getElementById("btn-mat") as HTMLButtonElement | null;
  if (matBtn) {
    // Default ON for first-time users; only an explicit "off" disables.
    const initialMat = matVisiblePref.get();
    scene.setMatVisible(initialMat);
    matBtn.classList.toggle("active", initialMat);
    matBtn.disabled = false;
    matBtn.addEventListener("click", () => {
      const next = !scene.isMatVisible();
      scene.setMatVisible(next);
      matBtn.classList.toggle("active", next);
      matVisiblePref.set(next);
    });
  }

  // Assembled / Spread toggle (#652). Default Spread (parts laid out for
  // cutting); the button label is the ACTION it performs — "Assemble" while
  // spread, "Spread" while assembled — and goes `.active` in the non-default
  // Assembled view. Persists like the mat toggle.
  const layoutBtn = document.getElementById(
    "btn-part-layout",
  ) as HTMLButtonElement | null;
  if (layoutBtn) {
    applyPartLayoutView(initialPartLayoutView());
    layoutBtn.addEventListener("click", () => {
      const next =
        scene.getPartLayoutView() === "spread" ? "assembled" : "spread";
      applyPartLayoutView(next);
      // Only a deliberate toggle persists the preference.
      partLayoutPref.set(next);
    });
  }

  // Zoom indicator: click → preset picker, double-click → reset to 1:1.
  // The "user ratio" the UI displays is the ratio of project mm to
  // physical screen mm — so 1:1 means the design is rendered at real
  // size. CSS defines 96 px / inch, so 96/25.4 px/mm is the paper.js zoom
  // factor that makes 1 project-mm cover 1 physical-mm on screen.
  const zoomEl = document.getElementById("zoom-indicator");
  if (zoomEl) {
    // Read getPxPerMm() on each call so a calibration update takes effect
    // without re-running this setup block — the indicator label refresh
    // (driven by viewport.onZoomChange below) re-formats with the new
    // ratio, and clicking a preset uses the new factor.
    const userToPaper = (ratio: number) => ratio * getPxPerMm();
    const paperToUser = (zoom: number) => zoom / getPxPerMm();
    const ZOOM_LEVELS = [
      12,
      8,
      4,
      3,
      2,
      1,
      1 / 2,
      1 / 3,
      1 / 4,
      1 / 8,
      1 / 12,
      1 / 16,
      1 / 24,
    ];
    const formatRatio = (ratio: number) =>
      ratio >= 1 ? `${Math.round(ratio)}:1` : `1:${Math.round(1 / ratio)}`;
    const isClean = (ratio: number) => {
      if (ratio >= 1) return Math.abs(ratio - Math.round(ratio)) / ratio < 0.01;
      return Math.abs(1 / ratio - Math.round(1 / ratio)) * ratio < 0.01;
    };
    const formatZoom = (paperZoom: number) => {
      const ratio = paperToUser(paperZoom);
      return isClean(ratio)
        ? formatRatio(ratio)
        : `${Math.round(ratio * 100)}%`;
    };

    const popup = document.createElement("div");
    popup.className = "zoom-popup";
    const optionEls: HTMLButtonElement[] = ZOOM_LEVELS.map((z) => {
      const opt = document.createElement("button");
      opt.className = "zoom-option";
      if (z === 1) opt.classList.add("prominent");
      opt.textContent = formatRatio(z);
      opt.addEventListener("click", (ev) => {
        ev.stopPropagation();
        viewport.zoom = userToPaper(z);
        closePopup();
      });
      popup.appendChild(opt);
      return opt;
    });
    // Fit-to-content shortcut at the bottom of the menu — convenience for
    // users who've zoomed in and want to reframe the whole design.
    const fitOpt = document.createElement("button");
    fitOpt.className = "zoom-option zoom-fit";
    fitOpt.textContent = "Fit";
    fitOpt.addEventListener("click", (ev) => {
      ev.stopPropagation();
      viewport.fitToContent();
      closePopup();
    });
    popup.appendChild(fitOpt);
    // Anchor the popup to canvas-host (not zoomEl.parentElement, which is
    // the .canvas-controls flex wrapper) so its bottom/left offsets stay
    // measured from the canvas — the wrapper is also absolute-positioned
    // and would shift the popup by the wrapper's own offset.
    (zoomEl.closest(".canvas-host") ?? zoomEl.parentElement)?.appendChild(
      popup,
    );

    const refreshOptions = (currentPaperZoom: number) => {
      const currentRatio = paperToUser(currentPaperZoom);
      ZOOM_LEVELS.forEach((z, i) => {
        const close =
          Math.abs(z - currentRatio) / Math.max(z, currentRatio) < 0.01;
        optionEls[i]!.classList.toggle("current", close);
      });
    };
    const openPopup = () => {
      refreshOptions(viewport.zoom);
      popup.classList.add("open");
    };
    const closePopup = () => popup.classList.remove("open");

    zoomEl.addEventListener("click", (e) => {
      e.stopPropagation();
      if (popup.classList.contains("open")) closePopup();
      else openPopup();
    });
    // Double-click cycles between 1:1 and fit-to-content: if the view
    // is already at 1:1, switch to Fit; otherwise jump to 1:1. Lets a
    // user toggle between "real size" and "see everything" without
    // opening the picker.
    zoomEl.addEventListener("dblclick", (e) => {
      e.stopPropagation();
      const oneToOne = userToPaper(1);
      const atOneToOne = Math.abs(viewport.zoom - oneToOne) / oneToOne < 0.01;
      if (atOneToOne) viewport.fitToContent();
      else viewport.zoom = oneToOne;
      closePopup();
    });
    document.addEventListener("click", (e) => {
      if (!popup.contains(e.target as Node) && e.target !== zoomEl) {
        closePopup();
      }
    });

    viewport.onZoomChange((z) => {
      zoomEl.textContent = formatZoom(z);
      if (popup.classList.contains("open")) refreshOptions(z);
    });
    zoomEl.textContent = formatZoom(viewport.zoom);
  }

  // Calibration: opens a modal with a reference line so the user can match
  // a physical ruler. The new multiplier flows through ../calibration to
  // every getPxPerMm() caller (mat-size picker, zoom indicator, initial
  // zoom). Re-scale the current viewport zoom by the same ratio so a "1:1"
  // view stays "1:1" after the calibration shifts what 1 mm means in CSS px.
  const calibrateBtn = document.getElementById("btn-calibrate");
  if (calibrateBtn) {
    const modalHost = document.getElementById("modal-host")!;
    const calibDialog = new CalibrationDialog(modalHost);
    calibrateBtn.addEventListener("click", () => calibDialog.open());
    let prevPxPerMm = getPxPerMm();
    onCalibrationChange(() => {
      const next = getPxPerMm();
      const ratio = next / prevPxPerMm;
      prevPxPerMm = next;
      // Scale the live zoom so visible content stays the same physical
      // size on screen (the user's perceived zoom level doesn't jump).
      viewport.zoom = viewport.zoom * ratio;
      // Re-render the scene — the cutting-mat layout's "fit largest mat at
      // 1:1" decision uses getPxPerMm() and may now pick a different size.
      scene.render(history.state);
    });
  }

  // Snap toggle group (issue #98). Five buttons: master + 4 children
  // (angle / point / point-constraint / sizes). When the master is
  // off, the 4 children render disabled but their stored flag values
  // are preserved so flipping master back on restores the prior set.
  const masterBtn = document.getElementById(
    "snap-toggle",
  ) as HTMLButtonElement | null;
  const angleBtn = document.getElementById(
    "snap-toggle-angle",
  ) as HTMLButtonElement | null;
  const pointBtn = document.getElementById(
    "snap-toggle-point",
  ) as HTMLButtonElement | null;
  const pointConstraintBtn = document.getElementById(
    "snap-toggle-point-constraint",
  ) as HTMLButtonElement | null;
  const sizesBtn = document.getElementById(
    "snap-toggle-sizes",
  ) as HTMLButtonElement | null;
  const centerBtn = document.getElementById(
    "snap-toggle-center",
  ) as HTMLButtonElement | null;
  const gridBtn = document.getElementById(
    "snap-toggle-grid",
  ) as HTMLButtonElement | null;
  const lineBtn = document.getElementById(
    "snap-toggle-line",
  ) as HTMLButtonElement | null;
  type SnapChildKey =
    | "snapAngle"
    | "snapPoint"
    | "snapPointConstraint"
    | "snapSizes"
    | "snapCenter"
    | "snapGrid"
    | "snapLine";
  const snapChildren: Array<[HTMLButtonElement | null, SnapChildKey]> = [
    [angleBtn, "snapAngle"],
    [sizesBtn, "snapSizes"],
    [centerBtn, "snapCenter"],
    [gridBtn, "snapGrid"],
    [pointBtn, "snapPoint"],
    [lineBtn, "snapLine"],
    [pointConstraintBtn, "snapPointConstraint"],
  ];
  const snapGroupEl = document.querySelector(".snap-group");
  /** Each chip's own tooltip, captured before #364 starts appending a reason to
   *  it — otherwise re-syncing would stack "— reason" on itself every pass. */
  const snapBaseTitles = new Map<HTMLButtonElement, string>();
  for (const [btn] of snapChildren) {
    // Kept without the Preview note (#1120), which the rebuild re-adds last.
    if (btn) snapBaseTitles.set(btn, withoutPreviewNote(btn.title));
  }
  /** The active tool + its sub-state, for `isSnapApplicable` (#364). The draw
   *  kind is read off the tool itself rather than mirrored here, so it can't
   *  drift from what the tool is actually set to. */
  const snapToolContext = (): SnapToolContext => {
    const construction = tools.construction as ConstructionTool;
    let drawKind: string | null = null;
    if (activeTool === tools.shape) drawKind = (tools.shape as ShapeTool).kind;
    else if (activeTool === tools.cutout)
      drawKind = (tools.cutout as CutoutTool).kind;
    else if (activeTool === tools.stitch)
      drawKind = (tools.stitch as StitchTool).kind;
    else if (activeTool === tools.construction) drawKind = construction.kind;
    return {
      toolId: activeTool?.id ?? null,
      subMode: activeTool === tools.construction ? construction.subMode : null,
      editAction:
        activeTool === tools.construction ? construction.editAction : null,
      drawKind,
    };
  };
  // Reusable predicate — Shift overrides size-snap ON inside the
  // tools that actually use it on a rect / ellipse drag (Shape,
  // Cutout, Construction Draw). The visual highlight in
  // `syncSnapButtons` mirrors that override so the user sees what
  // pressing Shift is doing without having to release the drag to
  // check.
  const isInSizeSnapDrawMode = () =>
    activeTool === tools.shape ||
    activeTool === tools.cutout ||
    (activeTool === tools.construction &&
      (tools.construction as ConstructionTool).subMode === "draw");
  /** The knife (construction Slice sub-sub-tool, or the leather Slice method).
   *  It takes modifier updates so the angle key switches a live cut between
   *  straight and freehand mid-drag (#1013). Kept SEPARATE from
   *  `isInSizeSnapDrawMode` on purpose: that gate also drives the snap chips'
   *  Shift-forces-size / Alt-forces-centre visuals, which mean nothing for a
   *  cut, and widening it would quietly switch on the construction Delete
   *  highlight's modifier branch too. */
  const isInKnifeMode = () =>
    activeTool === tools.leatherSlice ||
    (activeTool === tools.construction &&
      (tools.construction as ConstructionTool).subMode === "edit" &&
      (tools.construction as ConstructionTool).editAction === "slice");
  const syncSnapButtons = () => {
    if (masterBtn) {
      masterBtn.classList.toggle("active", settings.snapMaster);
      masterBtn.setAttribute(
        "aria-pressed",
        settings.snapMaster ? "true" : "false",
      );
    }
    // Master off → collapse the group to just the master button so
    // the 4 children disappear (rather than rendering disabled). CSS
    // rounds the master's right edge in collapsed mode so the chip
    // reads as a single pill.
    if (snapGroupEl) {
      snapGroupEl.classList.toggle(
        "snap-group--collapsed",
        !settings.snapMaster,
      );
    }
    for (const [btn, key] of snapChildren) {
      if (!btn) continue;
      let childOn = settings[key];
      // Shift forces size-snap on inside drawing modes (Shape /
      // Cutout / Construction Draw). Mirror that override on the
      // size-snap toggle's "active" class so the user gets the
      // same kind of "modifier flips the chip" feedback the
      // round-mode toggle already does — but ONLY in those tools,
      // because Shift means different things elsewhere (angle
      // snap when drawing a Line, etc.). The override doesn't
      // touch `settings.snapSizes` itself; releasing Shift snaps
      // the visual back to whatever the toggle is set to.
      if (key === "snapSizes" && roundModifierHeld && isInSizeSnapDrawMode()) {
        childOn = true;
      }
      // Alt / Option forces "draw from centre" on inside the same
      // drawing modes — mirror that on the centre chip so pressing
      // Alt flips it live, exactly like Shift flips the size chip.
      // Doesn't touch `settings.snapCenter`; releasing Alt restores
      // whatever the toggle is set to.
      if (
        key === "snapCenter" &&
        centerModifierHeld &&
        isInSizeSnapDrawMode()
      ) {
        childOn = true;
      }
      // Ctrl / Cmd forces angle snap while held (#927). Unlike Shift/Alt,
      // this isn't gated to the drawing modes — the angle modifier applies
      // to rotation, part moves, and vertex drags too, so the chip mirrors
      // the key everywhere. Doesn't touch `settings.snapAngle`.
      if (key === "snapAngle" && angleKeyHeld) {
        childOn = true;
      }
      btn.classList.toggle("active", settings.snapMaster && childOn);
      btn.hidden = !settings.snapMaster;
      btn.setAttribute(
        "aria-pressed",
        settings.snapMaster && childOn ? "true" : "false",
      );
      // Dim the chips this tool never consults (#364), and say why. The chip
      // stays CLICKABLE and its stored setting is untouched: an inapplicable
      // snap that's on is simply not read, so switching back to a tool that
      // uses it finds it exactly as the user left it — and they can still arm
      // one here for the tool they're about to pick.
      const ctxNow = snapToolContext();
      const inert = !isSnapApplicable(key, ctxNow);
      btn.classList.toggle("snap-toggle--inert", inert);
      const baseTitle = snapBaseTitles.get(btn) ?? "";
      const reason = snapInapplicableReason(key, ctxNow);
      btn.title = withPreviewNote(
        btn,
        reason ? `${baseTitle} — ${reason}` : baseTitle,
      );
    }
    // The round-corner toggle is grouped with the snap children,
    // so it also collapses when the master is off — keeps the
    // bottom-left pill consistent (everything snap-related folds
    // away together). The button's own state (active class, icon,
    // aria-pressed) is owned by `updateRoundModeToggle`; we only
    // gate visibility here.
    if (roundModeBtn) {
      roundModeBtn.hidden = !settings.snapMaster;
      // Narrowest control in the pill (#364): `roundAllCorners` is read only by
      // the leather Round tool and construction's Round sub-mode, so it decides
      // nothing anywhere else. Dimmed on the same terms as the snap chips —
      // still clickable, setting untouched.
      const roundCtx = snapToolContext();
      roundModeBtn.classList.toggle(
        "snap-toggle--inert",
        !isRoundModeApplicable(roundCtx),
      );
      // `updateRoundModeToggle` owns this button's title — it flips with the
      // all/single state — so the base is re-read each pass and any previously
      // appended reason stripped. Caching it once would go stale on the first
      // flip, and appending blind would stack the suffix on itself.
      const roundBase = roundModeBtn.title.split(" — ")[0]!;
      const why = roundModeInapplicableReason(roundCtx);
      roundModeBtn.title = why ? `${roundBase} — ${why}` : roundBase;
    }
  };
  syncSnapApplicability = syncSnapButtons;
  syncSnapButtons();
  if (masterBtn) {
    masterBtn.addEventListener("click", () => {
      settings.snapMaster = !settings.snapMaster;
      ctx.notifySettingsChanged();
      syncSnapButtons();
    });
  }
  for (const [btn, key] of snapChildren) {
    if (!btn) continue;
    btn.addEventListener("click", () => {
      if (!settings.snapMaster) return;
      settings[key] = !settings[key];
      ctx.notifySettingsChanged();
      syncSnapButtons();
    });
  }
  ctx.onSettingsChanged(syncSnapButtons);

  // Snap-candidate dots (#367). The setting alone isn't the gate: a tier's
  // points are marked only while THAT tier is armed, so a dot always means
  // "this would catch right now" rather than "this might, if you also flip a
  // toggle" — which is the confusion the ticket is about. Tracking the chips
  // separately is what makes the display answer "which toggle catches which
  // points": the Points chip governs anchors, hole centres, segment midpoints
  // and shape centres; the Line chip governs the crossings.
  const syncSnapCandidateDots = () => {
    const on = showSnapCandidatesPref.get();
    scene.setSnapCandidateKinds({
      point: on && isPointSnapActive(settings),
      line: on && isLineSnapActive(settings),
    });
  };
  syncSnapCandidateDots();
  ctx.onSettingsChanged(syncSnapCandidateDots);
  showSnapCandidatesPref.subscribe(syncSnapCandidateDots);

  // Round-corner sub-mode "all vs single" toggle (issue #100
  // follow-up). Lives inside the snap-group pill, so it visually
  // integrates with the snap children and hides alongside them
  // when the master toggle is off. Shift held forces "all" while
  // down (visual flip mirrors that, but only when Round is the
  // active sub-tool); releasing Shift returns to the persistent
  // setting. Pressing the button swaps the setting.
  // Shared icon — same glyph as the Construction sub-bar Round
  // button, so the user reads "this is about the corner-rounding
  // tool" at a glance. The toggle's all-vs-single state is shown
  // via the `.active` class (background tint), not by swapping
  // icons.
  const isInRoundMode = () =>
    (activeTool === tools.construction &&
      (tools.construction as ConstructionTool).subMode === "round") ||
    // The leather Round method is the same gesture on leather outlines
    // (#974), so the Shift-forces-all modifier reads the same there.
    activeTool === tools.leatherRound;
  // Real implementation — assigned to the forward-declared `let`
  // binding above so the early `activateTool` call (during the
  // bootstrap) goes through the no-op stub safely.
  updateRoundModeToggle = () => {
    if (!roundModeBtn) return;
    // Effective = setting OR (modifier AND in-round-mode). Shift
    // is a one-way modifier that FORCES "round all corners" while
    // held, matching the size-linking Shift modifier elsewhere in
    // the app — but ONLY when the Round sub-tool is the active
    // tool. Pressing Shift outside Round mode shouldn't flip the
    // toggle visual or push state into the Scene, because no Round
    // drag is possible there. When the user later enters Round
    // mode while still holding Shift, this call (fired from
    // `activateTool` / `setConstructionSubMode`) re-evaluates and
    // the visual catches up.
    const modifierActive = roundModifierHeld && isInRoundMode();
    const effective = settings.roundAllCorners || modifierActive;
    roundModeBtn.classList.toggle("active", effective);
    roundModeBtn.setAttribute("aria-pressed", effective ? "true" : "false");
    // The glyph is inlined from the file by index.html's marker (#216) and
    // never changes with state — only the `active` class, `aria-pressed` and
    // the tooltip do — so there is nothing to re-inject here.
    // Tooltip mirrors the persistent setting (not the modifier-
    // inverted state) so it stays meaningful while Shift is held —
    // and only mentions Shift in the "single" state since it has
    // nothing to add in the "all" state.
    roundModeBtn.title = settings.roundAllCorners
      ? "Rounding all eligible corners"
      : "Rounding only the grabbed corner (hold Shift to link all)";
    // The Scene's `roundModeAll` drives the corner-handle fill
    // (highlights every eligible knob in "all" mode). Only matters
    // while the Round sub-mode has handles up, but it's cheap to
    // keep in sync at all times.
    scene.setRoundModeAll(effective);
    // Refresh any in-flight Round drag's preview so the geometry
    // mirrors the new effective mode without waiting for the next
    // mouse-move event.
    (tools.construction as ConstructionTool).notifyRoundModeChanged();
    // Same for an in-flight leather Round drag, and for its resting handles —
    // "all corners" highlights every eligible knob (#974).
    if (activeTool === tools.leatherRound) {
      (tools.leatherRound as LeatherRoundTool).refreshHandles();
    }
    // Tool / sub-mode changes also affect whether the snap
    // toolbar's size chip should light up under Shift (Shape /
    // Cutout / Construction Draw highlight it; other tools don't).
    // Re-sync the snap buttons so the chip catches up to the
    // current tool without waiting for the next Shift event.
    syncSnapButtons();
    // Same reason for the properties panel — its drawing-mode
    // gallery labels depend on the effective size-snap state.
    ui.props.setShiftHeldForDrawing(
      roundModifierHeld && isInSizeSnapDrawMode(),
    );
    ui.props.setAltHeldForDrawing(centerModifierHeld && isInSizeSnapDrawMode());
  };
  if (roundModeBtn) {
    roundModeBtn.addEventListener("click", () => {
      settings.roundAllCorners = !settings.roundAllCorners;
      ctx.notifySettingsChanged();
      updateRoundModeToggle();
    });
  }
  ctx.onSettingsChanged(updateRoundModeToggle);
  // Initial sync — the snap children pick up their persisted state
  // when `syncSnapButtons` runs above; the round-mode toggle has to
  // do the same after the real `updateRoundModeToggle` is assigned,
  // because the bootstrap-time `activateTool` call went through the
  // forward-declared no-op stub. Without this call, the HTML's
  // static `class="active"` would linger even when localStorage had
  // `roundAllCorners: false`.
  updateRoundModeToggle();

  // Rays show/hide toggle (#745). Rays are remembered PER MODE — construction
  // defaults on, leather defaults off, and once the user toggles in a mode the
  // explicit choice sticks (persisted with the other settings). The button
  // shows only in construction / leather modes (rays don't apply elsewhere).
  const raysToggleBtn = document.getElementById(
    "rays-toggle",
  ) as HTMLButtonElement | null;
  const raysModeKey = ():
    | "raysConstruction"
    | "raysLeather"
    | "raysStamping"
    | null => {
    if (activeTool === tools.construction) return "raysConstruction";
    if (!activeTool) return null;
    const parent = parentToolbarIdFor(activeTool.id);
    // Hardware places holes into leather parts, so it shares the Leather rays
    // toggle with the Hole tool — enabling rays once covers both (#728).
    if (parent === "leather" || parent === "hardware") return "raysLeather";
    if (parent === "stamping") return "raysStamping";
    return null;
  };
  const effectiveRaysFor = (
    key: "raysConstruction" | "raysLeather" | "raysStamping",
  ): boolean =>
    // Construction defaults ON; every other mode defaults OFF.
    key === "raysConstruction"
      ? (settings.raysConstruction ?? true)
      : (settings[key] ?? false);
  syncRaysToggle = () => {
    const key = raysModeKey();
    if (!key) {
      // Outside the ray-bearing modes: hide the button and force rays off so a
      // stray ray from a previous mode can't linger.
      if (raysToggleBtn) raysToggleBtn.hidden = true;
      scene.setRaysEnabled(false);
      return;
    }
    const on = effectiveRaysFor(key);
    if (raysToggleBtn) {
      raysToggleBtn.hidden = false;
      raysToggleBtn.classList.toggle("active", on);
      raysToggleBtn.setAttribute("aria-pressed", on ? "true" : "false");
      raysToggleBtn.title = on ? "Hide value rays" : "Show value rays";
    }
    scene.setRaysEnabled(on);
  };
  if (raysToggleBtn) {
    raysToggleBtn.addEventListener("click", () => {
      const key = raysModeKey();
      if (!key) return;
      settings[key] = !effectiveRaysFor(key);
      ctx.notifySettingsChanged();
      syncRaysToggle();
    });
  }
  ctx.onSettingsChanged(syncRaysToggle);
  syncRaysToggle();

  // Global Shift tracker for the round-mode visual flip. Matches
  // the size-linking modifier the Shape / Construction tools
  // already use, so a leather-art user only has one modifier key
  // to remember for "force the linked behaviour." The visual
  // flip is gated on Round being the active tool by
  // `updateRoundModeToggle` itself — the listener captures the
  // raw key state at all times so a Shift-held → enter-Round
  // transition picks up the modifier on the way in.
  // Nudge the active draw tool's preview so an in-flight drag
  // re-renders with the current Shift / Alt modifier state — pressing
  // Shift mid-drag snaps a rectangle into a square, pressing Alt
  // recentres it, both immediately rather than on the next mouse-move.
  // Always dispatches the *combined* state of both modifiers so one
  // key's change doesn't clobber the other's effect.
  const dispatchDrawModifiers = () => {
    if (!isInSizeSnapDrawMode() && !isInKnifeMode()) return;
    activeTool?.onModifiersChanged?.({
      shiftKey: roundModifierHeld,
      // Ctrl / Cmd is the dedicated angle-snap modifier (#927); receivers
      // merge these into their last pointer sample, so either flag works.
      ctrlKey: angleKeyHeld,
      metaKey: angleKeyHeld,
      altKey: centerModifierHeld,
    });
  };
  const setRoundModifier = (held: boolean) => {
    if (roundModifierHeld === held) return;
    roundModifierHeld = held;
    updateRoundModeToggle();
    // Size-snap toggle's "active" class mirrors `Shift && in-
    // drawing-mode` too — refresh the snap buttons so the size
    // chip lights up in sync.
    syncSnapButtons();
    // Push the same effective state into the properties panel so
    // drawing-mode galleries flip "Rectangle" ↔ "Square" labels
    // live with Shift (no waiting for the next settings event).
    ui.props.setShiftHeldForDrawing(held && isInSizeSnapDrawMode());
    dispatchDrawModifiers();
  };
  // Alt / Option tracker — the "draw from centre" equivalent of the
  // Shift size-link modifier. Flips the centre snap chip's visual and
  // re-fires the in-flight preview so pressing Alt recentres a
  // rectangle / ellipse on the canvas immediately (issue #389).
  const setCenterModifier = (held: boolean) => {
    if (centerModifierHeld === held) return;
    centerModifierHeld = held;
    syncSnapButtons();
    // Mirror into the properties panel: while Alt forces the regular-polygon
    // mode, the "Regular polygon" checkbox reads checked + disabled and the
    // polygon tile flips, live with the modifier (#934).
    ui.props.setAltHeldForDrawing(held && isInSizeSnapDrawMode());
    dispatchDrawModifiers();
  };
  // Ctrl / Cmd tracker — the dedicated angle-snap modifier (#927). Lights
  // the angle chip and re-fires the in-flight preview so pressing Ctrl/Cmd
  // mid-drag locks the segment to a common drafting angle immediately.
  const setAngleModifier = (held: boolean) => {
    if (angleKeyHeld === held) return;
    angleKeyHeld = held;
    syncSnapButtons();
    dispatchDrawModifiers();
  };
  // Double-tap a modifier to LOCK its snap mode (issue #390): tapping
  // Shift twice in quick succession toggles the persistent Size snap,
  // tapping Alt / Option twice toggles the Centre snap, and tapping
  // Ctrl or Cmd twice toggles the Angle snap (#927) — the same modes
  // those keys force while held. The while-held temporary behaviour is
  // unaffected; this just promotes a transient modifier into a sticky
  // toggle without reaching for the mouse.
  const shiftTap = new ModifierTapDetector("Shift");
  const altTap = new ModifierTapDetector("Alt");
  const ctrlTap = new ModifierTapDetector("Control");
  const metaTap = new ModifierTapDetector("Meta");
  const lockSnapToggle = (
    key: "snapSizes" | "snapCenter" | "snapAngle",
    label: string,
  ) => {
    const turningOn = !settings[key];
    settings[key] = turningOn;
    // Child toggles do nothing while the master is off — turning one on
    // via a double-tap implies the user wants it to take effect, so flip
    // the master on too (matches "lock this mode").
    if (turningOn) settings.snapMaster = true;
    ctx.notifySettingsChanged();
    showCanvasToast(`${label} snap ${turningOn ? "locked on" : "off"}`);
  };
  window.addEventListener("keydown", (e) => {
    // Ignore the snap modifiers (and their live UI: the size-snap chip,
    // the Rectangle↔Square gallery labels, the round-mode toggle) while
    // the user is typing into a field — e.g. holding Shift for a capital
    // in a name input shouldn't flip drawing state (issue #506).
    if (isTextTarget(e.target)) return;
    if (e.shiftKey) setRoundModifier(true);
    if (e.altKey) setCenterModifier(true);
    if (e.ctrlKey || e.metaKey) setAngleModifier(true);
    const now = performance.now();
    shiftTap.onKeyDown(e, now);
    altTap.onKeyDown(e, now);
    ctrlTap.onKeyDown(e, now);
    metaTap.onKeyDown(e, now);
  });
  window.addEventListener("keyup", (e) => {
    if (!e.shiftKey) setRoundModifier(false);
    if (!e.altKey) setCenterModifier(false);
    if (!e.ctrlKey && !e.metaKey) setAngleModifier(false);
    const now = performance.now();
    if (shiftTap.onKeyUp(e, now)) lockSnapToggle("snapSizes", "Size");
    if (altTap.onKeyUp(e, now)) lockSnapToggle("snapCenter", "Center");
    if (ctrlTap.onKeyUp(e, now) || metaTap.onKeyUp(e, now))
      lockSnapToggle("snapAngle", "Angle");
  });
  // The window can lose focus mid-press (alt-tab, browser modal)
  // and never deliver the keyup. Drop both modifiers on blur so the
  // toggles don't get stuck in the inverted state, and disarm any
  // half-finished double-tap.
  window.addEventListener("blur", () => {
    setRoundModifier(false);
    setCenterModifier(false);
    setAngleModifier(false);
    shiftTap.reset();
    altTap.reset();
    ctrlTap.reset();
    metaTap.reset();
  });

  // Live snap-angle readout. Two chips, both populated together when a
  // snap-drag is in progress, both empty (and CSS-hidden) when not:
  //   • #snap-angle-indicator       — folded "angle from horizontal" 0°-90°
  //                                   with quadrant-emphasised crosshair and
  //                                   a short measurement arc.
  //   • #snap-angle-indicator-full  — full visual angle 0°-359° with a plain
  //                                   thin crosshair and an arc spanning the
  //                                   whole sweep CCW from horizontal right.
  const angleEl = document.getElementById("snap-angle-indicator");
  const angleFullEl = document.getElementById("snap-angle-indicator-full");
  if (angleEl || angleFullEl) {
    const TAU = 2 * Math.PI;
    const foldToHorizontalDeg = (rad: number): number => {
      let deg = ((((rad % TAU) + TAU) % TAU) * 180) / Math.PI; // [0, 360)
      deg = deg % 180; // undirected line → [0, 180)
      // Math.round is a defensive guard against float drift; the snap angles
      // are exact multiples of 15° so we always land on one of {0,30,45,60,90}.
      return Math.round(deg > 90 ? 180 - deg : deg);
    };
    /** Visual angle (0-359°), measuring CCW from positive x-axis with y-up
     *  the way users naturally think of angles — the inverse of the code's
     *  y-down atan2 convention. */
    const toVisualDeg = (rad: number): number => {
      const codeDeg = ((((rad % TAU) + TAU) % TAU) * 180) / Math.PI;
      return Math.round((((360 - codeDeg) % 360) + 360) % 360);
    };
    type IconOpts = {
      highlightQuadrant: boolean;
      // "measurement" → short arc between baseline and angle (≤ 90°);
      // "full"        → arc from 0° (right) sweeping CCW (visually) to angle.
      arcMode: "measurement" | "full";
    };
    const buildAngleIconSvg = (rad: number, opts: IconOpts): string => {
      const a = ((rad % TAU) + TAU) % TAU;
      const deg = (a * 180) / Math.PI;
      // Quadrant boundaries (code convention, y-down). Boundary angles go to
      // the *next* quadrant so 0/90/180/270 produce a stable icon.
      let bounds: [number, number];
      if (deg < 90) bounds = [0, 90];
      else if (deg < 180) bounds = [90, 180];
      else if (deg < 270) bounds = [180, 270];
      else bounds = [270, 360];
      // Baseline = whichever horizontal (0° or 180°) is closer; ties (90°,
      // 270°) go to 0° — arbitrary but consistent.
      const baselineDeg = deg <= 90 || deg >= 270 ? 0 : 180;

      const ray = (degrees: number) => {
        const r = (degrees * Math.PI) / 180;
        return [10 + 8 * Math.cos(r), 10 + 8 * Math.sin(r)] as const;
      };
      const line = (d: number, color: string, width: number) => {
        const [x, y] = ray(d);
        return `<line x1="10" y1="10" x2="${x.toFixed(2)}" y2="${y.toFixed(2)}" stroke="${color}" stroke-width="${width}" stroke-linecap="round"/>`;
      };

      const GREY = cssVar("--compass-axis");
      const BLUE = cssVar("--compass-active");
      // Crosshair. With highlightQuadrant=true, the two axes bounding the
      // active quadrant are thicker; otherwise all four are the same thin
      // weight. `% 360` normalises 360° back to 0° so the right axis lights
      // up for the upper-right quadrant.
      const b0 = bounds[0] % 360;
      const b1 = bounds[1] % 360;
      const axes = [0, 90, 180, 270]
        .map((d) =>
          line(
            d,
            GREY,
            opts.highlightQuadrant && (d === b0 || d === b1) ? 1.4 : 0.7,
          ),
        )
        .join("");

      // Arc geometry depends on the mode.
      const ARC_R = 4.5;
      const r = (d: number) => (d * Math.PI) / 180;
      let arc = "";
      if (opts.arcMode === "measurement") {
        // Short arc between baseline (0° or 180°) and the angle ray. Skipped
        // when displayed wedge is 0° (baseline === angle).
        if (deg !== baselineDeg) {
          const sx = 10 + ARC_R * Math.cos(r(baselineDeg));
          const sy = 10 + ARC_R * Math.sin(r(baselineDeg));
          const ex = 10 + ARC_R * Math.cos(r(deg));
          const ey = 10 + ARC_R * Math.sin(r(deg));
          const sweep = (deg - baselineDeg + 360) % 360 < 180 ? 1 : 0;
          arc = `<path d="M ${sx.toFixed(2)} ${sy.toFixed(2)} A ${ARC_R} ${ARC_R} 0 0 ${sweep} ${ex.toFixed(2)} ${ey.toFixed(2)}" fill="none" stroke="${BLUE}" stroke-width="1.2"/>`;
        }
      } else {
        // Full sweep from 0° (right) going CCW *visually* (which is sweep-
        // flag 0 in SVG's y-down coordinate system) to the angle ray. Skipped
        // when the angle is exactly 0°.
        const visualDeg = (360 - deg) % 360;
        if (visualDeg !== 0) {
          const sx = 10 + ARC_R; // baseline is right (0°)
          const sy = 10;
          const ex = 10 + ARC_R * Math.cos(r(deg));
          const ey = 10 + ARC_R * Math.sin(r(deg));
          const largeArc = visualDeg > 180 ? 1 : 0;
          arc = `<path d="M ${sx.toFixed(2)} ${sy.toFixed(2)} A ${ARC_R} ${ARC_R} 0 ${largeArc} 0 ${ex.toFixed(2)} ${ey.toFixed(2)}" fill="none" stroke="${BLUE}" stroke-width="1.2"/>`;
        }
      }

      // For the "full" mode the baseline is always the right axis (0°); for
      // "measurement" it's the closer horizontal. The blue rays are drawn
      // last so they sit on top of any coincident grey axis.
      const blueBaselineDeg = opts.arcMode === "full" ? 0 : baselineDeg;
      return `<svg viewBox="0 0 20 20" width="20" height="20" aria-hidden="true">${axes}${line(blueBaselineDeg, BLUE, 1.4)}${line(deg, BLUE, 1.6)}${arc}</svg>`;
    };

    onAngleHintChange((rad) => {
      if (rad === null) {
        if (angleEl) angleEl.innerHTML = "";
        if (angleFullEl) angleFullEl.innerHTML = "";
        return;
      }
      if (angleEl) {
        angleEl.innerHTML =
          buildAngleIconSvg(rad, {
            highlightQuadrant: true,
            arcMode: "measurement",
          }) +
          `<span class="snap-angle-value">${foldToHorizontalDeg(rad)}°</span>`;
      }
      if (angleFullEl) {
        // Hide the full-angle chip when the visual angle is already in the
        // upper-right (first) quadrant — the folded value in the first chip
        // matches it byte-for-byte and the second chip would just duplicate
        // information.
        const visualDeg = toVisualDeg(rad);
        if (visualDeg <= 90) {
          angleFullEl.innerHTML = "";
        } else {
          angleFullEl.innerHTML =
            buildAngleIconSvg(rad, {
              highlightQuadrant: false,
              arcMode: "full",
            }) + `<span class="snap-angle-value">${visualDeg}°</span>`;
        }
      }
    });
  }

  // Keyboard shortcuts
  window.addEventListener("keydown", (e) => {
    if (isTextTarget(e.target)) return;
    // Arrow keys nudge the current selection (#727): by the minor grid step,
    // the major step with Shift, or the ruler's smallest tick with
    // Cmd/Ctrl+Shift. Alt (with the same modifiers) instead ALIGNS the
    // selection's top-left corner to the next line of that grid tier in the
    // key's direction.
    if (
      e.key === "ArrowUp" ||
      e.key === "ArrowDown" ||
      e.key === "ArrowLeft" ||
      e.key === "ArrowRight"
    ) {
      if (!hasNudgeableSelection(scene.getSelection())) return;
      e.preventDefault();
      // Cmd/Ctrl+Shift → the ruler's smallest tick (1 mm / 1/16") — Cmd on
      // Mac, Ctrl on Windows/Linux, so it's reachable on both. Shift alone →
      // major grid line (50 mm / 1"); plain → minor grid line (10 mm / 1/4").
      // Alt turns any of these into an align-to-that-grid move (below).
      const mod = e.ctrlKey || e.metaKey;
      const step = scene.nudgeStep(
        mod && e.shiftKey ? "tick" : e.shiftKey ? "major" : "minor",
      );
      const dirX = e.key === "ArrowRight" ? 1 : e.key === "ArrowLeft" ? -1 : 0;
      const dirY = e.key === "ArrowDown" ? 1 : e.key === "ArrowUp" ? -1 : 0;
      let dx = dirX * step;
      let dy = dirY * step;
      if (e.altKey) {
        // Align mode: move the selection's leading bbox edge — the one facing
        // the arrow (right edge for →, left for ←, bottom for ↓, top for ↑) —
        // onto the next grid line of the active tier in that direction. Falls
        // back to the fixed step when there's no resolvable selection bounds or
        // no visible mat grid.
        const bounds = scene.getSelectionBounds();
        const grid = scene.getMatGridInfo();
        if (bounds && grid) {
          const nextLine = (c: number, origin: number, dir: number) => {
            const k = (c - origin) / step;
            return dir > 0
              ? origin + (Math.floor(k + 1e-6) + 1) * step
              : origin + (Math.ceil(k - 1e-6) - 1) * step;
          };
          const edgeX = dirX > 0 ? bounds.right : bounds.left;
          const edgeY = dirY > 0 ? bounds.bottom : bounds.top;
          dx = dirX ? nextLine(edgeX, grid.innerLeft, dirX) - edgeX : 0;
          dy = dirY ? nextLine(edgeY, grid.innerBottom, dirY) - edgeY : 0;
        }
      }
      nudgeSelection(ctx, dx, dy);
      return;
    }
    if (
      (e.ctrlKey || e.metaKey) &&
      (e.key === "z" || e.key === "Z") &&
      !e.shiftKey
    ) {
      // Cmd/Ctrl+Z — undo. Shift held is the redo branch below.
      e.preventDefault();
      history.undo();
    } else if (
      (e.ctrlKey || e.metaKey) &&
      (e.key === "y" ||
        e.key === "Y" ||
        ((e.key === "z" || e.key === "Z") && e.shiftKey))
    ) {
      // Cmd/Ctrl+Y or Shift+Cmd/Ctrl+Z — redo. Both cases ("z" / "Z")
      // are accepted because the browser emits the *shifted* character
      // ("Z") on `e.key` when Shift is held (issue #341); without
      // the uppercase branch the Shift+Z form never fired on real
      // keyboards.
      e.preventDefault();
      history.redo();
    } else if (
      (e.ctrlKey || e.metaKey) &&
      (e.key === "s" || e.key === "S") &&
      !e.shiftKey
    ) {
      // Cmd/Ctrl+S — Save the project, intercepting the browser's
      // "Save Page As" dialog (issue #428). Always swallow the key so
      // the browser dialog never appears, but only run the save when
      // the toolbar Save button is enabled — an empty canvas has
      // nothing to save, and the shortcut stays in lockstep with the
      // button's disabled gate.
      e.preventDefault();
      if (saveBtn?.disabled) return;
      void saveProject();
    } else if (
      (e.ctrlKey || e.metaKey) &&
      (e.key === "o" || e.key === "O") &&
      !e.shiftKey
    ) {
      // Cmd/Ctrl+O — Open a project, intercepting the browser's
      // file-open dialog (issue #428). Always swallow the key, but only
      // open the picker when the Open button is enabled (it's disabled
      // until the app is ready).
      e.preventDefault();
      if (openBtn?.disabled) return;
      void openProject();
    } else if (
      (e.ctrlKey || e.metaKey) &&
      (e.key === "p" || e.key === "P") &&
      !e.shiftKey
    ) {
      // Cmd/Ctrl+P — print the pattern through our print-ready PDF flow,
      // intercepting the browser's print-this-page dialog (issue #289).
      // Always swallow the key so the raw-DOM print never appears; only
      // run when the Print button is enabled (mirrors its disabled gate).
      e.preventDefault();
      void triggerPrint();
    } else if (
      (e.ctrlKey || e.metaKey) &&
      (e.key === "g" || e.key === "G") &&
      !e.shiftKey
    ) {
      // Cmd/Ctrl+G — wrap the current selection in a new
      // ConstructionGroup. Shared with the layers-panel "Group"
      // button via `groupConstructionSelection`.
      const sel = scene.getSelection();
      if (
        (sel.constructionIds ?? []).length === 0 &&
        (sel.constructionGroupIds ?? []).length === 0 &&
        (sel.tracingImageIds ?? []).length === 0
      ) {
        return;
      }
      e.preventDefault();
      groupConstructionSelection(ctx);
    } else if (
      (e.ctrlKey || e.metaKey) &&
      e.shiftKey &&
      (e.key === "g" || e.key === "G")
    ) {
      // Shift+Cmd/Ctrl+G — ungroup. Shared with the layers-panel
      // "Ungroup" button via `ungroupConstructionSelection`.
      const sel = scene.getSelection();
      if (
        (sel.constructionIds ?? []).length === 0 &&
        (sel.constructionGroupIds ?? []).length === 0 &&
        (sel.tracingImageIds ?? []).length === 0
      ) {
        return;
      }
      e.preventDefault();
      ungroupConstructionSelection(ctx);
    } else if (
      (e.ctrlKey || e.metaKey) &&
      (e.key === "d" || e.key === "D") &&
      !e.shiftKey
    ) {
      // Cmd/Ctrl+D — duplicate the current selection, scoped to its granularity
      // (#907): a single child feature (stitch / cutout / hole / decoration) →
      // just that feature, a leather part → the whole part, construction objects
      // → construction duplicate (issue #112). Only fires when there's something
      // selected so an empty-canvas press falls through to the browser default.
      const sel = scene.getSelection();
      const hasChildFeatureSelection =
        sel.decorationId != null ||
        sel.holeId != null ||
        sel.cutoutId != null ||
        sel.stitchId != null ||
        (sel.stitchIds?.length ?? 0) > 0;
      const hasPieceSelection =
        sel.pieceId != null || (sel.pieceIds?.length ?? 0) > 0;
      const hasConstructionSelection =
        (sel.constructionIds ?? []).length > 0 ||
        (sel.constructionGroupIds ?? []).length > 0 ||
        (sel.tracingImageIds ?? []).length > 0;
      if (
        !hasChildFeatureSelection &&
        !hasPieceSelection &&
        !hasConstructionSelection
      ) {
        return;
      }
      e.preventDefault();
      // Child scope is checked first: a child selection also carries its owner's
      // `pieceId`, so the piece path would otherwise swallow it.
      if (hasChildFeatureSelection) {
        duplicateChildFeatureSelection(ctx);
      } else if (hasPieceSelection) {
        duplicatePieceSelection(ctx);
      } else {
        duplicateConstructionSelection(ctx);
      }
    } else if (
      (e.ctrlKey || e.metaKey) &&
      !e.shiftKey &&
      (e.key === "c" || e.key === "C")
    ) {
      // Cmd/Ctrl+C — copy the selection into the app clipboard (#763).
      // Only intercepts when something copyable is selected; otherwise the
      // event falls through to the browser (text copying in inputs never
      // reaches here — the isTextTarget guard returns first).
      const payload = copySelection(ctx);
      if (payload) {
        e.preventDefault();
        appClipboard = { payload, pasteCount: 0 };
      }
    } else if (
      (e.ctrlKey || e.metaKey) &&
      !e.shiftKey &&
      (e.key === "v" || e.key === "V")
    ) {
      // Cmd/Ctrl+V — paste with the Duplicate nudge (#763). Each further
      // paste of the same payload steps one more, so repeated pastes
      // cascade instead of stacking on one spot (mirrors repeated Cmd+D).
      if (appClipboard) {
        e.preventDefault();
        const step = scene.nudgeStep("minor");
        const offsetMm = step * (appClipboard.pasteCount + 1);
        if (pasteClipboard(ctx, appClipboard.payload, { offsetMm })) {
          appClipboard.pasteCount += 1;
        }
      }
    } else if (
      (e.ctrlKey || e.metaKey) &&
      !e.shiftKey &&
      (e.key === "f" || e.key === "F")
    ) {
      // Cmd/Ctrl+F — paste IN PLACE (#763): the copy lands exactly on the
      // original (offset 0). Swallows the browser find dialog only while
      // the clipboard holds something; empty clipboard falls through.
      if (appClipboard) {
        e.preventDefault();
        pasteClipboard(ctx, appClipboard.payload, { offsetMm: 0 });
      }
    } else if (
      (e.ctrlKey || e.metaKey) &&
      !e.altKey &&
      (e.key === "]" || e.key === "[")
    ) {
      // Cmd/Ctrl+] / Cmd/Ctrl+[ — move the selected objects one row up /
      // down the layers list (#759), mirroring the panel's drag-reorder:
      // "]" raises (toward the top of the list, drawn on top), "[" lowers.
      // Parts step through the parts list; construction lines / groups /
      // tracing images step among their siblings. Child features (hole /
      // cutout / stitch / decoration) have no list order, so a focused
      // child falls through untouched — and Cmd+[ / Cmd+] must NOT reach
      // the browser (history back / forward) when we act, hence the
      // preventDefault on the handled paths only.
      const dir = e.key === "]" ? "up" : "down";
      const sel = scene.getSelection();
      const hasChildFeatureSelection =
        sel.decorationId != null ||
        sel.holeId != null ||
        sel.cutoutId != null ||
        sel.stitchId != null ||
        (sel.stitchIds?.length ?? 0) > 0;
      const pieceIds = sel.pieceIds ?? (sel.pieceId ? [sel.pieceId] : []);
      const lineIds = sel.constructionIds ?? [];
      const groupIds = sel.constructionGroupIds ?? [];
      const imageIds = sel.tracingImageIds ?? [];
      if (!hasChildFeatureSelection && pieceIds.length > 0) {
        e.preventDefault();
        // Pre-check on the live state: History.commit always pushes an
        // entry, so an at-the-edge no-op must not pollute the undo stack.
        if (stepPiecesInList(history.state, pieceIds, dir) !== history.state) {
          ctx.commit("Reorder parts", (proj) =>
            stepPiecesInList(proj, pieceIds, dir),
          );
        }
      } else if (
        lineIds.length > 0 ||
        groupIds.length > 0 ||
        imageIds.length > 0
      ) {
        e.preventDefault();
        const items = { lineIds, groupIds, imageIds };
        if (
          stepConstructionItems(history.state, items, dir) !== history.state
        ) {
          ctx.commit("Reorder construction items", (proj) =>
            stepConstructionItems(proj, items, dir),
          );
        }
      }
    } else if (
      (e.ctrlKey || e.metaKey) &&
      (e.key === "a" || e.key === "A") &&
      !e.shiftKey
    ) {
      // Cmd/Ctrl+A — select every visible item the active tool
      // operates on (issue #251). Dispatch by activeTool: each tool
      // family knows its own "all items" set. Hidden items stay
      // unselected so the gesture mirrors what's actually visible.
      if (activeTool === tools.construction) {
        const lineIds = projectConstructionLines(history.state)
          .filter((l) => isVisible(l))
          .map((l) => l.id);
        // Tracing images are construction objects too (issue #522) —
        // Cmd/Ctrl+A grabs every visible image alongside the lines.
        const imageIds = projectTracingImages(history.state)
          .filter((img) => isVisible(img))
          .map((img) => img.id);
        if (lineIds.length === 0 && imageIds.length === 0) return;
        e.preventDefault();
        const sel = {
          pieceId: null,
          decorationId: null,
          constructionIds: lineIds,
          tracingImageIds: imageIds,
        };
        ctx.setSelection(sel);
        // Select-all leaves the user on the Select sub-mode (issue
        // #381) so the freshly-selected items are immediately movable.
        switchToSelectToolForSelection(sel);
      } else if (
        // Leather toolbar group (Shape / Select / Hole / Cutout /
        // Stitch) all act on parts, so Cmd/Ctrl+A here grabs every
        // visible part. Sub-item selection clears as a side effect —
        // the multi-piece scope is mutually exclusive with hole /
        // cutout / stitch / decoration ids.
        activeTool === tools.shape ||
        activeTool === tools.select ||
        activeTool === tools.hole ||
        activeTool === tools.cutout ||
        activeTool === tools.stitch
      ) {
        const pieceIds = history.state.pieces
          .filter((p) => isVisible(p))
          .map((p) => p.id);
        if (pieceIds.length === 0) return;
        e.preventDefault();
        const sel = {
          pieceId: pieceIds[pieceIds.length - 1] ?? null,
          pieceIds,
          decorationId: null,
        };
        ctx.setSelection(sel);
        // Select-all switches to the leather Select tool (issue #381).
        switchToSelectToolForSelection(sel);
      }
    } else if (
      (e.ctrlKey || e.metaKey) &&
      e.shiftKey &&
      (e.key === "a" || e.key === "A")
    ) {
      // Cmd/Ctrl+Shift+A — deselect everything (issue #359). Tool-agnostic:
      // clears parts + sub-items, construction lines + groups, and tracing
      // images in one canonical call (the setSelection normaliser empties the
      // remaining multi-select arrays). Stays on the current tool.
      e.preventDefault();
      ctx.setSelection({
        pieceId: null,
        decorationId: null,
        constructionId: null,
        constructionGroupIds: [],
        tracingImageId: null,
      });
    } else if (e.key === "Escape" || e.key === "Enter") {
      // Constraint picker takes priority over every other Esc/Enter
      // behavior — if the user armed an "add constraint" pick and
      // hits Escape, that should always cancel the pick first.
      // Enter doesn't commit a partial pick (you need every ref).
      if (
        e.key === "Escape" &&
        (tools.construction as ConstructionTool).getConstraintPicker()
      ) {
        (tools.construction as ConstructionTool).cancelConstraintPicker();
        e.preventDefault();
      }
      // Notes: Escape abandons a note being dragged out of a part, or snaps a
      // note being moved / re-pointed back to where it was. Placing a note is
      // cheap to redo and annoying to undo, so backing out has to be possible
      // before letting go.
      else if (
        e.key === "Escape" &&
        activeTool === tools.notes &&
        (tools.notes as NoteTool).handleEscape()
      ) {
        e.preventDefault();
      }
      // Escape mid-drag of a polyline bezier-handle gesture pops the
      // in-progress vertex (pen-tool convention — the user grabbed a
      // point and is shaping it, Escape backs out THAT vertex rather
      // than committing the whole draft). The handler returns false
      // when no bezier drag is in flight, so the existing
      // commit-on-Esc/Enter branch below still runs in every other
      // case. Enter is excluded — committing on Enter mid-drag
      // matches user intent ("finish what I'm placing").
      else if (
        e.key === "Escape" &&
        activeTool === tools.construction &&
        (tools.construction as ConstructionTool).handleEscapeDuringDrag()
      ) {
        e.preventDefault();
      }
      // Stitch tool: finalize the polyline at the last clicked vertex so the
      // user can stop without travelling to the start point or
      // double-clicking. Both Escape (cancel-style) and Enter (confirm-style)
      // commit — same behavior, different keyboard convention.
      else if (
        activeTool === tools.stitch &&
        (tools.stitch as StitchTool).handleFinish()
      ) {
        e.preventDefault();
      } else if (
        activeTool === tools.construction &&
        (tools.construction as ConstructionTool).handleFinish()
      ) {
        e.preventDefault();
      }
      // Leather Shape / Cutout polygon: Enter commits the polygon (needs ≥ 3
      // vertices), Escape discards the in-progress draft (#779). Previously
      // neither key did anything, so a polygon could only be ended by clicking
      // back on its first point.
      else if (
        activeTool === tools.shape &&
        (e.key === "Enter"
          ? (tools.shape as ShapeTool).handleFinish()
          : (tools.shape as ShapeTool).handleDiscard())
      ) {
        e.preventDefault();
      } else if (
        activeTool === tools.cutout &&
        (e.key === "Enter"
          ? (tools.cutout as CutoutTool).handleFinish()
          : (tools.cutout as CutoutTool).handleDiscard())
      ) {
        e.preventDefault();
      }
      // Leather Mirror (#973): Enter commits the armed reflection, Escape
      // disarms and drops the source selection — which is also how you get out
      // of the gesture, since pointing at open space arms a mirror rather than
      // clearing the selection.
      else if (
        activeTool === tools.leatherMirror &&
        (e.key === "Enter"
          ? (tools.leatherMirror as LeatherMirrorTool).handleFinish()
          : (tools.leatherMirror as LeatherMirrorTool).handleDiscard())
      ) {
        e.preventDefault();
      }
      // Ruler (#155): Escape puts the measurement away. Only Escape — there is
      // nothing to confirm with Enter, since measuring commits nothing.
      else if (e.key === "Escape" && activeTool === tools.measure) {
        (tools.measure as MeasureTool).onCancel();
        e.preventDefault();
      }
      // Trace tool: Enter closes the outline to its start; Escape discards the
      // in-progress trace.
      else if (
        activeTool === tools.trace &&
        (e.key === "Enter"
          ? (tools.trace as TraceTool).handleFinish()
          : (tools.trace as TraceTool).handleCancel())
      ) {
        e.preventDefault();
      }
      // Region tool: Enter commits the selected regions as one piece; Escape
      // discards the selection.
      else if (
        activeTool === tools.region &&
        (e.key === "Enter"
          ? (tools.region as RegionTool).handleFinish()
          : (tools.region as RegionTool).handleCancel())
      ) {
        e.preventDefault();
      }
    }
    // Construction sub-sub-tool shortcuts come FIRST while the
    // Construction tool is engaged in Edit, so they take priority
    // over the global single-key tool shortcuts (e.g. "x" would
    // otherwise activate the cutout tool, "p" the freePath tool).
    // Each shortcut also moves focus to the matching button so the
    // user gets the focus-ring visual cue confirming the keyboard
    // shortcut landed.
    else if (
      e.key === "p" &&
      activeTool === tools.construction &&
      (tools.construction as ConstructionTool).subMode === "edit"
    ) {
      setEditActionAndSync("drag");
      dragPointBtn?.focus();
    } else if (
      e.key === "b" &&
      activeTool === tools.construction &&
      (tools.construction as ConstructionTool).subMode === "edit"
    ) {
      setEditActionAndSync("smooth-toggle");
      smoothToggleBtn?.focus();
    } else if (e.key === "x" && activeTool === tools.construction) {
      // "X" selects the top-level Delete sub-tool (issue #670
      // follow-up — promoted out of the Edit sub-sub-tool cluster).
      setConstructionSubMode("delete");
      subtoolButtons.get("delete")?.focus();
    } else if (e.key === "v" && activeTool === tools.construction) {
      // "V" inside Construction maps to the Select sub-mode rather
      // than the leather Select tool (issue #330). Same gate-first /
      // fall-through pattern Draw uses for "D" (vs lineStamp).
      setConstructionSubMode("select");
      subtoolButtons.get("select")?.focus();
    } else if (e.key === "v") {
      activateTool("select");
      ui.toolbar.getButton("select")?.focus();
    } else if (e.key === "l") {
      activateTool("shape");
      ui.toolbar.getButton("shape")?.focus();
    } else if (e.key === "t") {
      activateTool("trace");
      ui.toolbar.getButton("trace")?.focus();
    } else if (e.key === "h") {
      activateTool("hole");
      ui.toolbar.getButton("hole")?.focus();
    } else if (e.key === "x") {
      activateTool("cutout");
      ui.toolbar.getButton("cutout")?.focus();
    } else if (e.key === "s") {
      // The Stamping section is feature-gated (#358) — ignore the
      // shortcut when the whole group is hidden.
      if (isFeatureEnabled("stamping-section")) {
        activateTool("stamp");
        ui.toolbar.getButton("stamp")?.focus();
      }
    } else if (e.key === "n") {
      // Notes and Measure sit on their own at the foot of the bar (#155,
      // #157) and belong to no group, so they have no sub-mode branch above
      // to fall through — the plain letter is the whole story (#1088). Both
      // advertise their key in the toolbar tooltip, which is what made the
      // omission a broken promise rather than a missing feature.
      activateTool("notes");
      ui.toolbar.getButton("notes")?.focus();
    } else if (e.key === "m") {
      activateTool("measure");
      ui.toolbar.getButton("measure")?.focus();
    }
    // Construction sub-tool shortcuts come before the global single-key
    // tool shortcuts so the "d" key picks the Draw sub-mode instead of
    // activating Line stamp while the Construction tool is engaged.
    else if (e.key === "d" && activeTool === tools.construction) {
      setConstructionSubMode("draw");
      subtoolButtons.get("draw")?.focus();
    } else if (e.key === "a" && activeTool === tools.construction) {
      // "A" for Anchor — the Edit sub-mode is labelled "Edit anchor
      // points" (issue #331). Cmd/Ctrl+A is intercepted by the
      // select-all branch above, so the plain-letter form here only
      // fires for an unmodified A.
      setConstructionSubMode("edit");
      subtoolButtons.get("edit")?.focus();
    }
    // Round sub-tool ("R", #100). Must come BEFORE the global "r"
    // shortcut below — that one activates the Hardware tool, but
    // while Construction is engaged the user means "round corners",
    // matching the tooltip on the round sub-toolbar button.
    else if (e.key === "r" && activeTool === tools.construction) {
      setConstructionSubMode("round");
      subtoolButtons.get("round")?.focus();
    }
    // Offset sub-tool ("O", #29 / #344). "O" selects the SIMPLE
    // (standalone) offset — the default of the offset pair.
    else if (e.key === "o" && activeTool === tools.construction) {
      setConstructionSubMode("simple-offset");
      subtoolButtons.get("simple-offset")?.focus();
    }
    // Mirror sub-tool ("I", #180 / #344). "I" selects the SIMPLE
    // (standalone) mirror. Letter doesn't conflict with any global
    // tool shortcut; the "active tool is Construction" gate stays
    // consistent with the other sub-tool shortcuts above.
    else if (e.key === "i" && activeTool === tools.construction) {
      setConstructionSubMode("simple-mirror");
      subtoolButtons.get("simple-mirror")?.focus();
    } else if (e.key === "d") {
      // Line stamp lives in the feature-gated Stamping section (#358).
      if (isFeatureEnabled("stamping-section")) {
        activateTool("lineStamp");
        ui.toolbar.getButton("lineStamp")?.focus();
      }
    } else if (e.key === "f") {
      // "F" fills a region enclosed by construction lines (Leather group).
      activateTool("region");
      ui.toolbar.getButton("region")?.focus();
    } else if (e.key === "p") {
      activateTool("freePath");
      ui.toolbar.getButton("freePath")?.focus();
    } else if (e.key === "r") {
      // Hardware tool is feature-gated (#358) — ignore the shortcut
      // when it's hidden.
      if (isFeatureEnabled("hardware-tool")) {
        activateTool("hardware");
        ui.toolbar.getButton("hardware")?.focus();
      }
    } else if (e.key === "c") {
      activateTool("construction");
      ui.toolbar.getButton("construction")?.focus();
    } else if (e.key === "0") viewport.fitToContent();
    else if (e.key === "Delete" || e.key === "Backspace") {
      // Always preventDefault on the canvas-level delete shortcut —
      // WebKit (Safari) still maps Backspace on a non-input target
      // to history navigation, which would yank the user out of
      // the editor mid-edit. Issue #283 surfaced this when
      // multi-line delete tests crashed with "Execution context
      // was destroyed" because Backspace on `<body>` navigated.
      e.preventDefault();
      // Delete whatever is selected. Shared with the Parts panel trashcan
      // (issue #474) via `deleteSelection` so the keyboard shortcut and the
      // panel button stay in lockstep across every selectable item type.
      deleteSelection(ctx);
    }
  });

  // Initial zoom: open at 1:1 real size if the rendered content (mat +
  // any parts) fits the canvas at that scale, otherwise zoom out to fit.
  // The mm → CSS-px factor comes from the calibration module so a saved
  // per-display calibration applies on first paint. Applied here, before the
  // autosave await below, so the view is in place from first paint (#1189);
  // a restored project re-fits after the await.
  const applyInitialZoom = () => {
    const pxPerMm = getPxPerMm();
    const bounds = viewport.project.activeLayer?.bounds;
    if (bounds && bounds.width > 0 && bounds.height > 0) {
      const viewW = viewport.view.viewSize.width;
      const viewH = viewport.view.viewSize.height;
      if (bounds.width * pxPerMm <= viewW && bounds.height * pxPerMm <= viewH) {
        viewport.zoom = pxPerMm;
        viewport.view.center = new viewport.scope.Point(
          bounds.x + bounds.width / 2,
          bounds.y + bounds.height / 2,
        );
      } else {
        viewport.fitToContent();
      }
    } else {
      viewport.view.center = new viewport.scope.Point(0, 0);
      viewport.zoom = pxPerMm;
    }
  };
  applyInitialZoom();
  const loadView = {
    zoom: viewport.view.zoom,
    x: viewport.view.center.x,
    y: viewport.view.center.y,
  };
  const viewIsUntouchedSinceLoad = () =>
    viewport.view.zoom === loadView.zoom &&
    viewport.view.center.x === loadView.x &&
    viewport.view.center.y === loadView.y;

  // Feature-flag gates (issue #358): hide/show gated UI per the effective
  // flag state, and re-apply when the preview opt-out changes. Applied before the saved project loads (#1230), so a slow load
  // never shows tools that are off. Hiding (rather than removing) keeps the wiring intact so a
  // flip back is instant.
  // Gated markup carries `data-startupapi-feature`; it's hidden through
  // `data-startupapi-feature-off`, the attribute the static build and the
  // StartupAPI proxy write (#1244), not `hidden`, which the snap toolbar
  // toggles on the same buttons for its own reasons.
  const showEl = (el: Element | null, shown: boolean) => {
    el?.toggleAttribute("data-startupapi-feature-off", !shown);
  };
  const syncFeatureGatedUI = () => {
    showEl(ui.toolbar.getButton("hardware"), isFeatureEnabled("hardware-tool"));
    // The whole Stamping group (its top-level toolbar button, and thus
    // all of Stamp / Line stamp / Fill) hides as one section.
    showEl(
      ui.toolbar.getButton("stamping"),
      isFeatureEnabled("stamping-section"),
    );
    const modOn = isFeatureEnabled("modifiers");
    showEl(subtoolButtons.get("offset") ?? null, modOn);
    showEl(subtoolButtons.get("mirror") ?? null, modOn);
    // Print serves both the free basic print and the paid advanced one, so it
    // wears the Preview badge (#1120) only while it routes to the paid one.
    const printBtn = document.getElementById("btn-print");
    if (printBtn) {
      markPaidFeature(
        printBtn,
        "advanced-printing",
        isFeatureEnabled("advanced-printing"),
      );
    }
    showEl(document.getElementById("snap-toggle-point-constraint"), modOn);
    const connOn = isFeatureEnabled("construction-connections");
    // The Assembled/Spread toggle only matters for linked parts, which only
    // exist when construction connections are on (#652) — hide it with the
    // rest of the feature.
    showEl(document.getElementById("btn-part-layout"), connOn);
    // The account control in the top bar (#1241): StartupAPI's
    // <power-strip variant="compact">, a "Log in" pill or the avatar and its
    // account menu. It's an external web component, so unlike the rest of the
    // chrome its markup comes from its own script (the proxy injects it); the
    // CSS reserves its box until then. While it's hidden it doesn't ask
    // /users/api/me, so visitors without the flag make no request.
    showEl(
      document.querySelector("power-strip"),
      isFeatureEnabled("account-buttons"),
    );
    // What one finger does is gated on `touch-device-support` (#40). It reacts
    // here, with every other flag-gated behaviour — flipping the flag on used
    // to leave the gesture off until an unrelated preference change happened
    // to re-run it.
    applyTouchDraw();
    // The trace connection chips / rows are gated on `construction-connections`
    // (see `traceConnection`); re-render the panels so a toggle shows/hides
    // them.
    ui.props.refresh();
    ui.layers.refresh();
  };
  syncFeatureGatedUI();
  onFeatureFlagsChange(syncFeatureGatedUI);

  // Restore autosave if present. Now async (IndexedDB, #805) — awaited here so
  // the restore completes before the re-fit below reads the rendered content
  // bounds. All synchronous setup (incl. the `__editor` test hook and the
  // initial view) has already run above, so the app is interactive during
  // this await.
  /** Enter the loading state for the saved project (#1230); returns the
   *  hooks the restore calls as each phase ends, and to leave the state once
   *  the project is in the editor.
   *
   *  Both speeds: tool input is off (see `activateTool`), the tools and the
   *  project actions are inert, and app keyboard shortcuts are held, so
   *  nothing done before the restore lands can be overwritten by it. A load
   *  predicted to take LOADING_TEXT_MS or more also says "Loading…" in the
   *  title and "Loading parts…" in the Parts panel, and dims the tools; one
   *  predicted to take PROGRESS_BAR_MS or more fills a bar just below the mat
   *  grid's top line at the predicted pace (see LoadProgressBar). A load
   *  predicted under either that runs that long shows it then, when the hint
   *  says a project is coming. */
  function startProjectLoad(): {
    phaseDone: (phase: RestorePhase) => void;
    finish: (loaded: boolean) => void;
  } {
    const root = document.documentElement;
    const inertEls = [
      ...document.querySelectorAll<HTMLElement>(
        "#toolbar, .subtoolbar, #arrange-bar",
      ),
      ...[
        "btn-part-layout",
        "btn-undo",
        "btn-redo",
        "btn-reset",
        "btn-open",
        "btn-save",
        "btn-export",
        "btn-print",
      ]
        .map((id) => document.getElementById(id))
        .filter((el): el is HTMLElement => el !== null),
    ];
    for (const el of inertEls) el.inert = true;

    // App shortcuts (tool keys, Delete, undo, Open / Save / Print…) wait for
    // the project. Typing in a field, Tab, Escape and Space (pan) still work.
    const holdShortcuts = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (t?.closest("input, textarea, select, [contenteditable]")) return;
      if (e.key === "Tab" || e.key === "Escape" || e.key === " ") return;
      if ((e.metaKey || e.ctrlKey) && /^[sop]$/i.test(e.key)) {
        e.preventDefault();
      }
      e.stopImmediatePropagation();
    };
    window.addEventListener("keydown", holdShortcuts, true);

    // The bar sits just below the mat grid's top line, behind the grid's
    // marks, so it starts when the mat is first drawn: the earliest the
    // mat's size is known.
    const canvasEl = viewport.view.element as HTMLCanvasElement;
    const progressTrack = document.getElementById("load-progress-track");
    const topbarEl = document.querySelector<HTMLElement>(".topbar");
    const progressEl = document.getElementById("load-progress");
    const bar =
      progressTrack && progressEl
        ? new LoadProgressBar(
            progressTrack,
            progressEl,
            () => {
              const grid = scene.getMatGridInfo();
              if (!grid) {
                // Mat hidden: along the bottom of the top bar, its whole
                // width, a little thinner than on the mat.
                const r = topbarEl?.getBoundingClientRect();
                if (!r) return null;
                return {
                  left: r.left,
                  width: r.width,
                  top: r.bottom,
                  height: LOAD_BAR_PX * 0.8,
                  onMat: false,
                };
              }
              const view = viewport.view;
              const a = view.projectToView(
                new viewport.scope.Point(grid.innerLeft, grid.innerTop),
              );
              const b = view.projectToView(
                new viewport.scope.Point(grid.innerRight, grid.innerTop),
              );
              return {
                left: canvasEl.offsetLeft + a.x,
                width: b.x - a.x,
                top: canvasEl.offsetTop + a.y + MAT_BORDER_PX / 2,
                height: LOAD_BAR_PX,
                onMat: true,
              };
            },
            predictedPhases,
            (open) => scene.setLoadingStrip(open),
          )
        : null;
    let textShown = false;
    const showDelayed = () => {
      if (textShown) return;
      textShown = true;
      projectLoad = "delayed";
      root.dataset.projectLoad = "delayed";
      if (projectNameSpan) projectNameSpan.textContent = "Loading…";
      ui.layers.setLoading("shown");
    };
    let barShown = false;
    const showBar = () => {
      if (barShown) return;
      barShown = true;
      bar?.start();
    };
    // No hint almost always means nothing is saved (a first visit, cleared
    // storage), so a slow "nothing here" from IndexedDB must not flash
    // "Loading…" or the bar. Only a project we know is coming shows them when
    // it runs past their thresholds. (Projects saved before the hint existed
    // load without it once; they get the silent state.)
    const predictedMs = restoreMs(predictedPhases);
    let textOverrun = 0;
    let barOverrun = 0;
    if (projectLoad === "delayed") {
      // The inline script said "Loading…" at first paint; startup has since
      // put the empty project's name back, so say it again.
      showDelayed();
    } else if (loadHint) {
      // A project is coming: the Parts panel doesn't say there are none.
      ui.layers.setLoading("quiet");
      textOverrun = window.setTimeout(showDelayed, LOADING_TEXT_MS);
    }
    if (showsProgressBar(predictedMs)) showBar();
    else if (loadHint) barOverrun = window.setTimeout(showBar, PROGRESS_BAR_MS);

    // A restore that runs far past its prediction is stuck, most likely on an
    // IndexedDB request that never answers, and only a reload gets it going
    // again. Say so, in the delayed state even if this load was silent so far.
    let dismissStuck: (() => void) | null = null;
    const stuck = window.setTimeout(() => {
      showDelayed();
      showBar();
      dismissStuck = showCanvasToast(
        "Loading your project is taking too long.",
        {
          durationMs: null,
          action: { label: "Reload", run: () => location.reload() },
        },
      );
    }, stuckAfterMs(predictedMs));

    return {
      phaseDone: (phase: RestorePhase) => bar?.phaseDone(phase),
      finish: (loaded: boolean) => {
        window.clearTimeout(textOverrun);
        window.clearTimeout(barOverrun);
        // The project made it after all: the stuck toast has nothing left to
        // say.
        window.clearTimeout(stuck);
        dismissStuck?.();
        // A bar that showed fills to the end, so it reads as finished rather
        // than cut off; with nothing loaded after all, it just goes.
        if (barShown && loaded) bar?.finish();
        else bar?.hide();
        window.removeEventListener("keydown", holdShortcuts, true);
        for (const el of inertEls) el.inert = false;
        projectLoad = "done";
        root.dataset.projectLoad = "done";
        ui.layers.setLoading(null);
        // Now judge the remembered tool and sub-tool against the real
        // project: one that still has nothing to act on falls back here.
        syncToolAvailability();
        if (projectNameSpan) projectNameSpan.textContent = history.state.name;
        if (activeTool) pointer.setTool(activeTool);
      },
    };
  }

  // The restore runs in phases, timed from navigation start for the next
  // load's prediction, and yields to the browser between them so input and
  // the view stay responsive and the bar re-anchors as each one ends.
  // Parsing (JSON.parse) and rendering can't be split further without
  // restructuring them, so each is one step.
  const load = startProjectLoad();
  const yieldToBrowser = () => new Promise<void>((r) => setTimeout(r, 0));
  const tStart = performance.now();
  const savedText = await autosave.readLatestText();
  const tWait = performance.now();
  load.phaseDone("wait");
  await yieldToBrowser();
  let saved:
    | (ReturnType<typeof deserializeProject> & { bytes: number })
    | null = null;
  if (savedText) {
    try {
      saved = { ...deserializeProject(savedText), bytes: savedText.length };
    } catch (e) {
      console.warn("Autosave load failed", e);
    }
  }
  const tParse = performance.now();
  load.phaseDone("parse");
  await yieldToBrowser();
  let restored: Project | null = null;
  if (saved) {
    const reg = ensureUserStampRegistered(
      library,
      saved.userStampMetas,
      saved.project,
    );
    // Same derive refresh as Open (#1141).
    restored = deriveProject(
      {
        ...saved.project,
        userStamps: [...saved.project.userStamps, ...reg.addedStamps],
      },
      scene.getPaperScope(),
    );
  }
  const tDerive = performance.now();
  load.phaseDone("derive");
  await yieldToBrowser();
  // The restore below lays the project's own mat out; the stand-in is done.
  // With nothing restored, redraw now so the default mat replaces it.
  scene.setLoadingMatLayout(null, !restored);
  if (restored) {
    history.replace(restored);
    // Treat the autosave-restored state as the new "saved" baseline
    // so the user doesn't get an unsaved-changes warning before
    // they've made any new edits this session. Autosave is a
    // recovery mechanism — it doesn't represent a deliberate save —
    // but warning every page load would be more annoying than
    // helpful.
    lastSavedModified = restored.modified;
    scene.render(restored);
    // Reconcile the Assemble/Spread view with the RESTORED parts (#722): honor
    // the persisted view if the project has offsets, else Spread. Runs after
    // the button was first set up on the (then-empty) project.
    applyPartLayoutView(initialPartLayoutView());
    ui.layers.refresh();
    ui.stamps.refresh();
    if (projectNameSpan) projectNameSpan.textContent = restored.name;
    // Re-apply the saved Construction sub-mode + edit-action now that
    // restored lines exist (issue #194 CI fix). The earlier
    // `setConstructionSubMode(savedSubMode)` call above runs before
    // autosave restores, so on a fresh boot the project is still
    // empty and the no-line gate in `setConstructionSubMode` drops
    // the saved Select/Edit/Offset/Round back to Draw. Re-applying
    // here lets the gate pass.
    if (savedSubMode && savedSubMode !== "draw") {
      setConstructionSubMode(savedSubMode);
      if (savedEditAction && savedSubMode === "edit") {
        setEditActionAndSync(savedEditAction);
      }
    }
  }

  // Re-fit to the restored project — but only when there is one, and only if
  // nothing has moved the view during the await (#1189). The app is
  // interactive while IndexedDB answers, so a pan / zoom made in that window
  // (by the user, or by a test that seeds and positions the view straight
  // after load) must not be thrown away. With no autosave the view the user
  // first saw simply stays.
  if (saved && viewIsUntouchedSinceLoad()) applyInitialZoom();

  // The render at load ran at the default zoom; the initial zoom was just
  // applied without re-rendering, so zoom-dependent affordances built then —
  // notably the corner-radius handles, sized in screen px as `k / zoom` — are
  // stale and would paint too large until the next interaction. Re-render once
  // at the final zoom so they rebuild at the right size (#591).
  scene.render(history.state);

  // Remember how long each phase took, so the next load can predict its wait.
  if (saved) {
    const phases: LoadPhases = {
      startup: tStart,
      wait: tWait - tStart,
      parse: tParse - tWait,
      derive: tDerive - tParse,
      render: performance.now() - tDerive,
    };
    writeLoadHint({
      bytes: saved.bytes,
      phases,
      phaseBytes: saved.bytes,
      mat: history.state.matLayout ?? scene.getMatLayout() ?? undefined,
    });
  } else {
    clearLoadHint();
  }
  load.finish(!!saved);

  return ctx;
}
